import React, { useState, useEffect, useCallback, useRef } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { formatPermissionDuration } from '../lib/exportUtils';
import { 
  Calendar as CalendarIcon, Clock, ArrowLeftRight, Gift, Plus, X, FileEdit, 
  AlertTriangle, CheckCircle2, Eye, Download, Search, Filter, Settings, 
  User, ChevronLeft, ChevronRight, RefreshCw, ArrowRight, History, 
  UserCheck, MoreVertical, FileSpreadsheet, FileText, Trash2, Check,
  ChevronDown, UserMinus, ShieldAlert, HeartPulse, HelpCircle, Building2, TrendingUp,
  Paperclip, RotateCcw
} from 'lucide-react';
const toast = {
  success: (msg) => alert(msg),
  error: (msg) => alert(msg),
  info: (msg) => alert(msg)
};

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ESSLeaveAttendance = () => {
  const [activeTab, setActiveTab] = useState('leave');
  
  // Core state from backend
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [leaveApps, setLeaveApps] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [permSummary, setPermSummary] = useState(null);
  const [attendance, setAttendance] = useState({ records: [], summary: {} });
  const [shiftRequests, setShiftRequests] = useState([]);
  const [compoffRequests, setCompoffRequests] = useState([]);
  const [regularizations, setRegularizations] = useState([]);
  const [eligibleDates, setEligibleDates] = useState([]);
  const [regPolicyInfo, setRegPolicyInfo] = useState(null);
  const [eligibleCompOffs, setEligibleCompOffs] = useState([]);
  const [companyHolidays, setCompanyHolidays] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveTypesLoading, setLeaveTypesLoading] = useState(false);
  const [leaveTypesError, setLeaveTypesError] = useState(null);

  // UI state
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [previewItem, setPreviewItem] = useState(null); // For timeline & preview drawer
  const [previewType, setPreviewType] = useState(null); // 'leave' | 'reg' | 'shift' | 'compoff'
  const [selectedDateDetailModal, setSelectedDateDetailModal] = useState(null); // Leave Calendar date details modal

  // Rework & Reapply state
  const [reworkTarget, setReworkTarget] = useState(null); // Item being reworked/edited
  const [reworkType, setReworkType] = useState(null); // 'leave' | 'permission' | 'reg' | 'shift' | 'compoff'
  const [reapplyTarget, setReapplyTarget] = useState(null); // Item being reapplied
  const [reworkNotes, setReworkNotes] = useState(''); // Employee corrections/rework notes

  // Wizard state for Apply Leave
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [leaveWizardStep, setLeaveWizardStep] = useState(1);
  const [leaveForm, setLeaveForm] = useState({
    leave_type_id: '',
    from_date: '',
    to_date: '',
    half_day: false,
    half_day_period: 'first_half',
    permission_date: new Date().toISOString().slice(0, 10),
    start_time: '14:00',
    end_time: '15:00',
    reason: '',
    emergency_contact: '',
    delegate_employee: '',
    attachment_name: ''
  });

  // Modal / Form states for other tabs
  const [showRegModal, setShowRegModal] = useState(false);
  const [regForm, setRegForm] = useState({
    attendance_date: '',
    request_type: 'missing_punch',
    proposed_in_time: '',
    proposed_out_time: '',
    alternate_status: '',
    comp_off_id: '',
    reason: '',
    manager_id: '',
    attachment_name: ''
  });
  const [regCalendarMonth, setRegCalendarMonth] = useState(new Date());
  const [loadingEligibleDates, setLoadingEligibleDates] = useState(false);
  const [selectedAttDetail, setSelectedAttDetail] = useState(null);

  const fetchEligibleDatesForMonth = useCallback(async (targetDate) => {
    setLoadingEligibleDates(true);
    try {
      const year = targetDate.getFullYear();
      const monthStr = `${year}-${String(targetDate.getMonth() + 1).padStart(2, '0')}`;
      const res = await axios.get(`${API_URL}/ess/regularization/eligible-dates?month=${monthStr}`, { withCredentials: true });
      if (Array.isArray(res.data)) {
        setEligibleDates(res.data);
      } else {
        setEligibleDates(res.data?.eligible_dates || []);
        if (res.data?.policy_name) setRegPolicyInfo(res.data);
      }
    } catch (e) {
      console.error('Failed to fetch eligible dates', e);
    } finally {
      setLoadingEligibleDates(false);
    }
  }, []);

  const actionRequiredDates = React.useMemo(() => {
    return (eligibleDates || []).filter((d) => {
      const s = (d.current_status || '').toLowerCase();
      if (s === 'present' && !d.has_exception) return false;
      if (['holiday', 'weekly_off', 'wo', 'leave', 'compoff'].includes(s) && !d.has_exception) return false;
      return ['absent', 'half_day'].includes(s) || Boolean(d.has_exception);
    });
  }, [eligibleDates]);

  const getRegCalendarCells = (targetMonth) => {
    const year = targetMonth.getFullYear();
    const monthIndex = targetMonth.getMonth();
    const firstDay = new Date(year, monthIndex, 1);
    const lastDay = new Date(year, monthIndex + 1, 0);
    const startCol = (firstDay.getDay() + 6) % 7; // Mon=0
    const daysInMonth = lastDay.getDate();

    const cells = [];
    for (let i = 0; i < startCol; i++) {
      cells.push(null);
    }
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const eligItem = (actionRequiredDates || []).find((e) => e.attendance_date === dateStr);
      cells.push({
        dateStr,
        dayNum: d,
        isEligible: Boolean(eligItem),
        isSelected: regForm.attendance_date === dateStr,
        eligItem: eligItem || null,
      });
    }
    return cells;
  };

  const handleSelectRegDate = (dateStr, eligItem) => {
    const item = eligItem || (actionRequiredDates || []).find((e) => e.attendance_date === dateStr) || (eligibleDates || []).find((e) => e.attendance_date === dateStr);
    setSelectedAttDetail(item || null);
    if (!item) {
      setRegForm((prev) => ({ ...prev, attendance_date: '' }));
      return;
    }
    setRegForm((prev) => {
      const updated = { ...prev, attendance_date: dateStr };
      const inT = item.current_in_time;
      const outT = item.current_out_time;
      const status = (item.current_status || '').toLowerCase();
      if (status === 'half_day' || status === 'absent') {
        updated.alternate_status = 'present';
      }
      if (inT && !outT) {
        updated.request_type = 'missing_punch';
        updated.proposed_in_time = inT.slice(0, 5);
      } else if (!inT && outT) {
        updated.request_type = 'missing_punch';
        updated.proposed_out_time = outT.slice(0, 5);
      } else if (inT && outT) {
        updated.request_type = 'wrong_punch';
        updated.proposed_in_time = inT.slice(0, 5);
        updated.proposed_out_time = outT.slice(0, 5);
      } else {
        updated.request_type = 'missing_punch';
      }
      return updated;
    });
  };


  const [showShiftModal, setShowShiftModal] = useState(false);
  const [shiftForm, setShiftForm] = useState({
    request_type: 'change',
    requested_date: '',
    current_shift_id: 'GEN_9_6',
    requested_shift_id: 'MOR_6_2',
    reason: '',
    comments: ''
  });

  const [showCompoffModal, setShowCompoffModal] = useState(false);
  const [compoffForm, setCompoffForm] = useState({
    worked_date: '',
    comp_off_date: '',
    hours_worked: 8,
    reason: '',
    attachment_name: ''
  });
  const [compoffAttachment, setCompoffAttachment] = useState(null);
  const [compoffUploading, setCompoffUploading] = useState(false);
  const compoffFileInputRef = useRef(null);

  const handleCompoffFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedExts = ['.pdf', '.jpg', '.jpeg', '.png'];
    const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
    const allowedMime = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/pjpeg', 'image/png', 'image/x-png'];

    if (!allowedExts.includes(ext) && !allowedMime.includes(file.type.toLowerCase())) {
      toast.error('File type not supported. Please upload a PDF, JPG, JPEG, or PNG document.');
      if (compoffFileInputRef.current) compoffFileInputRef.current.value = '';
      return;
    }

    const maxBytes = 10 * 1024 * 1024; // 10MB
    if (file.size > maxBytes) {
      toast.error('File size exceeds the 10 MB limit.');
      if (compoffFileInputRef.current) compoffFileInputRef.current.value = '';
      return;
    }

    const sizeFormatted = file.size >= 1024 * 1024 
      ? `${(file.size / (1024 * 1024)).toFixed(1)} MB` 
      : `${Math.max(1, Math.round(file.size / 1024))} KB`;

    setCompoffAttachment({
      file,
      name: file.name,
      filename: file.name,
      size: file.size,
      size_formatted: sizeFormatted,
      type: file.type || 'application/octet-stream',
    });
  };

  const removeCompoffAttachment = () => {
    setCompoffAttachment(null);
    if (compoffFileInputRef.current) compoffFileInputRef.current.value = '';
  };

  // Search / Filter / Sort / Pagination Common States
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [columnSelectorOpen, setColumnSelectorOpen] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState({
    leave: ['appNo', 'leaveType', 'dates', 'days', 'reason', 'status', 'actions'],
    reg: ['reqNo', 'date', 'oldTime', 'newTime', 'reason', 'status', 'actions'],
    shift: ['reqNo', 'shifts', 'effectiveDate', 'reason', 'status', 'actions'],
    compoff: ['reqNo', 'workedDate', 'offDate', 'hours', 'status', 'actions']
  });

  // Calendar View month state (separate from attendance month)
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [bal, apps, perms, pSum, sr, co, cos, regs, elig, eligComp, hols, lTypes] = await Promise.all([
        axios.get(`${API_URL}/ess/leave/balances`, { withCredentials: true }),
        axios.get(`${API_URL}/ess/leave/applications`, { withCredentials: true }),
        axios.get(`${API_URL}/leave/permissions`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/leave/permissions/summary`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/ess/shift-requests`, { withCredentials: true }),
        axios.get(`${API_URL}/ess/compoff-requests`, { withCredentials: true }),
        axios.get(`${API_URL}/companies`, { withCredentials: true }),
        axios.get(`${API_URL}/ess/regularization`, { withCredentials: true }),
        axios.get(`${API_URL}/ess/regularization/eligible-dates`, { withCredentials: true }),
        axios.get(`${API_URL}/ess/regularization/eligible-compoffs`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/ess/holidays`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/leave/types?active_only=true`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setLeaveBalances(bal.data || []);
      setLeaveApps(apps.data || []);
      setPermissions(perms.data || []);
      setPermSummary(pSum.data || null);
      setShiftRequests(sr.data || []);
      setCompoffRequests(co.data || []);
      setCompanies(cos.data || []);
      setRegularizations(regs.data || []);
      setEligibleCompOffs(eligComp.data || []);
      setCompanyHolidays(hols.data || []);

      const rawLts = Array.isArray(lTypes.data) ? lTypes.data : [];
      const loadedLts = rawLts.filter(t => t.is_active !== false && t.status !== 'Inactive' && !t.is_deleted);
      setLeaveTypes(loadedLts);

      if (Array.isArray(elig.data)) {
        setEligibleDates(elig.data);
        setRegPolicyInfo(null);
      } else {
        setEligibleDates(elig.data?.eligible_dates || []);
        setRegPolicyInfo(elig.data || null);
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to load portal data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchActiveLeaveTypes = useCallback(async () => {
    setLeaveTypesLoading(true);
    setLeaveTypesError(null);
    try {
      const { data } = await axios.get(`${API_URL}/leave/types?active_only=true`, { withCredentials: true });
      const rawTypes = Array.isArray(data) ? data : [];
      const activeTypes = rawTypes.filter(t => t.is_active !== false && t.status !== 'Inactive' && !t.is_deleted);
      setLeaveTypes(activeTypes);
      return activeTypes;
    } catch (err) {
      console.error('Failed to load active leave types:', err);
      setLeaveTypesError('Failed to load active leave types. Please contact your HR administrator.');
      return [];
    } finally {
      setLeaveTypesLoading(false);
    }
  }, []);

  // Invalidate and refresh active leave types whenever Apply Leave modal opens
  useEffect(() => {
    if (showLeaveModal) {
      fetchActiveLeaveTypes();
    }
  }, [showLeaveModal, fetchActiveLeaveTypes]);

  // Clear previously selected leave type if it becomes inactive or unavailable
  useEffect(() => {
    if (leaveForm.leave_type_id && leaveForm.leave_type_id !== 'ONE_HOUR_PERMISSION') {
      const isStillAvailable = leaveTypes.some(t => t.id === leaveForm.leave_type_id || t.code === leaveForm.leave_type_id);
      if (!isStillAvailable) {
        setLeaveForm(prev => ({ ...prev, leave_type_id: '' }));
      }
    }
  }, [leaveTypes, leaveForm.leave_type_id]);


  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const fetchAttendance = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/ess/attendance?month=${month}`, { withCredentials: true });
      setAttendance(data || { records: [], summary: {} });
    } catch (e) {
      console.error(e);
    }
  }, [month]);

  useEffect(() => {
    fetchAttendance();
  }, [month, fetchAttendance]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    if (tabParam) {
      const tabMap = {
        'leave': 'leave',
        'leaves': 'leave',
        'attendance': 'attendance',
        'regularization': 'regularization',
        'regularizations': 'regularization',
        'shift': 'shift',
        'shifts': 'shift',
        'compoff': 'compoff',
        'comp-off': 'compoff',
      };
      if (tabMap[tabParam.toLowerCase()]) {
        setActiveTab(tabMap[tabParam.toLowerCase()]);
      }
    }
  }, []);

  // Clean-up filters when active tab changes
  useEffect(() => {
    setSearchTerm('');
    setStatusFilter('all');
    setTypeFilter('all');
    setCurrentPage(1);
  }, [activeTab]);

  // Status utility colors
  const statusColor = (s) => {
    const colors = {
      approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      pending: 'bg-amber-50 text-amber-700 border-amber-200',
      pending_hr: 'bg-blue-50 text-blue-700 border-blue-200',
      pending_manager: 'bg-indigo-50 text-indigo-700 border-indigo-200',
      rejected: 'bg-red-50 text-red-700 border-red-200',
      rework: 'bg-orange-50 text-orange-700 border-orange-200',
      cancelled: 'bg-slate-50 text-slate-500 border-slate-200'
    };
    return colors[s] || 'bg-slate-50 text-slate-600 border-slate-200';
  };

  // KEEP EXACT COLOR MAPPING FOR THE ATTENDANCE GRID CELLS
  const attColor = (s) => ({
    present: 'bg-emerald-400',
    absent: 'bg-red-400',
    leave: 'bg-blue-400',
    compoff: 'bg-teal-400',
    comp_off: 'bg-teal-400',
    half_day: 'bg-amber-400',
    half_day_present_leave: 'bg-gradient-to-r from-emerald-400 to-blue-400',
    half_day_present_compoff: 'bg-gradient-to-r from-emerald-400 to-teal-400',
    weekly_off: 'bg-slate-300',
    holiday: 'bg-purple-400'
  })[s] || 'bg-slate-200';

  // Rework & Reapply Launchers
  const handleOpenRework = (item, type) => {
    setReworkTarget(item);
    setReapplyTarget(null);
    setReworkType(type);
    setReworkNotes('');

    if (type === 'leave') {
      const isPerm = item.is_permission || item.leave_type_code === 'ONE_HOUR_PERMISSION' || item.leave_type_id === 'ONE_HOUR_PERMISSION';
      if (isPerm) {
        setLeaveForm({
          leave_type_id: 'ONE_HOUR_PERMISSION',
          from_date: item.from_date || item.permission_date || '',
          to_date: item.to_date || item.permission_date || '',
          half_day: false,
          half_day_period: 'first_half',
          permission_date: item.permission_date || item.from_date || new Date().toISOString().slice(0, 10),
          start_time: item.start_time || '14:00',
          end_time: item.end_time || '15:00',
          reason: item.reason || '',
          emergency_contact: item.emergency_contact || '',
          delegate_employee: item.delegate_employee || '',
          attachment_name: item.attachment_name || '',
        });
      } else {
        setLeaveForm({
          leave_type_id: item.leave_type_id || item.leave_type_code || item.leave_type || '',
          from_date: item.from_date || '',
          to_date: item.to_date || item.from_date || '',
          half_day: Boolean(item.half_day || item.is_partial_day),
          half_day_period: item.half_day_period || 'first_half',
          permission_date: new Date().toISOString().slice(0, 10),
          start_time: '14:00',
          end_time: '15:00',
          reason: item.reason || '',
          emergency_contact: item.emergency_contact || '',
          delegate_employee: item.delegate_employee || '',
          attachment_name: item.attachment_name || '',
        });
      }
      setLeaveWizardStep(1);
      setShowLeaveModal(true);
    } else if (type === 'permission') {
      setLeaveForm({
        leave_type_id: 'ONE_HOUR_PERMISSION',
        from_date: item.permission_date || '',
        to_date: item.permission_date || '',
        half_day: false,
        half_day_period: 'first_half',
        permission_date: item.permission_date || new Date().toISOString().slice(0, 10),
        start_time: item.start_time || '14:00',
        end_time: item.end_time || '15:00',
        reason: item.reason || '',
        emergency_contact: '',
        delegate_employee: '',
        attachment_name: item.attachment_name || '',
      });
      setLeaveWizardStep(1);
      setShowLeaveModal(true);
    } else if (type === 'reg') {
      setRegForm({
        attendance_date: item.attendance_date || '',
        request_type: item.request_type || 'missing_punch',
        proposed_in_time: item.proposed_in_time || '',
        proposed_out_time: item.proposed_out_time || '',
        alternate_status: item.alternate_status || '',
        half_day_period: item.half_day_period || '',
        comp_off_id: item.comp_off_id || '',
        reason: item.reason || '',
        manager_id: item.manager_user_id || '',
        attachment_name: item.attachment_name || '',
      });
      setShowRegModal(true);
    } else if (type === 'shift') {
      setShiftForm({
        request_type: item.request_type || 'change',
        requested_date: item.requested_date || '',
        current_shift_id: item.current_shift_id || 'GEN_9_6',
        requested_shift_id: item.requested_shift_id || 'MOR_6_2',
        swap_with_employee_id: item.swap_with_employee_id || '',
        reason: item.reason || '',
        comments: item.comments || '',
      });
      setShowShiftModal(true);
    } else if (type === 'compoff') {
      setCompoffForm({
        worked_date: item.worked_date || item.work_date || '',
        comp_off_date: item.comp_off_date || '',
        hours_worked: item.hours_worked || 8,
        reason: item.reason || '',
        attachment_name: item.attachment_name || '',
      });
      const existingAtt = (Array.isArray(item.attachments) && item.attachments[0]) 
        ? item.attachments[0] 
        : (item.attachment_name || item.file_id ? {
            name: item.attachment_name || 'Attached Document',
            filename: item.attachment_name || 'Attached Document',
            file_id: item.file_id || item.attachment_id,
            attachment_id: item.file_id || item.attachment_id,
            size: item.attachment_size,
            type: item.attachment_type,
            url: item.attachment_url,
          } : null);
      setCompoffAttachment(existingAtt);
      setShowCompoffModal(true);
    }
  };

  const handleOpenReapply = (item, type) => {
    setReworkTarget(null);
    setReapplyTarget(item);
    setReworkType(type);
    setReworkNotes('');

    if (type === 'leave') {
      const isPerm = item.is_permission || item.leave_type_code === 'ONE_HOUR_PERMISSION' || item.leave_type_id === 'ONE_HOUR_PERMISSION';
      if (isPerm) {
        setLeaveForm({
          leave_type_id: 'ONE_HOUR_PERMISSION',
          from_date: item.from_date || item.permission_date || '',
          to_date: item.to_date || item.permission_date || '',
          half_day: false,
          half_day_period: 'first_half',
          permission_date: item.permission_date || item.from_date || new Date().toISOString().slice(0, 10),
          start_time: item.start_time || '14:00',
          end_time: item.end_time || '15:00',
          reason: item.reason || '',
          emergency_contact: item.emergency_contact || '',
          delegate_employee: item.delegate_employee || '',
          attachment_name: item.attachment_name || '',
        });
      } else {
        setLeaveForm({
          leave_type_id: item.leave_type_id || item.leave_type_code || item.leave_type || '',
          from_date: item.from_date || '',
          to_date: item.to_date || item.from_date || '',
          half_day: Boolean(item.half_day || item.is_partial_day),
          half_day_period: item.half_day_period || 'first_half',
          permission_date: new Date().toISOString().slice(0, 10),
          start_time: '14:00',
          end_time: '15:00',
          reason: item.reason || '',
          emergency_contact: item.emergency_contact || '',
          delegate_employee: item.delegate_employee || '',
          attachment_name: item.attachment_name || '',
        });
      }
      setLeaveWizardStep(1);
      setShowLeaveModal(true);
    } else if (type === 'permission') {
      setLeaveForm({
        leave_type_id: 'ONE_HOUR_PERMISSION',
        from_date: item.permission_date || '',
        to_date: item.permission_date || '',
        half_day: false,
        half_day_period: 'first_half',
        permission_date: item.permission_date || new Date().toISOString().slice(0, 10),
        start_time: item.start_time || '14:00',
        end_time: item.end_time || '15:00',
        reason: item.reason || '',
        emergency_contact: '',
        delegate_employee: '',
        attachment_name: item.attachment_name || '',
      });
      setLeaveWizardStep(1);
      setShowLeaveModal(true);
    } else if (type === 'reg') {
      setRegForm({
        attendance_date: item.attendance_date || '',
        request_type: item.request_type || 'missing_punch',
        proposed_in_time: item.proposed_in_time || '',
        proposed_out_time: item.proposed_out_time || '',
        alternate_status: item.alternate_status || '',
        half_day_period: item.half_day_period || '',
        comp_off_id: item.comp_off_id || '',
        reason: item.reason || '',
        manager_id: item.manager_user_id || '',
        attachment_name: item.attachment_name || '',
      });
      setShowRegModal(true);
    } else if (type === 'shift') {
      setShiftForm({
        request_type: item.request_type || 'change',
        requested_date: item.requested_date || '',
        current_shift_id: item.current_shift_id || 'GEN_9_6',
        requested_shift_id: item.requested_shift_id || 'MOR_6_2',
        swap_with_employee_id: item.swap_with_employee_id || '',
        reason: item.reason || '',
        comments: item.comments || '',
      });
      setShowShiftModal(true);
    } else if (type === 'compoff') {
      setCompoffForm({
        worked_date: item.worked_date || item.work_date || '',
        comp_off_date: item.comp_off_date || '',
        hours_worked: item.hours_worked || 8,
        reason: item.reason || '',
        attachment_name: item.attachment_name || '',
      });
      const existingAtt = (Array.isArray(item.attachments) && item.attachments[0]) 
        ? item.attachments[0] 
        : (item.attachment_name || item.file_id ? {
            name: item.attachment_name || 'Attached Document',
            filename: item.attachment_name || 'Attached Document',
            file_id: item.file_id || item.attachment_id,
            attachment_id: item.file_id || item.attachment_id,
            size: item.attachment_size,
            type: item.attachment_type,
            url: item.attachment_url,
          } : null);
      setCompoffAttachment(existingAtt);
      setShowCompoffModal(true);
    }
  };

  // Submission Handlers
  const handleLeaveSubmit = async (e) => {
    if (e) e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const isPermissionType = leaveForm.leave_type_id === 'ONE_HOUR_PERMISSION';

      if (isPermissionType) {
        if (!leaveForm.permission_date || !leaveForm.start_time || !leaveForm.end_time || !leaveForm.reason.trim()) {
          toast.error('Please fill all required permission fields (Date, Start Time, End Time, Reason)');
          setSubmitting(false);
          return;
        }

        if (reworkTarget) {
          const targetId = reworkTarget.id || reworkTarget._id;
          await axios.put(`${API_URL}/leave/permissions/${targetId}/resubmit`, {
            permission_date: leaveForm.permission_date,
            start_time: leaveForm.start_time,
            end_time: leaveForm.end_time,
            reason: leaveForm.reason.trim(),
            attachment_name: leaveForm.attachment_name || null,
            rework_notes: reworkNotes,
          }, { withCredentials: true });
          toast.success('Permission request resubmitted successfully!');
        } else {
          const permPayload = {
            permission_date: leaveForm.permission_date,
            start_time: leaveForm.start_time,
            end_time: leaveForm.end_time,
            reason: leaveForm.reason.trim(),
            attachment_name: leaveForm.attachment_name || null,
          };
          if (reapplyTarget) {
            permPayload.reapplied_from_id = reapplyTarget.id || reapplyTarget._id;
          }
          await axios.post(`${API_URL}/leave/permissions`, permPayload, { withCredentials: true });
          toast.success(reapplyTarget ? 'Permission request reapplied successfully!' : 'Permission submitted successfully!');
        }

        setShowLeaveModal(false);
        setReworkTarget(null);
        setReapplyTarget(null);
        setReworkNotes('');
        setLeaveWizardStep(1);
        setLeaveForm({
          leave_type_id: '', from_date: '', to_date: '', half_day: false,
          permission_date: new Date().toISOString().slice(0, 10), start_time: '14:00', end_time: '15:00',
          reason: '', emergency_contact: '', delegate_employee: '', attachment_name: ''
        });
        fetchAll();
        return;
      }

      const lt = leaveTypes.find(t => t.id === leaveForm.leave_type_id);
      let computedDays = 1;
      if (leaveForm.from_date && leaveForm.to_date) {
        const d1 = new Date(leaveForm.from_date);
        const d2 = new Date(leaveForm.to_date);
        const diffTime = Math.abs(d2 - d1);
        computedDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      }
      if (leaveForm.half_day) computedDays = 0.5;

      const payload = {
        leave_type_id: leaveForm.leave_type_id,
        from_date: leaveForm.from_date,
        to_date: leaveForm.to_date,
        total_days: computedDays,
        days: computedDays,
        is_partial_day: !!leaveForm.half_day,
        half_day: !!leaveForm.half_day,
        half_day_period: leaveForm.half_day ? leaveForm.half_day_period : null,
        reason: leaveForm.reason || 'N/A',
        contact_during_leave: leaveForm.emergency_contact || null,
        emergency_contact: leaveForm.emergency_contact || null,
        delegate_employee: leaveForm.delegate_employee || null,
        address_during_leave: leaveForm.delegate_employee || null,
        attachment_name: leaveForm.attachment_name || null,
        company_id: companies[0]?.id,
        leave_type_name: lt?.name || '',
      };

      if (reworkTarget) {
        payload.rework_notes = reworkNotes;
        const targetId = reworkTarget.id || reworkTarget._id;
        await axios.put(`${API_URL}/ess/leave/applications/${targetId}/resubmit`, payload, { withCredentials: true });
        toast.success('Leave application resubmitted successfully!');
      } else {
        if (reapplyTarget) {
          payload.reapplied_from_id = reapplyTarget.id || reapplyTarget._id;
        }
        await axios.post(`${API_URL}/leave/applications`, payload, { withCredentials: true });
        toast.success(reapplyTarget ? 'Leave application reapplied successfully!' : 'Leave application submitted successfully');
      }

      setShowLeaveModal(false);
      setReworkTarget(null);
      setReapplyTarget(null);
      setReworkNotes('');
      setLeaveWizardStep(1);
      setLeaveForm({
        leave_type_id: '', from_date: '', to_date: '', half_day: false, half_day_period: 'first_half',
        permission_date: new Date().toISOString().slice(0, 10), start_time: '14:00', end_time: '15:00',
        reason: '', emergency_contact: '', delegate_employee: '', attachment_name: ''
      });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.response?.data?.message || 'Error submitting application');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePermissionCancel = async (id) => {
    if (!window.confirm('Cancel this permission request?')) return;
    try {
      await axios.patch(`${API_URL}/leave/permissions/${id}`, { status: 'cancelled' }, { withCredentials: true });
      toast.success('Permission request cancelled');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to cancel permission');
    }
  };

  const handlePermissionDelete = async (permParam) => {
    const targetId = typeof permParam === 'object' ? (permParam.id || permParam._id) : permParam;
    if (!targetId) return;
    const ref = `PRM-${String(targetId).slice(-6).toUpperCase()}`;
    if (!window.confirm(`Are you sure you want to delete permission request ${ref}?`)) return;
    try {
      await axios.delete(`${API_URL}/leave/permissions/${targetId}`, { withCredentials: true });
      toast.success(`Permission request ${ref} deleted successfully`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.response?.data?.message || 'Failed to delete permission');
    }
  };

  const handleLeaveCancel = async (appParam) => {
    const app = typeof appParam === 'object' ? appParam : leaveApps.find(a => (a.id === appParam || a._id === appParam));
    const targetId = typeof appParam === 'object' ? (appParam.id || appParam._id) : appParam;
    if (!targetId) return;

    const ltName = app?.leave_type_name || app?.leave_type_display || app?.leave_type || 'Leave';
    const datesStr = app?.from_date && app?.to_date ? `${app.from_date} to ${app.to_date}` : 'selected dates';
    const appRef = `LV-${String(targetId).slice(-6).toUpperCase()}`;

    const confirmMsg = `Confirm Cancellation:\n\nApplication Ref: ${appRef}\nLeave Type: ${ltName}\nDates: ${datesStr} (${app?.total_days || app?.days || 1} day(s))\nCurrent Status: ${(app?.status || 'pending').toUpperCase()}\n\nAre you sure you want to cancel this leave application?`;

    if (!window.confirm(confirmMsg)) return;

    try {
      try {
        await axios.patch(`${API_URL}/leave/applications/${targetId}`, { status: 'cancelled' }, { withCredentials: true });
      } catch (patchErr) {
        if (patchErr.response?.status === 404 || patchErr.response?.status === 405) {
          await axios.post(`${API_URL}/leave/applications/${targetId}/cancel`, {}, { withCredentials: true });
        } else {
          throw patchErr;
        }
      }
      toast.success(`Leave application ${appRef} cancelled successfully.`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.response?.data?.message || 'Failed to cancel leave application.');
    }
  };

  const handleLeaveDelete = async (appParam) => {
    const targetId = typeof appParam === 'object' ? (appParam.id || appParam._id) : appParam;
    if (!targetId) return;
    const appRef = `LV-${String(targetId).slice(-6).toUpperCase()}`;
    if (!window.confirm(`Are you sure you want to delete leave application ${appRef}? This record will be safely removed.`)) return;
    try {
      await axios.delete(`${API_URL}/ess/leave/applications/${targetId}`, { withCredentials: true });
      toast.success(`Leave application ${appRef} deleted successfully`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.response?.data?.message || 'Failed to delete leave application');
    }
  };

  const handleRegSubmit = async (e) => {
    e.preventDefault();
    if (!regForm.attendance_date || !regForm.reason.trim()) {
      toast.error('Please pick a date and provide a reason.');
      return;
    }
    if (regForm.request_type === 'compoff' && !regForm.comp_off_id) {
      toast.error('Please select an approved Comp-Off record to adjust against.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...regForm,
        proposed_in_time: regForm.proposed_in_time || null,
        proposed_out_time: regForm.proposed_out_time || null,
        alternate_status: regForm.request_type === 'compoff' ? 'leave' : (regForm.alternate_status || null),
        comp_off_id: regForm.request_type === 'compoff' ? regForm.comp_off_id : null,
      };

      if (reworkTarget) {
        payload.rework_notes = reworkNotes;
        const targetId = reworkTarget.id || reworkTarget._id;
        await axios.put(`${API_URL}/ess/regularization/${targetId}/resubmit`, payload, { withCredentials: true });
        toast.success('Attendance regularization resubmitted successfully!');
      } else {
        if (reapplyTarget) {
          payload.reapplied_from_id = reapplyTarget.id || reapplyTarget._id;
        }
        await axios.post(`${API_URL}/ess/regularization`, payload, { withCredentials: true });
        toast.success(reapplyTarget ? 'Regularization request reapplied successfully!' : 'Regularization request submitted');
      }

      setShowRegModal(false);
      setReworkTarget(null);
      setReapplyTarget(null);
      setReworkNotes('');
      setSelectedAttDetail(null);
      setRegForm({
        attendance_date: '', request_type: 'missing_punch', proposed_in_time: '',
        proposed_out_time: '', alternate_status: '', comp_off_id: '', reason: '', manager_id: '', attachment_name: ''
      });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.response?.data?.message || 'Error submitting regularization');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegCancel = async (id) => {
    if (!window.confirm('Cancel this regularization request?')) return;
    try {
      await axios.post(`${API_URL}/ess/regularization/${id}/cancel`, null, { withCredentials: true });
      toast.success('Regularization request cancelled');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cancel failed');
    }
  };

  const handleRegDelete = async (regParam) => {
    const targetId = typeof regParam === 'object' ? (regParam.id || regParam._id) : regParam;
    if (!targetId) return;
    const ref = `REG-${String(targetId).slice(-6).toUpperCase()}`;
    if (!window.confirm(`Are you sure you want to delete regularization request ${ref}?`)) return;
    try {
      await axios.delete(`${API_URL}/ess/regularization/${targetId}`, { withCredentials: true });
      toast.success(`Regularization request ${ref} deleted successfully`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.response?.data?.message || 'Failed to delete regularization');
    }
  };

  const handleShiftSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...shiftForm };
      if (reworkTarget) {
        payload.rework_notes = reworkNotes;
        const targetId = reworkTarget.id || reworkTarget._id;
        await axios.put(`${API_URL}/ess/shift-requests/${targetId}/resubmit`, payload, { withCredentials: true });
        toast.success('Shift exchange request resubmitted successfully!');
      } else {
        if (reapplyTarget) {
          payload.reapplied_from_id = reapplyTarget.id || reapplyTarget._id;
        }
        await axios.post(`${API_URL}/ess/shift-requests`, payload, { withCredentials: true });
        toast.success(reapplyTarget ? 'Shift request reapplied successfully!' : 'Shift exchange request submitted');
      }

      setShowShiftModal(false);
      setReworkTarget(null);
      setReapplyTarget(null);
      setReworkNotes('');
      setShiftForm({
        request_type: 'change', requested_date: '', current_shift_id: 'GEN_9_6',
        requested_shift_id: 'MOR_6_2', reason: '', comments: ''
      });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.response?.data?.message || 'Error submitting shift request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleShiftCancel = async (shiftParam) => {
    const targetId = typeof shiftParam === 'object' ? (shiftParam.id || shiftParam._id) : shiftParam;
    if (!targetId) return;
    if (!window.confirm('Are you sure you want to cancel this shift request?')) return;
    try {
      await axios.post(`${API_URL}/ess/shift-requests/${targetId}/cancel`, {}, { withCredentials: true });
      toast.success('Shift request cancelled successfully');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.response?.data?.message || 'Failed to cancel shift request');
    }
  };

  const handleShiftDelete = async (shiftParam) => {
    const targetId = typeof shiftParam === 'object' ? (shiftParam.id || shiftParam._id) : shiftParam;
    if (!targetId) return;
    const ref = `S-${String(targetId).slice(-6).toUpperCase()}`;
    if (!window.confirm(`Are you sure you want to delete shift request ${ref}?`)) return;
    try {
      await axios.delete(`${API_URL}/ess/shift-requests/${targetId}`, { withCredentials: true });
      toast.success(`Shift request ${ref} deleted successfully`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.response?.data?.message || 'Failed to delete shift request');
    }
  };

  const handleCompoffSubmit = async (e) => {
    e.preventDefault();
    if (!compoffForm.worked_date) {
      toast.error('Worked Date is mandatory.');
      return;
    }
    const todayIso = new Date().toISOString().split('T')[0];
    if (compoffForm.worked_date >= todayIso) {
      toast.error('Worked date must be a past date on which you have already worked. Today and future dates cannot be requested for Comp-Off.');
      return;
    }
    if (!compoffForm.hours_worked || compoffForm.hours_worked <= 0) {
      toast.error('Hours Worked is mandatory and must be greater than 0.');
      return;
    }
    if (!compoffForm.reason || !compoffForm.reason.trim()) {
      toast.error('Reason / Work Details is mandatory.');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...compoffForm };

      // Securely upload attachment to backend if a local file was selected
      if (compoffAttachment?.file) {
        setCompoffUploading(true);
        const fd = new FormData();
        fd.append('file', compoffAttachment.file);
        const uploadRes = await axios.post(`${API_URL}/ess/compoff-requests/upload-attachment`, fd, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const uploadedData = uploadRes.data;
        payload.attachments = [uploadedData];
        payload.attachment_name = uploadedData.name || uploadedData.filename;
        payload.attachment_id = uploadedData.file_id;
        payload.file_id = uploadedData.file_id;
        payload.attachment_url = uploadedData.url;
        payload.attachment_size = uploadedData.size;
        payload.attachment_type = uploadedData.type;
      } else if (compoffAttachment) {
        payload.attachments = [compoffAttachment];
        payload.attachment_name = compoffAttachment.name || compoffAttachment.filename;
        payload.attachment_id = compoffAttachment.file_id || compoffAttachment.attachment_id;
        payload.file_id = compoffAttachment.file_id || compoffAttachment.attachment_id;
        payload.attachment_url = compoffAttachment.url;
        payload.attachment_size = compoffAttachment.size;
        payload.attachment_type = compoffAttachment.type;
      } else {
        payload.attachments = [];
        payload.attachment_name = null;
        payload.file_id = null;
        payload.attachment_id = null;
        payload.attachment_url = null;
      }

      if (reworkTarget) {
        payload.rework_notes = reworkNotes;
        const targetId = reworkTarget.id || reworkTarget._id;
        await axios.put(`${API_URL}/ess/compoff-requests/${targetId}/resubmit`, payload, { withCredentials: true });
        toast.success('Comp-Off request resubmitted successfully!');
      } else {
        if (reapplyTarget) {
          payload.reapplied_from_id = reapplyTarget.id || reapplyTarget._id;
        }
        await axios.post(`${API_URL}/ess/compoff-requests`, payload, { withCredentials: true });
        toast.success(reapplyTarget ? 'Comp-Off request reapplied successfully!' : 'Comp-Off request submitted successfully!');
      }

      setShowCompoffModal(false);
      setReworkTarget(null);
      setReapplyTarget(null);
      setReworkNotes('');
      setCompoffAttachment(null);
      if (compoffFileInputRef.current) compoffFileInputRef.current.value = '';
      setCompoffForm({ worked_date: '', comp_off_date: '', hours_worked: 8, reason: '', attachment_name: '' });
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.response?.data?.message || 'Error submitting comp-off request');
    } finally {
      setSubmitting(false);
      setCompoffUploading(false);
    }
  };

  const handleCompoffCancel = async (compoffParam) => {
    const targetId = typeof compoffParam === 'object' ? (compoffParam.id || compoffParam._id) : compoffParam;
    if (!targetId) return;
    if (!window.confirm('Are you sure you want to cancel this Comp-Off request?')) return;
    try {
      await axios.post(`${API_URL}/ess/compoff-requests/${targetId}/cancel`, {}, { withCredentials: true });
      toast.success('Comp-Off request cancelled successfully');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.response?.data?.message || 'Failed to cancel Comp-Off request');
    }
  };

  const handleCompoffDelete = async (compoffParam) => {
    const targetId = typeof compoffParam === 'object' ? (compoffParam.id || compoffParam._id) : compoffParam;
    if (!targetId) return;
    const ref = `CO-${String(targetId).slice(-6).toUpperCase()}`;
    if (!window.confirm(`Are you sure you want to delete Comp-Off request ${ref}?`)) return;
    try {
      await axios.delete(`${API_URL}/ess/compoff-requests/${targetId}`, { withCredentials: true });
      toast.success(`Comp-Off request ${ref} deleted successfully`);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.response?.data?.message || 'Failed to delete Comp-Off request');
    }
  };

  // Excel and PDF Export
  const handleExportExcel = (type) => {
    if (type === 'Comp-Off') {
      try {
        const headers = ["Request No", "Worked Date", "Hours Worked", "Earned Credit (Days)", "Used Credit (Days)", "Remaining Balance (Days)", "Comp-Off Date", "Expiry Date", "Reason", "Status"];
        const rows = (compoffRequests || []).map((c, i) => [
          c.request_number || ('C-' + String(i + 1).padStart(4, '0')),
          c.worked_date || c.work_date || '—',
          (c.hours_worked || 8) + 'h',
          getCompoffCredit(c),
          c.used_days !== undefined && c.used_days !== null ? c.used_days : 0,
          c.remaining_balance !== undefined && c.remaining_balance !== null ? c.remaining_balance : Math.max(0, getCompoffCredit(c) - getCompoffUsed(c)),
          c.comp_off_date || '—',
          c.expiry_date || '—',
          `"${(c.reason || '—').replace(/"/g, '""')}"`,
          c.status || '—'
        ]);
        const csvContent = "data:text/csv;charset=utf-8," + [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', `compoff_ledger_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Comp-Off Ledger exported successfully");
        return;
      } catch (err) {
        console.error("Export CSV error:", err);
      }
    }
    toast.success(`Exporting ${type} History to Excel...`);
  };

  const handleExportPDF = (type) => {
    window.print();
  };

  // Helper for sorting
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  // Dynamic calculations for Leave summary cards - Strictly isolate each leave type
  const isMatchCL = (b) => {
    const code = (b.leave_type_code || b.leave_type || '').toUpperCase().trim();
    const name = (b.leave_type_name || '').toLowerCase().trim();
    if (code === 'CL' || code === 'CASUAL' || code === 'CASUAL_LEAVE') return true;
    if (['AL', 'EL', 'SL', 'PL', 'ML', 'COMPOFF', 'CO', 'COMP_OFF', 'LOP'].includes(code)) return false;
    return name.includes('casual');
  };

  const isMatchEL = (b) => {
    const code = (b.leave_type_code || b.leave_type || '').toUpperCase().trim();
    const name = (b.leave_type_name || '').toLowerCase().trim();
    if (code === 'EL' || code === 'EARNED' || code === 'EARNED_LEAVE') return true;
    if (['AL', 'CL', 'SL', 'ML', 'COMPOFF', 'CO', 'COMP_OFF', 'LOP'].includes(code)) return false;
    return name.includes('earned') || name.includes('privilege');
  };

  const isMatchSL = (b) => {
    const code = (b.leave_type_code || b.leave_type || '').toUpperCase().trim();
    const name = (b.leave_type_name || '').toLowerCase().trim();
    if (code === 'SL' || code === 'SICK' || code === 'SICK_LEAVE') return true;
    if (['AL', 'CL', 'EL', 'PL', 'ML', 'COMPOFF', 'CO', 'COMP_OFF', 'LOP'].includes(code)) return false;
    return name.includes('sick') || name.includes('medical');
  };

  const isMatchAL = (b) => {
    const code = (b.leave_type_code || b.leave_type || '').toUpperCase().trim();
    const name = (b.leave_type_name || '').toLowerCase().trim();
    if (code === 'AL' || code === 'ANNUAL' || code === 'ANNUAL_LEAVE') return true;
    if (['CL', 'EL', 'SL', 'PL', 'ML', 'COMPOFF', 'CO', 'COMP_OFF', 'LOP'].includes(code)) return false;
    return name.includes('annual');
  };

  const isMatchCompOff = (b) => {
    const code = (b.leave_type_code || b.leave_type || '').toUpperCase().trim();
    const name = (b.leave_type_name || '').toLowerCase().trim();
    return ['COMPOFF', 'CO', 'COMP_OFF', 'COMP-OFF'].includes(code) || name.includes('compensatory') || name.includes('comp-off') || name.includes('compoff');
  };

  const clType = (leaveTypes || []).find(t => isMatchCL({ leave_type_code: t.code, leave_type_name: t.name }));
  const slType = (leaveTypes || []).find(t => isMatchSL({ leave_type_code: t.code, leave_type_name: t.name }));
  const elType = (leaveTypes || []).find(t => isMatchEL({ leave_type_code: t.code, leave_type_name: t.name }));
  const alType = (leaveTypes || []).find(t => isMatchAL({ leave_type_code: t.code, leave_type_name: t.name }));

  const clBal = (leaveBalances || []).find(isMatchCL);
  const slBal = (leaveBalances || []).find(isMatchSL);
  const elBal = (leaveBalances || []).find(isMatchEL);
  const alBal = (leaveBalances || []).find(isMatchAL);
  const coBal = (leaveBalances || []).find(isMatchCompOff);

  const clData = clBal ? {
    balance: Math.round(parseFloat(clBal.available_balance ?? clBal.closing_balance ?? clBal.balance ?? 0) * 100) / 100,
    entitled: Math.round(parseFloat(clBal.total_entitled ?? clBal.entitled ?? clBal.opening_balance ?? 0) * 100) / 100,
    availed: Math.round(parseFloat(clBal.availed ?? clBal.already_availed ?? 0) * 100) / 100
  } : {
    balance: 0,
    entitled: 0,
    availed: 0
  };

  const slData = slBal ? {
    balance: Math.round(parseFloat(slBal.available_balance ?? slBal.closing_balance ?? slBal.balance ?? 0) * 100) / 100,
    entitled: Math.round(parseFloat(slBal.total_entitled ?? slBal.entitled ?? slBal.opening_balance ?? 0) * 100) / 100,
    availed: Math.round(parseFloat(slBal.availed ?? slBal.already_availed ?? 0) * 100) / 100
  } : {
    balance: 0,
    entitled: 0,
    availed: 0
  };

  const elData = elBal ? {
    balance: Math.round(parseFloat(elBal.available_balance ?? elBal.closing_balance ?? elBal.balance ?? 0) * 100) / 100,
    entitled: Math.round(parseFloat(elBal.total_entitled ?? elBal.entitled ?? elBal.opening_balance ?? 0) * 100) / 100,
    availed: Math.round(parseFloat(elBal.availed ?? elBal.already_availed ?? 0) * 100) / 100
  } : {
    balance: 0,
    entitled: 0,
    availed: 0
  };

  const alData = alBal ? {
    balance: Math.round(parseFloat(alBal.available_balance ?? alBal.closing_balance ?? alBal.balance ?? 0) * 100) / 100,
    entitled: Math.round(parseFloat(alBal.total_entitled ?? alBal.entitled ?? alBal.opening_balance ?? 0) * 100) / 100,
    availed: Math.round(parseFloat(alBal.availed ?? alBal.already_availed ?? 0) * 100) / 100
  } : {
    balance: 0,
    entitled: 0,
    availed: 0
  };

  // Comp-off credit resolution helpers
  const getCompoffCredit = (c) => {
    if (!c) return 0;
    if (c.earned_days !== undefined && c.earned_days !== null && !isNaN(c.earned_days)) return parseFloat(c.earned_days);
    if (c.days !== undefined && c.days !== null && !isNaN(c.days)) return parseFloat(c.days);
    if (c.earned_credit !== undefined && c.earned_credit !== null && !isNaN(c.earned_credit)) return parseFloat(c.earned_credit);
    if (c.credit_days !== undefined && c.credit_days !== null && !isNaN(c.credit_days)) return parseFloat(c.credit_days);
    if (c.amount !== undefined && c.amount !== null && !isNaN(c.amount)) return parseFloat(c.amount);
    if (c.hours_worked && parseFloat(c.hours_worked) > 0) {
      return parseFloat(c.hours_worked) < 8 ? 0.5 : Math.round(parseFloat(c.hours_worked) / 8 * 100) / 100;
    }
    return 0;
  };

  const getCompoffUsed = (c) => {
    if (!c) return 0;
    if (c.used_days !== undefined && c.used_days !== null && !isNaN(c.used_days)) return parseFloat(c.used_days);
    return 0; // Never return earned credit as used
  };

  // Comp-off balance resolution
  const compoffBalanceDoc = coBal || (leaveBalances || []).find(isMatchCompOff);

  const availableCompoff = compoffBalanceDoc
    ? Math.round(parseFloat(compoffBalanceDoc.available_balance ?? compoffBalanceDoc.closing_balance ?? compoffBalanceDoc.balance ?? 0) * 100) / 100
    : Math.max(0, Math.round((
        (compoffRequests || []).filter(c => (c.status === 'approved' || c.status === 'completed') && (!c.expiry_date || c.expiry_date >= new Date().toISOString().slice(0, 10)))
          .reduce((sum, c) => sum + getCompoffCredit(c), 0) -
        (compoffRequests || []).reduce((sum, c) => sum + getCompoffUsed(c), 0)
      ) * 100) / 100);

  const usedFromDoc = compoffBalanceDoc ? parseFloat(compoffBalanceDoc.availed ?? compoffBalanceDoc.already_availed ?? compoffBalanceDoc.used_days ?? null) : null;
  
  // Deduplicated calculation across approved leave applications and regularizations
  const seenUtilDates = new Set();
  const seenUtilCoIds = new Set();
  let usedFromLeaves = 0;
  (leaveApps || []).forEach(a => {
    if (a.status !== 'approved' && a.status !== 'completed') return;
    const code = (a.leave_type || a.leave_type_code || '').toUpperCase().trim();
    if (!['COMPOFF', 'CO', 'COMP_OFF', 'COMP-OFF', 'COMPENSATORY OFF'].includes(code)) return;
    const days = parseFloat(a.total_days || a.days || 1);
    usedFromLeaves += isNaN(days) ? 0 : days;
    const fDate = (a.from_date || '').slice(0, 10);
    const tDate = (a.to_date || a.from_date || '').slice(0, 10);
    if (fDate) seenUtilDates.add(fDate);
    if (tDate) seenUtilDates.add(tDate);
    if (a.comp_off_id) seenUtilCoIds.add(String(a.comp_off_id).trim());
    if (a.compoff_request_id) seenUtilCoIds.add(String(a.compoff_request_id).trim());
  });

  let usedFromRegs = 0;
  // If employee has never applied for/earned comp-off and has no comp-off leaves, do not infer usage from regularizations
  const hasCoCreditActivity = (compoffRequests || []).length > 0 || (compoffBalanceDoc && (parseFloat(compoffBalanceDoc.total_entitled || compoffBalanceDoc.earned || compoffBalanceDoc.opening_balance || 0) > 0));
  if (hasCoCreditActivity || usedFromLeaves > 0) {
    (regularizations || []).forEach(r => {
      if (r.status !== 'approved' && r.status !== 'completed') return;
      const reqType = (r.request_type || r.regularization_type || '').toLowerCase().trim();
      const coId = r.comp_off_id ? String(r.comp_off_id).trim() : '';
      const isCoReg = ['compoff', 'comp_off', 'comp-off', 'co'].includes(reqType) || (coId && coId !== 'null' && coId !== 'undefined');
      if (!isCoReg) return;
      const attDate = (r.attendance_date || r.date || '').slice(0, 10);
      if (attDate && seenUtilDates.has(attDate)) return;
      if (coId && seenUtilCoIds.has(coId)) return;
      if (attDate) seenUtilDates.add(attDate);
      if (coId) seenUtilCoIds.add(coId);
      const isHalf = r.alternate_status === 'half_day' || !!r.half_day_period || !!r.is_half_day;
      let rDays = parseFloat(r.days || r.regularized_days || (isHalf ? 0.5 : 1));
      if (isNaN(rDays) || rDays <= 0) rDays = isHalf ? 0.5 : 1;
      else if (isHalf && rDays > 0.5) rDays = 0.5;
      usedFromRegs += rDays;
    });
  }

  const fallbackUsedCompoff = Math.round((usedFromLeaves + usedFromRegs) * 100) / 100;
  const usedCompoff = (usedFromDoc !== null && !isNaN(usedFromDoc))
    ? Math.round(usedFromDoc * 100) / 100
    : fallbackUsedCompoff;

  const coData = compoffBalanceDoc ? {
    balance: availableCompoff,
    entitled: Math.round(parseFloat(compoffBalanceDoc.total_entitled ?? compoffBalanceDoc.entitled ?? compoffBalanceDoc.opening_balance ?? compoffBalanceDoc.earned ?? 0) * 100) / 100,
    availed: usedCompoff
  } : {
    balance: availableCompoff,
    entitled: hasCoCreditActivity ? Math.round((availableCompoff + usedCompoff) * 100) / 100 : 0,
    availed: usedCompoff
  };

  // Total Leave card calculations:
  // Only sum applicable leave types under the employee's configured entitlement.
  // Exclude LOP, Comp-Off (tracked in its dedicated card/tab), and Permission credits.
  const isApplicableForTotalLeave = (b) => {
    if (!b) return false;
    const code = (b.leave_type_code || b.leave_type || '').toUpperCase().trim();
    const name = (b.leave_type_name || '').toLowerCase().trim();
    if (code === 'LOP' || code === 'LOSS_OF_PAY' || name.includes('loss of pay')) return false;
    if (['COMPOFF', 'CO', 'COMP_OFF', 'COMP-OFF'].includes(code) || name.includes('compensatory') || name.includes('comp-off') || name.includes('compoff')) return false;
    if (['PERMISSION', 'PERM', 'PERMISSIONS'].includes(code) || name.includes('permission')) return false;
    return true;
  };

  const applicableBalances = (leaveBalances || []).filter(isApplicableForTotalLeave);

  const allEntitledTotal = applicableBalances
    .reduce((sum, b) => sum + parseFloat(b.total_entitled ?? b.entitled ?? b.opening_balance ?? 0), 0);

  const allAvailedTotal = applicableBalances
    .reduce((sum, b) => sum + parseFloat(b.availed ?? b.already_availed ?? 0), 0);

  const allBalancesTotal = applicableBalances
    .reduce((sum, b) => sum + parseFloat(b.available_balance ?? b.closing_balance ?? b.balance ?? 0), 0);

  const totalLeaveEntitled = (leaveBalances && leaveBalances.length > 0)
    ? Math.round(allEntitledTotal * 100) / 100
    : Math.round(((clData.entitled || 0) + (slData.entitled || 0) + (elData.entitled || 0)) * 100) / 100;

  const totalLeaveAvailed = (leaveBalances && leaveBalances.length > 0)
    ? Math.round(allAvailedTotal * 100) / 100
    : Math.round(((clData.availed || 0) + (slData.availed || 0) + (elData.availed || 0)) * 100) / 100;

  const totalLeaveBalance = (leaveBalances && leaveBalances.length > 0)
    ? Math.round(allBalancesTotal * 100) / 100
    : Math.round(((clData.balance || 0) + (slData.balance || 0) + (elData.balance || 0)) * 100) / 100;

  const otherConfiguredBalances = (leaveBalances || []).filter(b => {
    const code = (b.leave_type_code || b.leave_type || '').toUpperCase().trim();
    if (code === 'LOP') return false;
    if (isMatchCL(b) || isMatchSL(b) || isMatchEL(b) || isMatchCompOff(b)) return false;
    return true;
  });

  const appliedCount = leaveApps.length;
  const approvedCount = leaveApps.filter(a => a.status === 'approved').length;
  const pendingCount = leaveApps.filter(a => ['pending', 'pending_hr', 'pending_manager'].includes(a.status)).length;
  const rejectedCount = leaveApps.filter(a => a.status === 'rejected').length;

  // Regularization card counts
  const pendingRegCount = regularizations.filter(r => r.status === 'pending').length;
  const approvedRegCount = regularizations.filter(r => r.status === 'approved').length;
  const rejectedRegCount = regularizations.filter(r => r.status === 'rejected').length;
  const currentMonthRegCount = regularizations.filter(r => r.attendance_date?.startsWith(new Date().toISOString().slice(0, 7))).length;

  // Shift Request card counts
  const pendingShiftCount = shiftRequests.filter(s => s.status === 'pending').length;
  const approvedShiftCount = shiftRequests.filter(s => s.status === 'approved').length;
  const rejectedShiftCount = shiftRequests.filter(s => s.status === 'rejected').length;

  const pendingReqsCount = (compoffRequests || []).filter(c => ['pending', 'pending_manager', 'pending_hr'].includes(c.status))
    .reduce((sum, c) => sum + getCompoffCredit(c), 0);
  const pendingFromLeaves = (leaveApps || []).filter(a => ['pending', 'pending_manager', 'pending_hr'].includes(a.status) && ['COMPOFF', 'CO', 'COMP_OFF', 'COMP-OFF', 'COMPENSATORY OFF'].includes((a.leave_type || a.leave_type_code || '').toUpperCase()))
    .reduce((sum, a) => sum + parseFloat(a.total_days || a.days || 0), 0);
  const docPendingEarn = compoffBalanceDoc && compoffBalanceDoc.pending_earning_days !== undefined
    ? parseFloat(compoffBalanceDoc.pending_earning_days)
    : pendingReqsCount;
  const docPendingLeave = compoffBalanceDoc && compoffBalanceDoc.pending_approval !== undefined
    ? parseFloat(compoffBalanceDoc.pending_approval)
    : pendingFromLeaves;
  const pendingCompoff = Math.round((docPendingEarn + docPendingLeave) * 100) / 100;

  const expiredCompoff = compoffBalanceDoc && compoffBalanceDoc.expired_days !== undefined
    ? Math.round(parseFloat(compoffBalanceDoc.expired_days) * 100) / 100
    : (compoffRequests || []).filter(c => (c.status === 'approved' || c.status === 'completed') && c.expiry_date && c.expiry_date < new Date().toISOString().slice(0, 10))
        .reduce((sum, c) => sum + Math.max(0, getCompoffCredit(c) - getCompoffUsed(c)), 0);

  // Tab configurations
  const tabs = [
    { key: 'leave', label: 'Leave', icon: CalendarIcon },
    { key: 'permission', label: 'Permission', icon: Clock },
    { key: 'attendance', label: 'Attendance', icon: Clock },
    { key: 'regularization', label: 'Regularization', icon: FileEdit },
    { key: 'shift', label: 'Shift Requests', icon: ArrowLeftRight },
    { key: 'compoff', label: 'Comp-Off', icon: Gift },
  ];

  // Helper for Leave Interactive Calendar
  const getISODateString = (dateObj) => {
    if (!dateObj) return '';
    const y = dateObj.getFullYear();
    const m = String(dateObj.getMonth() + 1).padStart(2, '0');
    const d = String(dateObj.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const formatDateTimeStr = (ts) => {
    if (!ts) return null;
    try {
      const d = new Date(ts);
      if (isNaN(d.getTime())) return String(ts);
      return d.toLocaleString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } catch (e) {
      return String(ts);
    }
  };

  const getCalendarDays = () => {
    const year = calendarMonth.getFullYear();
    const monthIndex = calendarMonth.getMonth();
    const firstDay = new Date(year, monthIndex, 1);
    const lastDay = new Date(year, monthIndex + 1, 0);
    const startCol = (firstDay.getDay() + 6) % 7; // Mon=0
    const daysInMonth = lastDay.getDate();

    const cells = [];
    // Pad
    for (let i = 0; i < startCol; i++) {
      cells.push(null);
    }
    // Days
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push(new Date(year, monthIndex, d));
    }
    return cells;
  };

  const getCalendarDayInfo = (dateObj) => {
    if (!dateObj) return { isNull: true };
    const iso = getISODateString(dateObj);

    // Matching leaves
    const matchingLeaves = (leaveApps || []).filter(app => {
      if (!app.from_date || !app.to_date) return false;
      return iso >= app.from_date && iso <= app.to_date;
    });

    // Matching holidays
    const matchingHolidays = (companyHolidays || []).filter(h => {
      const hDate = h.holiday_date || h.date;
      if (!hDate) return false;
      if (h.from_date && h.to_date) {
        return iso >= h.from_date && iso <= h.to_date;
      }
      return hDate === iso;
    });

    const isWeekend = dateObj.getDay() === 0 || dateObj.getDay() === 6;

    const hasApproved = matchingLeaves.some(l => l.status === 'approved');
    const hasPending = matchingLeaves.some(l => l.status === 'pending');
    const hasRejected = matchingLeaves.some(l => l.status === 'rejected');
    const hasCancelled = matchingLeaves.some(l => l.status === 'cancelled');
    const hasHoliday = matchingHolidays.length > 0;

    // Matching attendance record
    const attendanceRecord = (attendance?.records || []).find(r => (r.attendance_date || r.date) === iso);

    // Matching permissions
    const matchingPermissions = (permissions || []).filter(p => (p.permission_date || p.date) === iso);

    // Matching regularizations
    const matchingRegularizations = (regularizations || []).filter(reg => (reg.attendance_date || reg.date) === iso);

    // Matching comp-offs
    const matchingCompOffs = (compoffRequests || []).filter(c => {
      const cDate = c.comp_off_date || c.availed_date || c.work_date || c.worked_date;
      return cDate === iso;
    });

    const approvedCompOff = matchingCompOffs.find(c => (c.status === 'approved' || c.status === 'availed') && (c.comp_off_date === iso || c.availed_date === iso));
    const approvedSplitCompOff = matchingCompOffs.find(c => 
      (c.status === 'approved' || c.status === 'availed') && 
      (c.comp_off_date === iso || c.availed_date === iso) && 
      (c.is_partial_day || c.half_day || c.days === 0.5 || c.earned_days === 0.5 || c.used_days === 0.5)
    );

    // Split attendance & leave / compoff detection
    const approvedSplitLeave = matchingLeaves.find(l => l.status === 'approved' && (l.is_partial_day || l.half_day));
    
    const isSplitLeave = attendanceRecord?.status === 'half_day_present_leave' || Boolean(
      approvedSplitLeave && attendanceRecord && (
        attendanceRecord.in_time || attendanceRecord.first_in || attendanceRecord.actual_in_time ||
        ['present', 'half_day', 'half_day_present_leave'].includes((attendanceRecord.status || '').toLowerCase())
      )
    );

    const isSplitCompoff = attendanceRecord?.status === 'half_day_present_compoff' || Boolean(
      approvedSplitCompOff && attendanceRecord && (
        attendanceRecord.in_time || attendanceRecord.first_in || attendanceRecord.actual_in_time ||
        ['present', 'half_day', 'half_day_present_compoff'].includes((attendanceRecord.status || '').toLowerCase())
      )
    );

    const hasSplit = Boolean(
      attendanceRecord?.has_attendance_split || 
      isSplitLeave || 
      isSplitCompoff
    );

    const firstHalfStatus = attendanceRecord?.first_half_status || (
      approvedSplitLeave ? ((approvedSplitLeave.half_day_period === 'first_half' || approvedSplitLeave.half_day_session === 'first_half') ? 'leave' : 'present') :
      (approvedSplitCompOff ? ((approvedSplitCompOff.half_day_period === 'first_half' || approvedSplitCompOff.half_day_session === 'first_half') ? 'compoff' : 'present') : null)
    );
    const secondHalfStatus = attendanceRecord?.second_half_status || (
      approvedSplitLeave ? ((approvedSplitLeave.half_day_period === 'second_half' || approvedSplitLeave.half_day_session === 'second_half') ? 'leave' : 'present') :
      (approvedSplitCompOff ? ((approvedSplitCompOff.half_day_period === 'second_half' || approvedSplitCompOff.half_day_session === 'second_half') ? 'compoff' : 'present') : null)
    );

    const hasApprovedPermission = matchingPermissions.some(p => p.status === 'approved');
    const hasPendingPermission = matchingPermissions.some(p => p.status === 'pending');
    const hasApprovedReg = matchingRegularizations.some(r => r.status === 'approved');
    const hasPendingReg = matchingRegularizations.some(r => r.status === 'pending');

    return {
      iso,
      dateObj,
      isWeekend,
      attendanceRecord,
      matchingLeaves,
      matchingHolidays,
      matchingPermissions,
      matchingRegularizations,
      matchingCompOffs,
      hasSplit,
      isSplitCompoff,
      approvedCompOff,
      approvedSplitCompOff,
      firstHalfStatus,
      secondHalfStatus,
      approvedSplitLeave,
      hasApproved,
      hasPending,
      hasRejected,
      hasCancelled,
      hasHoliday,
      hasApprovedPermission,
      hasPendingPermission,
      hasApprovedReg,
      hasPendingReg,
      isMultiOverlap: (matchingLeaves.length + matchingHolidays.length + matchingPermissions.length + matchingCompOffs.length + (attendanceRecord ? 1 : 0)) > 1,
    };
  };

  return (
    <MainLayout>
      <div data-testid="ess-leave-attendance-page" className="space-y-6 max-w-6xl pb-16">
        
        {/* Sticky Header */}
        <div className="sticky top-0 bg-slate-50/90 backdrop-blur-md z-20 py-4 border-b border-slate-200 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">Leave & Attendance</h1>
            <p className="text-sm text-slate-500 mt-1">Manage leave balances, request punch regularizations, view calendar and shifts.</p>
          </div>
          
          <div className="flex items-center gap-2">
            <button 
              onClick={fetchAll} 
              className="p-2 border border-slate-200 rounded-lg bg-white hover:bg-slate-50 transition-colors"
              title="Refresh Data"
            >
              <RefreshCw className="w-4 h-4 text-slate-600" />
            </button>
            
            {activeTab === 'leave' && (
              <button 
                data-testid="apply-leave-btn" 
                onClick={() => { setShowLeaveModal(true); setLeaveWizardStep(1); }} 
                className="flex items-center space-x-2 bg-[#0052FF] text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#0040CC] shadow-sm hover:shadow transition-all"
              >
                <Plus className="w-4 h-4" />
                <span>Apply Leave</span>
              </button>
            )}

            {activeTab === 'regularization' && (
              <button 
                data-testid="new-reg-btn" 
                onClick={() => {
                  setSelectedAttDetail(null);
                  const now = new Date();
                  setRegCalendarMonth(now);
                  fetchEligibleDatesForMonth(now);
                  setShowRegModal(true);
                }} 
                className="flex items-center space-x-2 bg-[#0052FF] text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#0040CC] shadow-sm hover:shadow transition-all"
              >
                <Plus className="w-4 h-4" />
                <span>New Request</span>
              </button>
            )}

            {activeTab === 'shift' && (
              <button 
                data-testid="new-shift-btn" 
                onClick={() => setShowShiftModal(true)} 
                className="flex items-center space-x-2 bg-[#0052FF] text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#0040CC] shadow-sm hover:shadow transition-all"
              >
                <Plus className="w-4 h-4" />
                <span>New Shift Request</span>
              </button>
            )}

            {activeTab === 'compoff' && (
              <button 
                data-testid="new-compoff-btn" 
                onClick={() => setShowCompoffModal(true)} 
                className="flex items-center space-x-2 bg-[#0052FF] text-white px-4 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#0040CC] shadow-sm hover:shadow transition-all"
              >
                <Plus className="w-4 h-4" />
                <span>New Comp-Off Request</span>
              </button>
            )}
          </div>
        </div>

        {/* Tab Bar navigation */}
        <div className="flex space-x-1 bg-slate-100 rounded-xl p-1 overflow-x-auto border border-slate-200">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button 
                key={tab.key} 
                data-testid={`tab-${tab.key}`} 
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center space-x-2 py-2.5 px-5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.key 
                    ? 'bg-white text-[#0052FF] shadow-sm font-semibold border-b-2 border-b-[#0052FF]' 
                    : 'text-slate-600 hover:text-slate-800 hover:bg-slate-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {loading ? (
          // Loading skeleton state
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="bg-white border border-slate-200 rounded-xl p-5 animate-pulse space-y-3">
                  <div className="h-4 bg-slate-100 rounded w-1/2"></div>
                  <div className="h-8 bg-slate-100 rounded w-1/3"></div>
                </div>
              ))}
            </div>
            <div className="bg-white border border-slate-200 rounded-xl p-6 animate-pulse space-y-4">
              <div className="h-4 bg-slate-100 rounded w-1/4"></div>
              <div className="h-20 bg-slate-100 rounded w-full"></div>
            </div>
          </div>
        ) : (
          <>
            {/* 1. LEAVE TAB REDESIGN */}
            {activeTab === 'leave' && (
              <div className="space-y-6">
                
                {/* Dashboard Cards Grid */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {/* Total Leave */}
                  <div className="bg-white rounded-xl border border-slate-200 p-4 relative overflow-hidden group hover:border-[#0052FF] transition-all duration-300">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Leave</p>
                      <CalendarIcon className="w-4 h-4 text-[#0052FF]" />
                    </div>
                    <p className="text-2xl font-bold text-slate-900 mt-2 font-mono">{totalLeaveBalance}</p>
                    <div className="mt-2 flex items-center justify-between text-[10px] text-slate-500 font-medium">
                      <span>Used: {totalLeaveAvailed}</span>
                      <span>Total: {totalLeaveEntitled}</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5 mt-2 overflow-hidden">
                      <div className="bg-[#0052FF] h-1.5 rounded-full transition-all" style={{ width: `${Math.min(100, (totalLeaveBalance / Math.max(totalLeaveEntitled, 1)) * 100)}%` }} />
                    </div>
                  </div>

                  {/* Casual Leave */}
                  <div className="bg-white rounded-xl border border-slate-200 p-4 relative overflow-hidden group hover:border-indigo-400 transition-all duration-300">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Casual Leave</p>
                      <User className="w-4 h-4 text-indigo-500" />
                    </div>
                    <p className="text-2xl font-bold text-slate-900 mt-2 font-mono">{clData.balance}</p>
                    <div className="mt-2 flex items-center justify-between text-[10px] text-slate-500 font-medium">
                      <span>Used: {clData.availed}</span>
                      <span>Total: {clData.entitled}</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5 mt-2 overflow-hidden">
                      <div className="bg-indigo-500 h-1.5 rounded-full transition-all" style={{ width: `${Math.min(100, (clData.balance / Math.max(clData.entitled, 1)) * 100)}%` }} />
                    </div>
                  </div>

                  {/* Sick Leave */}
                  <div className="bg-white rounded-xl border border-slate-200 p-4 relative overflow-hidden group hover:border-emerald-400 transition-all duration-300">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Sick Leave</p>
                      <HeartPulse className="w-4 h-4 text-emerald-500" />
                    </div>
                    <p className="text-2xl font-bold text-slate-900 mt-2 font-mono">{slData.balance}</p>
                    <div className="mt-2 flex items-center justify-between text-[10px] text-slate-500 font-medium">
                      <span>Used: {slData.availed}</span>
                      <span>Total: {slData.entitled}</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5 mt-2 overflow-hidden">
                      <div className="bg-emerald-500 h-1.5 rounded-full transition-all" style={{ width: `${Math.min(100, (slData.balance / Math.max(slData.entitled, 1)) * 100)}%` }} />
                    </div>
                  </div>

                  {/* Earned Leave */}
                  <div className="bg-white rounded-xl border border-slate-200 p-4 relative overflow-hidden group hover:border-amber-400 transition-all duration-300">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Earned Leave</p>
                      <TrendingUp className="w-4 h-4 text-amber-500" />
                    </div>
                    <p className="text-2xl font-bold text-slate-900 mt-2 font-mono">{elData.balance}</p>
                    <div className="mt-2 flex items-center justify-between text-[10px] text-slate-500 font-medium">
                      <span>Used: {elData.availed}</span>
                      <span>Total: {elData.entitled}</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5 mt-2 overflow-hidden">
                      <div className="bg-amber-500 h-1.5 rounded-full transition-all" style={{ width: `${Math.min(100, (elData.balance / Math.max(elData.entitled, 1)) * 100)}%` }} />
                    </div>
                  </div>

                  {/* Comp-Off Balance */}
                  <div className="bg-white rounded-xl border border-slate-200 p-4 relative overflow-hidden group hover:border-purple-400 transition-all duration-300">
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Comp-Off Balance</p>
                      <Gift className="w-4 h-4 text-purple-500" />
                    </div>
                    <p className="text-2xl font-bold text-slate-900 mt-2 font-mono">{coData.balance}</p>
                    <div className="mt-2 flex items-center justify-between text-[10px] text-slate-500 font-medium">
                      <span>Used: {coData.availed}</span>
                      <span>Total: {coData.entitled}</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-1.5 mt-2 overflow-hidden">
                      <div className="bg-purple-500 h-1.5 rounded-full transition-all" style={{ width: `${Math.min(100, (coData.balance / Math.max(coData.entitled, 1)) * 100)}%` }} />
                    </div>
                  </div>
                </div>

                {/* Additional Configured Leave Types (e.g. Annual Leave, Paternity, Maternity, Custom) */}
                {otherConfiguredBalances.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {otherConfiguredBalances.map((b, idx) => {
                      const bName = b.leave_type_name || b.leave_type_code || b.leave_type || 'Other Leave';
                      const bBal = Math.round(parseFloat(b.available_balance ?? b.closing_balance ?? b.balance ?? 0) * 100) / 100;
                      const bEnt = Math.round(parseFloat(b.total_entitled ?? b.entitled ?? b.opening_balance ?? 0) * 100) / 100;
                      const bUsed = Math.round(parseFloat(b.availed ?? b.already_availed ?? 0) * 100) / 100;
                      return (
                        <div key={b.id || b._id || idx} className="bg-white rounded-xl border border-slate-200 p-4 relative overflow-hidden group hover:border-teal-400 transition-all duration-300">
                          <div className="flex items-center justify-between">
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{bName}</p>
                            <CalendarIcon className="w-4 h-4 text-teal-500" />
                          </div>
                          <p className="text-2xl font-bold text-slate-900 mt-2 font-mono">{bBal}</p>
                          <div className="mt-2 flex items-center justify-between text-[10px] text-slate-500 font-medium">
                            <span>Used: {bUsed}</span>
                            <span>Total: {bEnt}</span>
                          </div>
                          <div className="w-full bg-slate-100 rounded-full h-1.5 mt-2 overflow-hidden">
                            <div className="bg-teal-500 h-1.5 rounded-full transition-all" style={{ width: `${Math.min(100, (bBal / Math.max(bEnt, 1)) * 100)}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Second Row Counters */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="bg-[#0052FF]/5 border border-[#0052FF]/10 rounded-xl p-4 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-500">Applied</p>
                      <p className="text-xl font-bold text-[#0052FF] font-mono mt-1">{appliedCount}</p>
                    </div>
                    <CalendarIcon className="w-6 h-6 text-[#0052FF]/30" />
                  </div>
                  <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-emerald-600">Approved</p>
                      <p className="text-xl font-bold text-emerald-700 font-mono mt-1">{approvedCount}</p>
                    </div>
                    <CheckCircle2 className="w-6 h-6 text-emerald-300" />
                  </div>
                  <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-amber-600">Pending</p>
                      <p className="text-xl font-bold text-amber-700 font-mono mt-1">{pendingCount}</p>
                    </div>
                    <Clock className="w-6 h-6 text-amber-300" />
                  </div>
                  <div className="bg-red-50 border border-red-100 rounded-xl p-4 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-medium text-red-600">Rejected</p>
                      <p className="text-xl font-bold text-red-700 font-mono mt-1">{rejectedCount}</p>
                    </div>
                    <X className="w-6 h-6 text-red-300" />
                  </div>
                </div>

                {/* Calendar & Filters section */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  
                  {/* Calendar View */}
                  <div className="bg-white border border-slate-200 rounded-xl p-5 lg:col-span-1 shadow-sm">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-4">
                      <h3 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
                        <CalendarIcon className="w-4 h-4 text-[#0052FF]" />
                        Leave Calendar
                      </h3>
                      <div className="flex items-center gap-1.5">
                        <button 
                          onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))}
                          className="p-1 hover:bg-slate-100 rounded"
                        >
                          <ChevronLeft className="w-4 h-4 text-slate-600" />
                        </button>
                        <span className="text-xs font-semibold text-slate-700 font-mono">
                          {calendarMonth.toLocaleString('default', { month: 'short', year: 'numeric' })}
                        </span>
                        <button 
                          onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))}
                          className="p-1 hover:bg-slate-100 rounded"
                        >
                          <ChevronRight className="w-4 h-4 text-slate-600" />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-7 gap-1 text-center mb-3">
                      {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, idx) => (
                        <span key={idx} className="text-[10px] font-bold text-slate-400 uppercase py-1">{day}</span>
                      ))}
                      {getCalendarDays().map((dayDate, idx) => {
                        if (!dayDate) return <div key={`empty-${idx}`} />;
                        const info = getCalendarDayInfo(dayDate);
                        const isToday = new Date().toDateString() === dayDate.toDateString();

                        let colorClass = 'text-slate-700 hover:bg-slate-100 border-slate-200';
                        if (info.hasSplit) {
                          colorClass = info.isSplitCompoff 
                            ? 'bg-gradient-to-r from-emerald-100 to-teal-100 border-teal-400 text-slate-900 font-bold'
                            : 'bg-gradient-to-r from-emerald-100 to-blue-100 border-blue-400 text-slate-900 font-bold';
                        } else if (info.hasHoliday && info.hasApproved) {
                          colorClass = 'bg-purple-100 border-purple-300 text-purple-900 font-bold';
                        } else if (info.hasHoliday) {
                          colorClass = 'bg-indigo-50 border-indigo-300 text-indigo-800 font-bold';
                        } else if (info.attendanceRecord?.status === 'compoff' || info.approvedCompOff) {
                          colorClass = 'bg-teal-50 border-teal-300 text-teal-800 font-bold';
                        } else if (info.hasApproved || info.attendanceRecord?.status === 'leave') {
                          colorClass = 'bg-blue-50 border-blue-300 text-blue-800 font-bold';
                        } else if (info.attendanceRecord?.status === 'present') {
                          colorClass = 'bg-emerald-50 border-emerald-300 text-emerald-800 font-bold';
                        } else if (info.attendanceRecord?.status === 'absent') {
                          colorClass = 'bg-red-50 border-red-300 text-red-800 font-bold';
                        } else if (info.hasPending) {
                          colorClass = 'bg-amber-50 border-amber-300 text-amber-800 font-bold';
                        } else if (info.hasRejected) {
                          colorClass = 'bg-red-50 border-red-300 text-red-800 font-bold';
                        } else if (info.hasCancelled) {
                          colorClass = 'bg-slate-100 border-slate-300 text-slate-500 font-medium';
                        } else if (info.isWeekend) {
                          colorClass = 'bg-slate-50 text-slate-400 border-transparent';
                        }

                        return (
                          <button
                            key={idx}
                            data-testid={`calendar-day-${info.iso}`}
                            onClick={() => setSelectedDateDetailModal(info)}
                            className={`aspect-square w-full rounded-lg border text-xs flex flex-col items-center justify-between p-1 transition-all relative ${colorClass} ${
                              isToday ? 'ring-2 ring-[#0052FF] ring-offset-1 font-extrabold' : ''
                            }`}
                          >
                            <span className="text-[11px] self-start leading-none">{dayDate.getDate()}</span>
                            
                            <div className="flex items-center gap-0.5 self-end flex-wrap justify-end">
                              {info.hasSplit && (
                                <span className="flex items-center gap-0.5" title={info.isSplitCompoff ? "Half-Day Present + Half-Day Approved Comp-Off" : "Half-Day Present + Half-Day Approved Leave"}>
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
                                  <span className={`w-1.5 h-1.5 rounded-full ${info.isSplitCompoff ? 'bg-teal-600' : 'bg-blue-600'}`} />
                                </span>
                              )}
                              {!info.hasSplit && (info.attendanceRecord?.status === 'compoff' || info.approvedCompOff) && (
                                <span className="w-1.5 h-1.5 rounded-full bg-teal-600" title="Approved Comp-Off" />
                              )}
                              {!info.hasSplit && !info.approvedCompOff && info.attendanceRecord?.status !== 'compoff' && info.hasHoliday && (
                                <span className="w-1.5 h-1.5 rounded-full bg-indigo-600" title="Company Holiday" />
                              )}
                              {!info.hasSplit && (info.hasApproved || info.attendanceRecord?.status === 'leave') && (
                                <span className="w-1.5 h-1.5 rounded-full bg-blue-600" title="Approved Leave" />
                              )}
                              {info.hasPending && (
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-500" title="Pending Leave" />
                              )}
                              {info.hasRejected && (
                                <span className="w-1.5 h-1.5 rounded-full bg-red-600" title="Rejected Leave" />
                              )}
                              {info.hasApprovedPermission && (
                                <span className="w-1.5 h-1.5 rounded-full bg-teal-500" title="Approved Permission" />
                              )}
                              {info.hasApprovedReg && (
                                <span className="w-1.5 h-1.5 rounded-full bg-violet-600" title="Approved Regularization" />
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>

                    {/* Legend */}
                    <div className="flex flex-wrap gap-2 text-[10px] text-slate-600 border-t border-slate-100 pt-3 font-medium">
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" />Leave</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-teal-500" />Comp-Off</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gradient-to-r from-emerald-500 to-blue-500" />Split (Present + Leave)</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500" />Split (Present + Comp-Off)</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" />Pending</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" />Rejected</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-teal-500" />Permission</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-violet-600" />Regularization</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-indigo-600" />Holiday</span>
                      <span className="flex items-center gap-1"><span className="w-2 h-2 rounded bg-slate-200" />Weekend</span>
                    </div>
                  </div>

                  {/* Leave History List/Table */}
                  <div className="lg:col-span-2 space-y-4">
                    {/* Toolbar */}
                    <div className="flex flex-wrap items-center justify-between gap-3 bg-white border border-slate-200 p-3 rounded-xl shadow-sm">
                      <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                        <Search className="w-4 h-4 text-slate-400 shrink-0" />
                        <input
                          type="text"
                          placeholder="Search applications..."
                          value={searchTerm}
                          onChange={e => setSearchTerm(e.target.value)}
                          className="w-full text-xs focus:outline-none bg-transparent"
                        />
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <select
                          value={statusFilter}
                          onChange={e => setStatusFilter(e.target.value)}
                          className="text-xs border border-slate-200 rounded px-2 py-1.5 bg-slate-50"
                        >
                          <option value="all">All Status</option>
                          <option value="approved">Approved</option>
                          <option value="pending">Pending</option>
                          <option value="rework">Sent Back (Rework)</option>
                          <option value="rejected">Rejected</option>
                          <option value="cancelled">Cancelled</option>
                        </select>

                        <button 
                          onClick={() => handleExportExcel('Leave')}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 border border-slate-200 rounded text-xs hover:bg-slate-50 bg-white"
                        >
                          <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" />
                          <span className="hidden sm:inline">Excel</span>
                        </button>
                      </div>
                    </div>

                    {/* Table View */}
                    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs text-left">
                          <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[10px] border-b border-slate-100">
                            <tr>
                              <th className="px-4 py-3 font-semibold">App No</th>
                              <th className="px-4 py-3 font-semibold">Leave Type</th>
                              <th className="px-4 py-3 font-semibold">Dates</th>
                              <th className="px-4 py-3 font-semibold text-center">Days</th>
                              <th className="px-4 py-3 font-semibold">Status</th>
                              <th className="px-4 py-3 font-semibold text-center">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {leaveApps
                              .filter(a => {
                                if (statusFilter !== 'all' && a.status !== statusFilter) return false;
                                if (searchTerm && !`${a.leave_type_name} ${a.reason}`.toLowerCase().includes(searchTerm.toLowerCase())) return false;
                                return true;
                              })
                              .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                              .map((app, idx) => (
                                <tr key={app.id || idx} className="hover:bg-slate-50/50 transition-colors">
                                  <td className="px-4 py-3 font-mono font-medium text-slate-600">
                                    L-{String(idx + 1).padStart(4, '0')}
                                  </td>
                                  <td className="px-4 py-3 font-medium text-slate-800">
                                    {app.leave_type_name || app.leave_type || 'Leave'}
                                  </td>
                                  <td className="px-4 py-3 text-slate-500">
                                    <span className="font-mono">{app.from_date}</span> to <span className="font-mono">{app.to_date}</span>
                                  </td>
                                  <td className="px-4 py-3 text-center font-semibold text-slate-700">
                                    {app.is_permission || app.leave_type_code === 'ONE_HOUR_PERMISSION' || app.leave_type_id === 'ONE_HOUR_PERMISSION' ? (
                                      <span className="text-indigo-800 font-medium bg-indigo-50 px-2 py-0.5 rounded border border-indigo-200 inline-block text-xs">
                                        {app.duration_display || formatPermissionDuration(app.duration_minutes || (app.duration_hours ? app.duration_hours * 60 : 60))}
                                      </span>
                                    ) : app.is_partial_day || app.half_day ? (
                                      <span className="text-amber-800 font-medium bg-amber-50 px-2 py-0.5 rounded border border-amber-200 inline-block text-xs">
                                        0.5 Day {app.half_day_period ? `(${app.half_day_period === 'first_half' ? 'First Half' : 'Second Half'})` : '(Half Day)'}
                                      </span>
                                    ) : (
                                      `${app.total_days !== undefined && app.total_days !== null ? app.total_days : (app.days !== undefined && app.days !== null ? app.days : 1)} Day(s)`
                                    )}
                                  </td>
                                  <td className="px-4 py-3">
                                    <span className={`px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase ${statusColor(app.status)}`}>
                                      {app.status}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex items-center justify-center gap-1.5 flex-wrap">
                                      <button 
                                        onClick={() => { setPreviewItem(app); setPreviewType('leave'); }}
                                        className="p-1 hover:bg-slate-100 rounded text-slate-600 transition-colors"
                                        title="Preview Approval Timeline"
                                      >
                                        <Eye className="w-3.5 h-3.5" />
                                      </button>
                                      <button 
                                        onClick={() => handleExportPDF('Leave')}
                                        className="p-1 hover:bg-slate-100 rounded text-slate-600 transition-colors"
                                        title="Download PDF"
                                      >
                                        <FileText className="w-3.5 h-3.5" />
                                      </button>
                                      {['rework', 'sent_back', 'draft'].includes(app.status) && (
                                        <button 
                                          onClick={() => handleOpenRework(app, 'leave')}
                                          className="p-1 px-1.5 hover:bg-orange-100 bg-orange-50 text-orange-700 border border-orange-200 rounded transition-colors flex items-center gap-1 text-[11px] font-semibold"
                                          title="Rework / Edit and Resubmit"
                                        >
                                          <FileEdit className="w-3.5 h-3.5 text-orange-600" />
                                          <span className="hidden sm:inline">Rework</span>
                                        </button>
                                      )}
                                      {app.status === 'rejected' && (
                                        <button 
                                          onClick={() => handleOpenReapply(app, 'leave')}
                                          className="p-1 px-1.5 hover:bg-blue-100 bg-blue-50 text-blue-700 border border-blue-200 rounded transition-colors flex items-center gap-1 text-[11px] font-semibold"
                                          title="Reapply for this Leave"
                                        >
                                          <RefreshCw className="w-3.5 h-3.5 text-blue-600" />
                                          <span className="hidden sm:inline">Reapply</span>
                                        </button>
                                      )}
                                      {['pending', 'pending_manager', 'pending_hr', 'rework', 'sent_back'].includes(app.status) && (
                                        <button 
                                          onClick={() => handleLeaveCancel(app)}
                                          className="p-1 hover:bg-red-50 rounded text-red-600 transition-colors"
                                          title="Cancel Application"
                                        >
                                          <X className="w-3.5 h-3.5" />
                                        </button>
                                      )}
                                      {['draft', 'cancelled', 'rejected', 'rework', 'sent_back'].includes(app.status) && (
                                        <button 
                                          onClick={() => handleLeaveDelete(app)}
                                          className="p-1 hover:bg-rose-50 rounded text-rose-600 transition-colors"
                                          title="Delete Application"
                                        >
                                          <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            {leaveApps.length === 0 && (
                              <tr>
                                <td colSpan={6} className="text-center text-slate-400 py-10 font-medium">
                                  No leave applications found.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      {leaveApps.length > 0 && (
                        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50/50 text-[11px] text-slate-500">
                          <div>
                            Showing {Math.min(leaveApps.length, (currentPage - 1) * pageSize + 1)}-{Math.min(leaveApps.length, currentPage * pageSize)} of {leaveApps.length} applications
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              disabled={currentPage === 1}
                              onClick={() => setCurrentPage(prev => prev - 1)}
                              className="px-2 py-1 border border-slate-200 rounded disabled:opacity-50 hover:bg-slate-50 font-medium transition-colors"
                            >
                              Prev
                            </button>
                            <button
                              disabled={currentPage * pageSize >= leaveApps.length}
                              onClick={() => setCurrentPage(prev => prev + 1)}
                              className="px-2 py-1 border border-slate-200 rounded disabled:opacity-50 hover:bg-slate-50 font-medium transition-colors"
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

              </div>
            )}

            {/* PERMISSION TAB */}
            {activeTab === 'permission' && (
              <div className="space-y-6" data-testid="permission-tab-content">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Monthly Limit</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1 font-mono">
                      {permSummary?.monthly_limit !== undefined && permSummary?.monthly_limit !== null ? permSummary.monthly_limit : '--'}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-1">
                      {permSummary?.policy_name ? `Policy: ${permSummary.policy_name}` : 'Configured via Policy Master'}
                    </p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Permissions Used</p>
                    <p className="text-2xl font-bold text-amber-600 mt-1 font-mono">
                      {permSummary?.used_count ?? 0}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-1">
                      {permSummary?.period_start_date && permSummary?.period_end_date 
                        ? `${permSummary.period_start_date} to ${permSummary.period_end_date}` 
                        : 'Applied this month'}
                    </p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Remaining Balance</p>
                    <p className="text-2xl font-bold text-emerald-600 mt-1 font-mono">
                      {permSummary?.remaining_count !== undefined && permSummary?.remaining_count !== null ? permSummary.remaining_count : '--'}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-1">Available for application</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Max Duration</p>
                    <p className="text-2xl font-bold text-indigo-600 mt-1 font-mono">
                      {permSummary?.max_duration_label || (permSummary?.max_duration_minutes ? (permSummary.max_duration_minutes >= 60 && permSummary.max_duration_minutes % 60 === 0 ? `${permSummary.max_duration_minutes / 60} Hour${permSummary.max_duration_minutes > 60 ? 's' : ''}` : `${permSummary.max_duration_minutes} Mins`) : '--')}
                    </p>
                    <p className="text-[10px] text-slate-400 mt-1">Per permission request</p>
                  </div>
                </div>

                {/* Toolbar & Filter */}
                <div className="flex flex-wrap items-center justify-between gap-3 bg-white border border-slate-200 p-3 rounded-xl shadow-sm">
                  <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                    <Search className="w-4 h-4 text-slate-400 shrink-0" />
                    <input
                      type="text"
                      placeholder="Search permissions by reason or date..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      className="w-full text-xs focus:outline-none bg-transparent"
                    />
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <select
                      value={statusFilter}
                      onChange={e => setStatusFilter(e.target.value)}
                      className="text-xs border border-slate-200 rounded px-2.5 py-1.5 bg-slate-50 font-medium"
                    >
                      <option value="all">All Status</option>
                      <option value="approved">Approved</option>
                      <option value="pending">Pending</option>
                      <option value="rework">Sent Back (Rework)</option>
                      <option value="rejected">Rejected</option>
                      <option value="cancelled">Cancelled</option>
                    </select>

                    <button 
                      onClick={() => {
                        window.open(`${API_URL}/leave/permissions/export`, '_blank');
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded text-xs hover:bg-slate-50 bg-white font-medium"
                    >
                      <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" />
                      <span>Export Register</span>
                    </button>

                    <button 
                      onClick={() => {
                        setLeaveForm(prev => ({ ...prev, leave_type_id: 'ONE_HOUR_PERMISSION' }));
                        setShowLeaveModal(true);
                        setLeaveWizardStep(1);
                      }}
                      className="flex items-center gap-1.5 bg-[#0052FF] text-white px-3 py-1.5 rounded text-xs font-semibold hover:bg-[#0040CC]"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>Apply Permission</span>
                    </button>
                  </div>
                </div>

                {/* Table View */}
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs text-left">
                      <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[10px] border-b border-slate-100">
                        <tr>
                          <th className="px-4 py-3 font-semibold">Ref No</th>
                          <th className="px-4 py-3 font-semibold">Permission Date</th>
                          <th className="px-4 py-3 font-semibold">Time Range</th>
                          <th className="px-4 py-3 font-semibold text-center">Duration</th>
                          <th className="px-4 py-3 font-semibold">Reason / Purpose</th>
                          <th className="px-4 py-3 font-semibold">Status</th>
                          <th className="px-4 py-3 font-semibold text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {permissions
                          .filter(p => {
                            if (statusFilter !== 'all' && p.status !== statusFilter) return false;
                            if (searchTerm && !`${p.reason} ${p.permission_date}`.toLowerCase().includes(searchTerm.toLowerCase())) return false;
                            return true;
                          })
                          .map((perm, idx) => (
                            <tr key={perm.id || idx} className="hover:bg-slate-50/50 transition-colors">
                              <td className="px-4 py-3 font-mono font-medium text-slate-600">
                                PRM-{String(perm.id || idx).slice(-6).toUpperCase()}
                              </td>
                              <td className="px-4 py-3 font-semibold text-slate-900">
                                {perm.permission_date}
                              </td>
                              <td className="px-4 py-3 font-mono text-slate-700">
                                {perm.start_time} - {perm.end_time}
                              </td>
                              <td className="px-4 py-3 text-center font-bold text-slate-800">
                                {perm.duration_minutes || 60} Mins
                              </td>
                              <td className="px-4 py-3 text-slate-600 max-w-xs truncate" title={perm.reason}>
                                {perm.reason}
                              </td>
                              <td className="px-4 py-3">
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase border ${statusColor(perm.status)}`}>
                                  {perm.status}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <div className="flex items-center justify-center gap-1.5 flex-wrap">
                                  <button 
                                    onClick={() => { setPreviewItem(perm); setPreviewType('permission'); }}
                                    className="p-1 hover:bg-slate-100 rounded text-slate-600 transition-colors"
                                    title="Preview Approval Timeline"
                                  >
                                    <Eye className="w-3.5 h-3.5" />
                                  </button>
                                  {['rework', 'sent_back', 'draft'].includes(perm.status) && (
                                    <button 
                                      onClick={() => handleOpenRework(perm, 'permission')}
                                      className="p-1 px-1.5 hover:bg-orange-100 bg-orange-50 text-orange-700 border border-orange-200 rounded transition-colors flex items-center gap-1 text-[11px] font-semibold"
                                      title="Rework / Edit and Resubmit"
                                    >
                                      <FileEdit className="w-3.5 h-3.5 text-orange-600" />
                                      <span className="hidden sm:inline">Rework</span>
                                    </button>
                                  )}
                                  {perm.status === 'rejected' && (
                                    <button 
                                      onClick={() => handleOpenReapply(perm, 'permission')}
                                      className="p-1 px-1.5 hover:bg-blue-100 bg-blue-50 text-blue-700 border border-blue-200 rounded transition-colors flex items-center gap-1 text-[11px] font-semibold"
                                      title="Reapply Permission"
                                    >
                                      <RefreshCw className="w-3.5 h-3.5 text-blue-600" />
                                      <span className="hidden sm:inline">Reapply</span>
                                    </button>
                                  )}
                                  {['pending', 'pending_manager', 'pending_hr', 'rework', 'sent_back'].includes(perm.status) && (
                                    <button 
                                      onClick={() => handlePermissionCancel(perm.id || perm._id)}
                                      className="p-1 hover:bg-red-50 rounded text-red-600 transition-colors"
                                      title="Cancel Permission"
                                    >
                                      <X className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                  {['draft', 'cancelled', 'rejected', 'rework', 'sent_back'].includes(perm.status) && (
                                    <button 
                                      onClick={() => handlePermissionDelete(perm)}
                                      className="p-1 hover:bg-rose-50 rounded text-rose-600 transition-colors"
                                      title="Delete Permission"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        {permissions.length === 0 && (
                          <tr>
                            <td colSpan={7} className="text-center text-slate-400 py-12 font-medium">
                              No permission requests found. Click "Apply Permission" to submit a permission.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

              </div>
            )}

            {/* 2. ATTENDANCE TAB - KEEP EXACTLY AS ORIGINAL */}
            {activeTab === 'attendance' && (
              <div className="space-y-4">
                <div className="flex flex-wrap items-center gap-3">
                  <input type="month" data-testid="att-month" value={month} onChange={(e) => setMonth(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
                    <span className="text-emerald-600 font-medium">Present: {attendance.summary?.present || 0}</span>
                    <span className="text-red-600 font-medium">Absent: {attendance.summary?.absent || 0}</span>
                    <span className="text-blue-600 font-medium">Leave: {attendance.summary?.leave || 0}</span>
                    <span className="text-amber-600 font-medium">Half-day: {attendance.summary?.half_day || 0}</span>
                    {Number(attendance.summary?.compoff || 0) > 0 && (
                      <span className="text-teal-600 font-medium">Comp-Off: {attendance.summary?.compoff || 0}</span>
                    )}
                    <span className="text-slate-500 font-medium">Weekly-off: {attendance.summary?.weekly_off || 0}</span>
                    <span className="text-purple-600 font-medium">Holiday: {attendance.summary?.holiday || 0}</span>
                    <span className="text-slate-700 font-semibold">Total: {attendance.summary?.total || 0}</span>
                  </div>
                </div>
                {/* Legend */}
                <div className="flex flex-wrap gap-3 text-xs text-slate-600" data-testid="att-legend">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-emerald-400" />Present</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-gradient-to-r from-emerald-400 to-blue-400" />Half-Day Present + Leave</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-gradient-to-r from-emerald-400 to-teal-400" />Half-Day Present + Comp-Off</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-400" />Absent</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-blue-400" />Leave</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-teal-400" />Comp-Off</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-amber-400" />Half-day</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-slate-300" />Weekly Off</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-purple-400" />Holiday</span>
                </div>
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="att-grid">
                  <div className="grid grid-cols-7 gap-1 p-3">
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((d) => <div key={d} className="text-center text-xs font-semibold text-slate-500 py-1 border-b border-slate-100">{d}</div>)}
                    {(() => {
                      if (!month) return null;
                      const [y, m] = month.split('-').map(Number);
                      if (!y || !m) return null;
                      const firstDay = new Date(y, m - 1, 1);
                      const daysInMonth = new Date(y, m, 0).getDate();
                      const startCol = (firstDay.getDay() + 6) % 7;
                      const byDate = {};
                      (attendance.records || []).forEach(r => { if (r.attendance_date) byDate[r.attendance_date] = r; });
                      const cells = [];
                      for (let i = 0; i < startCol; i++) {
                        cells.push(<div key={`pad-${i}`} />);
                      }
                      for (let d = 1; d <= daysInMonth; d++) {
                        const iso = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
                        const r = byDate[iso];
                        const status = (r?.status || r?.attendance_status || '').toLowerCase();
                        const color = attColor(status);
                        const label = status ? status.replace(/_/g, ' ') : 'no data';
                        const inTime = r?.in_time || r?.actual_in_time || r?.check_in || r?.first_in || null;
                        const outTime = r?.out_time || r?.actual_out_time || r?.check_out || r?.last_out || null;
                        
                        const titlePunch = inTime
                          ? ` | In: ${inTime}${outTime ? ` | Out: ${outTime}` : ' | Out: Pending'}`
                          : (outTime ? ` | Out: ${outTime}` : '');

                        cells.push(
                          <div 
                            key={iso} 
                            className="text-center py-1.5 min-h-[76px] flex flex-col items-center justify-start cursor-pointer hover:bg-slate-50 transition-colors rounded-lg" 
                            data-testid={`att-cell-${iso}`} 
                            title={`${iso} — ${label}${titlePunch} (Click to view full details)`}
                            onClick={() => setSelectedDateDetailModal(getCalendarDayInfo(new Date(y, m - 1, d)))}
                          >
                            <div className={`w-9 h-9 mx-auto rounded-full flex items-center justify-center text-xs font-semibold ${r ? 'text-white' : 'text-slate-400 bg-slate-50 border border-slate-200'} ${r ? color : ''}`}>
                              {String(d).padStart(2, '0')}
                            </div>
                            {r && (inTime || outTime || ['present', 'half_day', 'half_day_present_leave', 'half_day_present_compoff', 'late', 'on_duty', 'work_from_home'].includes(status)) && (
                              <div className="mt-1 text-[9px] leading-tight font-mono flex flex-col items-center gap-0.5">
                                <div className="text-slate-600 truncate max-w-full" title={`In: ${inTime || 'N/A'}`}>
                                  <span className="text-[8px] text-slate-400 font-sans mr-0.5">In:</span>
                                  {inTime || '--:--'}
                                </div>
                                <div className={outTime ? "text-slate-600 truncate max-w-full" : "text-amber-600 font-semibold text-[8px] truncate max-w-full"} title={`Out: ${outTime || 'Pending'}`}>
                                  <span className="text-[8px] text-slate-400 font-sans mr-0.5">Out:</span>
                                  {outTime ? outTime : (inTime ? 'Pending' : '--:--')}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      }
                      return cells;
                    })()}
                  </div>
                  {attendance.records.length === 0 && <p className="text-sm text-slate-400 py-6 text-center" data-testid="att-empty">No attendance records for this month</p>}
                </div>
              </div>
            )}

            {/* 3. REGULARIZATION TAB REDESIGN */}
            {activeTab === 'regularization' && (
              <div className="space-y-6" data-testid="regularization-tab">
                
                {/* Regularization Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Pending Requests</p>
                    <p className="text-2xl font-bold text-amber-600 mt-1 font-mono">{pendingRegCount}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Approved Requests</p>
                    <p className="text-2xl font-bold text-emerald-600 mt-1 font-mono">{approvedRegCount}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Rejected Requests</p>
                    <p className="text-2xl font-bold text-red-600 mt-1 font-mono">{rejectedRegCount}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Current Month Total</p>
                    <p className="text-2xl font-bold text-[#0052FF] mt-1 font-mono">{currentMonthRegCount}</p>
                  </div>
                </div>

                {/* Eligible Dates Action Required Banner */}
                {actionRequiredDates.length > 0 ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="reg-eligible-banner">
                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div className="text-xs text-amber-900 flex-1">
                      <p className="font-semibold text-sm">Action Required: {actionRequiredDates.length} regularization-eligible day(s)</p>
                      <p className="mt-1">
                        Attendance requires correction for these dates within the configured {regPolicyInfo?.regularization_window_days ? `${regPolicyInfo.regularization_window_days}-day` : 'active'} regularization window. Click any date to file a regularization request.
                      </p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {actionRequiredDates.map((d, i) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => {
                              const [yr, mo] = (d.attendance_date || '').split('-').map(Number);
                              if (yr && mo) setRegCalendarMonth(new Date(yr, mo - 1, 1));
                              handleSelectRegDate(d.attendance_date, d);
                              setShowRegModal(true);
                            }}
                            className="px-2.5 py-1 bg-white hover:bg-amber-100 border border-amber-200 rounded font-mono font-medium text-amber-900 transition-colors cursor-pointer flex items-center gap-1.5 shadow-sm text-xs"
                            title="Click to regularize this date"
                          >
                            <span>{d.attendance_date} — {d.current_status}</span>
                            <ChevronRight className="w-3 h-3 text-amber-600" />
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3" data-testid="reg-no-action-required">
                    <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                    <div className="text-xs text-emerald-900">
                      <p className="font-semibold text-sm">No attendance regularization is required at this time.</p>
                      <p className="text-emerald-700 mt-0.5">All attendance records are complete or already resolved within the active regularization window.</p>
                    </div>
                  </div>
                )}

                {/* Search & List */}
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-3 bg-white border border-slate-200 p-3 rounded-xl shadow-sm">
                    <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                      <Search className="w-4 h-4 text-slate-400 shrink-0" />
                      <input
                        type="text"
                        placeholder="Search corrections..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        className="w-full text-xs focus:outline-none bg-transparent"
                      />
                    </div>
                    <select
                      value={statusFilter}
                      onChange={e => setStatusFilter(e.target.value)}
                      className="border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs bg-white text-slate-600 focus:outline-none"
                    >
                      <option value="all">All Statuses</option>
                      <option value="pending">Pending</option>
                      <option value="rework">Sent Back (Rework)</option>
                      <option value="approved">Approved</option>
                      <option value="rejected">Rejected</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                    <button 
                      onClick={() => handleExportExcel('Regularization')}
                      className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded text-xs hover:bg-slate-50 bg-white"
                    >
                      <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" />
                      <span>Export</span>
                    </button>
                  </div>

                  <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs text-left">
                        <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[10px] border-b border-slate-100">
                          <tr>
                            <th className="px-4 py-3 font-semibold">Request No</th>
                            <th className="px-4 py-3 font-semibold">Date</th>
                            <th className="px-4 py-3 font-semibold">Old Punch Time</th>
                            <th className="px-4 py-3 font-semibold">New Proposed Time</th>
                            <th className="px-4 py-3 font-semibold">Reason</th>
                            <th className="px-4 py-3 font-semibold">Status</th>
                            <th className="px-4 py-3 font-semibold text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {regularizations
                            .filter(r => {
                              if (statusFilter !== 'all') {
                                if (statusFilter === 'rework' && !['rework', 'sent_back'].includes(r.status)) return false;
                                else if (statusFilter !== 'rework' && r.status !== statusFilter) return false;
                              }
                              if (searchTerm && !`${r.attendance_date} ${r.reason}`.toLowerCase().includes(searchTerm.toLowerCase())) return false;
                              return true;
                            })
                            .map((r, i) => (
                              <tr key={r.id || r._id || i} className="hover:bg-slate-50/50 transition-colors">
                                <td className="px-4 py-3 font-mono font-medium text-slate-600">R-{String(i + 1).padStart(4, '0')}</td>
                                <td className="px-4 py-3 font-medium text-slate-800 font-mono">{r.attendance_date}</td>
                                <td className="px-4 py-3 text-slate-500 font-mono">
                                  {r.old_in_time || '--:--'} / {r.old_out_time || '--:--'}
                                </td>
                                <td className="px-4 py-3 text-slate-800 font-mono font-semibold">
                                  {r.proposed_in_time || '--:--'} / {r.proposed_out_time || '--:--'}
                                </td>
                                <td className="px-4 py-3 text-slate-600 max-w-[150px] truncate" title={r.reason}>{r.reason}</td>
                                <td className="px-4 py-3">
                                  <span className={`px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase ${statusColor(r.status)}`}>
                                    {r.status}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-center">
                                  <div className="flex justify-center items-center gap-1.5">
                                    <button 
                                      onClick={() => { setPreviewItem(r); setPreviewType('reg'); }}
                                      className="p-1 hover:bg-slate-100 rounded text-slate-600"
                                      title="Preview Workflow"
                                    >
                                      <Eye className="w-3.5 h-3.5" />
                                    </button>
                                    {['rework', 'sent_back'].includes(r.status) && (
                                      <button
                                        onClick={() => handleOpenRework(r, 'reg')}
                                        className="p-1 hover:bg-orange-100 rounded text-orange-600 font-semibold"
                                        title="Rework / Edit & Resubmit"
                                      >
                                        <RotateCcw className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                    {r.status === 'rejected' && (
                                      <button
                                        onClick={() => handleOpenReapply(r, 'reg')}
                                        className="p-1 hover:bg-amber-100 rounded text-amber-600 font-semibold"
                                        title="Reapply Regularization"
                                      >
                                        <RotateCcw className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                    {['pending', 'pending_hr', 'pending_manager', 'rework', 'sent_back'].includes(r.status) && (
                                      <button 
                                        onClick={() => handleRegCancel(r.id || r._id)}
                                        className="p-1 hover:bg-red-50 rounded text-red-600"
                                        title="Cancel Request"
                                      >
                                        <X className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                    {r.status !== 'approved' && (
                                      <button
                                        onClick={() => handleRegDelete(r.id || r._id)}
                                        className="p-1 hover:bg-red-50 rounded text-red-500"
                                        title="Delete Request"
                                      >
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          {regularizations.length === 0 && (
                            <tr>
                              <td colSpan={7} className="text-center text-slate-400 py-10 font-medium">
                                No regularization requests filed yet.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

              </div>
            )}

            {/* 4. SHIFT REQUESTS TAB REDESIGN */}
            {activeTab === 'shift' && (
              <div className="space-y-6">
                
                {/* Shift Stats Card */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Pending Swaps</p>
                      <p className="text-xl font-bold text-amber-600 mt-1 font-mono">{pendingShiftCount}</p>
                    </div>
                    <ArrowLeftRight className="w-6 h-6 text-amber-200" />
                  </div>
                  <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Approved Exchanges</p>
                      <p className="text-xl font-bold text-emerald-600 mt-1 font-mono">{approvedShiftCount}</p>
                    </div>
                    <CheckCircle2 className="w-6 h-6 text-emerald-200" />
                  </div>
                  <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Rejected Requests</p>
                      <p className="text-xl font-bold text-red-600 mt-1 font-mono">{rejectedShiftCount}</p>
                    </div>
                    <X className="w-6 h-6 text-red-200" />
                  </div>
                </div>

                {/* Shift History Grid */}
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                  <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-3">
                    <h3 className="font-semibold text-slate-800 text-sm">Shift Swap & Exchange Ledger</h3>
                    <div className="flex items-center gap-2">
                      <select
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value)}
                        className="border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs bg-white text-slate-600 focus:outline-none"
                      >
                        <option value="all">All Statuses</option>
                        <option value="pending">Pending</option>
                        <option value="rework">Sent Back (Rework)</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                      <button 
                        onClick={() => handleExportExcel('Shift')}
                        className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded text-xs hover:bg-slate-50 bg-white"
                      >
                        <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" />
                        <span>Export Ledger</span>
                      </button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs text-left">
                      <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[10px] border-b border-slate-100">
                        <tr>
                          <th className="px-4 py-3 font-semibold">Request No</th>
                          <th className="px-4 py-3 font-semibold">Type</th>
                          <th className="px-4 py-3 font-semibold">Current Shift</th>
                          <th className="px-4 py-3 font-semibold">Requested Shift</th>
                          <th className="px-4 py-3 font-semibold">Effective Date</th>
                          <th className="px-4 py-3 font-semibold">Reason</th>
                          <th className="px-4 py-3 font-semibold">Status</th>
                          <th className="px-4 py-3 font-semibold text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {shiftRequests
                          .filter(s => {
                            if (statusFilter !== 'all') {
                              if (statusFilter === 'rework' && !['rework', 'sent_back'].includes(s.status)) return false;
                              else if (statusFilter !== 'rework' && s.status !== statusFilter) return false;
                            }
                            return true;
                          })
                          .map((s, i) => (
                          <tr key={s.id || s._id || i} className="hover:bg-slate-50/50 transition-colors">
                            <td className="px-4 py-3 font-mono font-medium text-slate-600">S-{String(i + 1).padStart(4, '0')}</td>
                            <td className="px-4 py-3 font-medium capitalize text-slate-800">{s.request_type || 'Change'}</td>
                            <td className="px-4 py-3 text-slate-500 font-mono">{s.current_shift_id || '9 AM - 6 PM'}</td>
                            <td className="px-4 py-3 text-slate-800 font-semibold font-mono">{s.requested_shift_id || '6 AM - 2 PM'}</td>
                            <td className="px-4 py-3 text-slate-600 font-mono">{s.requested_date}</td>
                            <td className="px-4 py-3 text-slate-500 max-w-[200px] truncate" title={s.reason}>{s.reason || 'Personal request'}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase ${statusColor(s.status)}`}>
                                {s.status}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex justify-center items-center gap-1.5">
                                <button 
                                  onClick={() => { setPreviewItem(s); setPreviewType('shift'); }}
                                  className="p-1 hover:bg-slate-100 rounded text-slate-600"
                                  title="Preview Workflow"
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                </button>
                                {['rework', 'sent_back'].includes(s.status) && (
                                  <button
                                    onClick={() => handleOpenRework(s, 'shift')}
                                    className="p-1 hover:bg-orange-100 rounded text-orange-600 font-semibold"
                                    title="Rework / Edit & Resubmit"
                                  >
                                    <RotateCcw className="w-3.5 h-3.5" />
                                  </button>
                                )}
                                {s.status === 'rejected' && (
                                  <button
                                    onClick={() => handleOpenReapply(s, 'shift')}
                                    className="p-1 hover:bg-amber-100 rounded text-amber-600 font-semibold"
                                    title="Reapply Request"
                                  >
                                    <RotateCcw className="w-3.5 h-3.5" />
                                  </button>
                                )}
                                {['pending', 'pending_manager', 'pending_hr', 'rework', 'sent_back'].includes(s.status) && (
                                  <button 
                                    onClick={() => handleShiftCancel(s.id || s._id)}
                                    className="p-1 hover:bg-red-50 rounded text-red-600"
                                    title="Cancel Request"
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                )}
                                {s.status !== 'approved' && (
                                  <button
                                    onClick={() => handleShiftDelete(s.id || s._id)}
                                    className="p-1 hover:bg-red-50 rounded text-red-500"
                                    title="Delete Request"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                        {shiftRequests.length === 0 && (
                          <tr>
                            <td colSpan={8} className="text-center text-slate-400 py-10 font-medium">
                              No shift change requests found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

              </div>
            )}

            {/* 5. COMP-OFF TAB REDESIGN */}
            {activeTab === 'compoff' && (
              <div className="space-y-6">
                
                {/* Comp-Off Dashboard Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Available Days</p>
                    <p className="text-2xl font-bold text-emerald-600 mt-1 font-mono">{availableCompoff}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Used Comp-Offs</p>
                    <p className="text-2xl font-bold text-indigo-600 mt-1 font-mono">{usedCompoff}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Pending Approvals</p>
                    <p className="text-2xl font-bold text-amber-600 mt-1 font-mono">{pendingCompoff}</p>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 p-4">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Expired Days</p>
                    <p className="text-2xl font-bold text-slate-400 mt-1 font-mono">{expiredCompoff}</p>
                  </div>
                </div>

                {/* Ledger & History */}
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                  <div className="p-4 border-b border-slate-100 flex items-center justify-between gap-3">
                    <h3 className="font-semibold text-slate-800 text-sm">Comp-Off Ledger</h3>
                    <div className="flex items-center gap-2">
                      <select
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value)}
                        className="border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs bg-white text-slate-600 focus:outline-none"
                      >
                        <option value="all">All Statuses</option>
                        <option value="pending">Pending</option>
                        <option value="rework">Sent Back (Rework)</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                      <button 
                        onClick={() => handleExportExcel('Comp-Off')}
                        className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 rounded text-xs hover:bg-slate-50 bg-white"
                      >
                        <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" />
                        <span>Export ledger</span>
                      </button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs text-left">
                      <thead className="bg-slate-50 text-slate-500 uppercase tracking-wider text-[10px] border-b border-slate-100">
                        <tr>
                          <th className="px-4 py-3 font-semibold">Request No</th>
                          <th className="px-4 py-3 font-semibold">Worked Date</th>
                          <th className="px-4 py-3 font-semibold text-center">Hours Worked</th>
                          <th className="px-4 py-3 font-semibold text-center">Earned Credit</th>
                          <th className="px-4 py-3 font-semibold text-center">Used Credit</th>
                          <th className="px-4 py-3 font-semibold text-center">Remaining Balance</th>
                          <th className="px-4 py-3 font-semibold">Comp-Off Date</th>
                          <th className="px-4 py-3 font-semibold">Expiry Date</th>
                          <th className="px-4 py-3 font-semibold">Reason</th>
                          <th className="px-4 py-3 font-semibold">Status</th>
                          <th className="px-4 py-3 font-semibold text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {compoffRequests
                          .filter(c => {
                            if (statusFilter !== 'all') {
                              if (statusFilter === 'rework' && !['rework', 'sent_back'].includes(c.status)) return false;
                              else if (statusFilter !== 'rework' && c.status !== statusFilter) return false;
                            }
                            return true;
                          })
                          .map((c, i) => (
                          <tr key={c.id || c._id || i} className="hover:bg-slate-50/50 transition-colors">
                            <td className="px-4 py-3 font-mono font-medium text-slate-600">{c.request_number || ('C-' + String(i + 1).padStart(4, '0'))}</td>
                            <td className="px-4 py-3 font-mono text-slate-800">{c.worked_date || c.work_date || '—'}</td>
                            <td className="px-4 py-3 text-center text-slate-700 font-semibold">{c.hours_worked || 8}h</td>
                            <td className="px-4 py-3 text-center font-bold text-indigo-700">{getCompoffCredit(c)} Day(s)</td>
                            <td className="px-4 py-3 text-center font-semibold text-slate-600">{c.used_days !== undefined && c.used_days !== null ? `${c.used_days} Day(s)` : '0 Day(s)'}</td>
                            <td className="px-4 py-3 text-center font-bold text-emerald-700">{c.remaining_balance !== undefined && c.remaining_balance !== null ? `${c.remaining_balance} Day(s)` : `${Math.max(0, getCompoffCredit(c) - getCompoffUsed(c))} Day(s)`}</td>
                            <td className="px-4 py-3 font-mono font-semibold text-slate-900">{c.comp_off_date || '—'}</td>
                            <td className="px-4 py-3 font-mono text-slate-500">{c.expiry_date || '—'}</td>
                            <td className="px-4 py-3 text-slate-600 max-w-[200px] truncate" title={c.reason}>{c.reason || '—'}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase ${statusColor(c.status)}`}>
                                {c.status}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex justify-center items-center gap-1.5">
                                <button 
                                  onClick={() => { setPreviewItem(c); setPreviewType('compoff'); }}
                                  className="p-1 hover:bg-slate-100 rounded text-slate-600"
                                  title="Preview Workflow"
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                </button>
                                {['rework', 'sent_back'].includes(c.status) && (
                                  <button
                                    onClick={() => handleOpenRework(c, 'compoff')}
                                    className="p-1 hover:bg-orange-100 rounded text-orange-600 font-semibold"
                                    title="Rework / Edit & Resubmit"
                                  >
                                    <RotateCcw className="w-3.5 h-3.5" />
                                  </button>
                                )}
                                {c.status === 'rejected' && (
                                  <button
                                    onClick={() => handleOpenReapply(c, 'compoff')}
                                    className="p-1 hover:bg-amber-100 rounded text-amber-600 font-semibold"
                                    title="Reapply Request"
                                  >
                                    <RotateCcw className="w-3.5 h-3.5" />
                                  </button>
                                )}
                                {['pending', 'pending_manager', 'pending_hr', 'rework', 'sent_back'].includes(c.status) && (
                                  <button 
                                    onClick={() => handleCompoffCancel(c.id || c._id)}
                                    className="p-1 hover:bg-red-50 rounded text-red-600"
                                    title="Cancel Request"
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                )}
                                {c.status !== 'approved' && (
                                  <button
                                    onClick={() => handleCompoffDelete(c.id || c._id)}
                                    className="p-1 hover:bg-red-50 rounded text-red-500"
                                    title="Delete Request"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                        {compoffRequests.length === 0 && (
                          <tr>
                            <td colSpan={11} className="text-center text-slate-400 py-10 font-medium">
                              No comp-off requests found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

              </div>
            )}
          </>
        )}

        {/* ================= MODAL WORKFLOWS ================= */}

        {/* 1. APPLY LEAVE WIZARD MODAL */}
        {showLeaveModal && (
          <div className="fixed inset-0 bg-black/55 flex items-center justify-center z-50 p-4" onClick={() => {
            setShowLeaveModal(false);
            setReworkTarget(null);
            setReapplyTarget(null);
            setReworkNotes('');
            setLeaveWizardStep(1);
          }}>
            <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
              
              {/* Wizard Title & Step Indicators */}
              <div className="p-5 border-b border-slate-100 bg-slate-50">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-bold text-slate-900">
                    {reworkTarget 
                      ? (reworkType === 'permission' ? 'Rework & Resubmit Permission Request' : 'Rework & Resubmit Leave Application') 
                      : reapplyTarget 
                        ? (reworkType === 'permission' ? 'Reapply Permission Request' : 'Reapply Leave Application') 
                        : (leaveForm.leave_type_id === 'ONE_HOUR_PERMISSION' ? 'Apply Permission Request' : 'Apply Leave Application')}
                  </h3>
                  <button onClick={() => {
                    setShowLeaveModal(false);
                    setReworkTarget(null);
                    setReapplyTarget(null);
                    setReworkNotes('');
                    setLeaveWizardStep(1);
                  }}><X className="w-5 h-5 text-slate-400 hover:text-slate-600" /></button>
                </div>
                
                {/* Step indicator bubbles */}
                <div className="flex items-center justify-center gap-2 mt-4">
                  {[1, 2, 3].map((step) => (
                    <div key={step} className="flex items-center">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-all ${
                        leaveWizardStep === step 
                          ? 'bg-[#0052FF] text-white ring-4 ring-[#0052FF]/20' 
                          : leaveWizardStep > step 
                            ? 'bg-emerald-500 text-white' 
                            : 'bg-slate-200 text-slate-500'
                      }`}>
                        {leaveWizardStep > step ? <Check className="w-4 h-4" /> : step}
                      </div>
                      {step < 3 && <div className={`w-16 h-1 rounded transition-colors ${leaveWizardStep > step ? 'bg-emerald-500' : 'bg-slate-200'}`} />}
                    </div>
                  ))}
                </div>
              </div>

              {/* Wizard Contents */}
              <form id="apply-leave-form" onSubmit={handleLeaveSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
                {reworkTarget && (
                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-3.5 text-xs text-orange-900 animate-in fade-in duration-150">
                    <div className="flex items-center gap-2 font-bold mb-1 text-orange-800">
                      <RotateCcw className="w-4 h-4 text-orange-600 shrink-0" />
                      <span>Rework Requested by Approver</span>
                    </div>
                    <p className="text-slate-700 mb-2">
                      <strong className="text-orange-950 font-semibold">Approver Remarks:</strong>{' '}
                      {reworkTarget.send_back_remarks || reworkTarget.rework_remarks || reworkTarget.rejection_reason || reworkTarget.approver_remarks || 'Please update and resubmit your request.'}
                    </p>
                    <div>
                      <label className="block text-[11px] font-semibold text-orange-950 mb-1 uppercase tracking-wider">
                        Your Rework Notes / Clarification (Sent to Approver)
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Updated dates and clarified project coverage..."
                        value={reworkNotes}
                        onChange={(e) => setReworkNotes(e.target.value)}
                        className="w-full bg-white border border-orange-200 rounded-lg px-3 py-2 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-400"
                      />
                    </div>
                  </div>
                )}
                {reapplyTarget && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 text-xs text-amber-900 animate-in fade-in duration-150">
                    <div className="flex items-center gap-2 font-bold mb-1 text-amber-800">
                      <RotateCcw className="w-4 h-4 text-amber-600 shrink-0" />
                      <span>Reapplying for Previously Rejected Request</span>
                    </div>
                    <p className="text-slate-700">
                      <strong className="text-amber-950 font-semibold">Previous Rejection Reason:</strong>{' '}
                      {reapplyTarget.rejection_reason || reapplyTarget.approver_remarks || reapplyTarget.remarks || 'Rejected by approver'}
                    </p>
                    <p className="text-[11px] text-amber-700 mt-1">
                      You are submitting a fresh application referencing the rejected request. You may adjust dates, reasons, and supporting information as needed.
                    </p>
                  </div>
                )}
                
                {/* STEP 1: Date & Type */}
                {leaveWizardStep === 1 && (
                  <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider">Leave Type *</label>
                        {leaveTypesLoading && (
                          <span className="text-[10px] text-blue-600 font-medium flex items-center gap-1">
                            <RefreshCw className="w-3 h-3 animate-spin" /> Fetching active types...
                          </span>
                        )}
                      </div>

                      {leaveTypesError && (
                        <div className="mb-2 bg-red-50 border border-red-200 text-red-700 p-2.5 rounded-lg text-xs font-medium">
                          {leaveTypesError}
                        </div>
                      )}

                      {!leaveTypesLoading && leaveTypes.length === 0 && (
                        <div className="mb-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3 text-xs flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                          <span>No active leave types available. Please contact your HR administrator.</span>
                        </div>
                      )}

                      <select 
                        data-testid="leave-type-select" 
                        value={leaveForm.leave_type_id} 
                        onChange={(e) => setLeaveForm({ ...leaveForm, leave_type_id: e.target.value })} 
                        className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0052FF]/20 focus:border-[#0052FF] disabled:bg-slate-100 disabled:text-slate-400" 
                        required
                        disabled={leaveTypesLoading || (leaveTypes.length === 0 && leaveForm.leave_type_id !== 'ONE_HOUR_PERMISSION')}
                      >
                        <option value="">{leaveTypes.length === 0 ? "No active leave types available" : "Select a Leave Type"}</option>
                        {leaveForm.leave_type_id === 'ONE_HOUR_PERMISSION' && (
                          <option value="ONE_HOUR_PERMISSION">Short Permission</option>
                        )}
                        {leaveTypes.map((lt) => (
                          <option key={lt.id} value={lt.id}>{lt.name} ({lt.code})</option>
                        ))}
                      </select>
                    </div>

                    {/* Balance Validation & Impact Indicator */}
                    {(() => {
                      if (!leaveForm.leave_type_id || leaveForm.leave_type_id === 'ONE_HOUR_PERMISSION') return null;
                      const selectedLt = leaveTypes.find(t => t.id === leaveForm.leave_type_id || t.code === leaveForm.leave_type_id);
                      const selectedCode = selectedLt?.code || selectedLt?.leave_type || '';
                      const requiresBalance = selectedLt?.requires_balance !== false && selectedLt?.balance_required !== false && selectedCode !== 'LOP';
                      
                      let reqDays = 1;
                      if (leaveForm.half_day) {
                        reqDays = 0.5;
                      } else if (leaveForm.from_date && leaveForm.to_date) {
                        const d1 = new Date(leaveForm.from_date);
                        const d2 = new Date(leaveForm.to_date);
                        const diffTime = d2 - d1;
                        reqDays = diffTime >= 0 ? Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1 : 1;
                      }

                      if (!requiresBalance) {
                        return (
                          <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs space-y-2 animate-in fade-in duration-150">
                            <div className="flex items-center justify-between font-semibold">
                              <span className="text-slate-800 flex items-center gap-1.5 font-bold">
                                ℹ️ {selectedLt?.name || selectedCode} (Unpaid Leave)
                              </span>
                              <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded text-[11px] font-semibold border border-amber-200">
                                No Balance Required
                              </span>
                            </div>
                            <p className="text-[11px] text-slate-600">
                              Loss of Pay does not require an available leave balance. Approved duration ({reqDays} day{reqDays > 1 ? 's' : ''}) will be recorded as unpaid leave without deduction from paid leave balances.
                            </p>
                          </div>
                        );
                      }

                      const matchedBal = leaveBalances.find(b => 
                        (selectedCode && (b.leave_type_code === selectedCode || b.leave_type === selectedCode)) ||
                        b.leave_type_id === leaveForm.leave_type_id
                      );
                      const availBal = matchedBal ? parseFloat(matchedBal.available_balance ?? matchedBal.closing_balance ?? matchedBal.balance ?? 0) : null;
                      const afterBal = availBal !== null ? Math.round((availBal - reqDays) * 100) / 100 : null;

                      if (availBal !== null && availBal <= 0) {
                        return (
                          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-700 font-semibold space-y-2 animate-in fade-in duration-150">
                            <div className="flex items-center justify-between">
                              <span>⚠️ Zero balance available for {selectedLt?.name || 'this leave type'}. Application may result in LOP.</span>
                              <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded font-mono text-[11px] shrink-0 ml-2">0 Available</span>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-center pt-1 border-t border-red-200 text-[11px]">
                              <div><span className="text-slate-500">Available:</span> <strong className="text-red-700 font-mono">{availBal}</strong></div>
                              <div><span className="text-slate-500">Requested:</span> <strong className="text-amber-700 font-mono">{reqDays}</strong></div>
                              <div><span className="text-slate-500">After Approval:</span> <strong className="text-red-700 font-mono">{afterBal}</strong></div>
                            </div>
                          </div>
                        );
                      }
                      if (availBal !== null) {
                        const isInsufficient = availBal < reqDays;
                        return (
                          <div className={`rounded-xl border p-3 text-xs space-y-2 animate-in fade-in duration-150 ${
                            isInsufficient ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-blue-50/70 border-blue-200 text-slate-800'
                          }`}>
                            <div className="flex items-center justify-between font-semibold">
                              <span>Leave Balance Preview ({selectedLt?.name || selectedCode}):</span>
                              <span className={`px-2 py-0.5 rounded text-[11px] font-mono font-bold ${
                                isInsufficient ? 'bg-amber-200 text-amber-900' : 'bg-emerald-100 text-emerald-800'
                              }`}>
                                {availBal} Day(s) Available
                              </span>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-center bg-white p-2 rounded-lg border border-slate-200">
                              <div>
                                <p className="text-[10px] text-slate-500 font-medium">Available Now</p>
                                <p className="text-base font-bold text-slate-900 font-mono">{availBal}</p>
                              </div>
                              <div>
                                <p className="text-[10px] text-slate-500 font-medium">Requested</p>
                                <p className="text-base font-bold text-amber-600 font-mono">{reqDays}</p>
                              </div>
                              <div>
                                <p className="text-[10px] text-slate-500 font-medium">After Approval</p>
                                <p className={`text-base font-bold font-mono ${isInsufficient ? 'text-red-600' : 'text-emerald-700'}`}>
                                  {afterBal}
                                </p>
                              </div>
                            </div>
                            {isInsufficient && (
                              <p className="text-[11px] text-amber-800 font-semibold">
                                ⚠️ Insufficient balance: requested {reqDays} day(s) exceeds currently available {availBal} day(s).
                              </p>
                            )}
                          </div>
                        );
                      }
                      return null;
                    })()}

                    {leaveForm.leave_type_id === 'ONE_HOUR_PERMISSION' ? (
                      <div className="space-y-3 bg-blue-50/60 p-3.5 rounded-xl border border-blue-100">
                        <div className="flex items-center justify-between">
                          <label className="block text-xs font-bold text-blue-900 uppercase">Permission Date *</label>
                          <span className="text-[10px] font-bold bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">Max 1 Hour</span>
                        </div>
                        <input
                          type="date"
                          value={leaveForm.permission_date}
                          onChange={(e) => setLeaveForm({ ...leaveForm, permission_date: e.target.value })}
                          className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white"
                          required
                        />
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">Start Time *</label>
                            <input
                              type="time"
                              value={leaveForm.start_time}
                              onChange={(e) => setLeaveForm({ ...leaveForm, start_time: e.target.value })}
                              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
                              required
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-slate-700 uppercase mb-1">End Time *</label>
                            <input
                              type="time"
                              value={leaveForm.end_time}
                              onChange={(e) => setLeaveForm({ ...leaveForm, end_time: e.target.value })}
                              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
                              required
                            />
                          </div>
                        </div>
                        {(() => {
                          const sMins = (parseInt(leaveForm.start_time?.split(':')[0] || 0) * 60) + parseInt(leaveForm.start_time?.split(':')[1] || 0);
                          const eMins = (parseInt(leaveForm.end_time?.split(':')[0] || 0) * 60) + parseInt(leaveForm.end_time?.split(':')[1] || 0);
                          const diff = eMins - sMins;
                          if (diff <= 0) {
                            return <div className="text-xs text-red-600 font-bold bg-red-50 p-2 rounded-lg border border-red-200">⚠️ End time must be after start time.</div>;
                          } else if (diff > 60) {
                            return <div className="text-xs text-red-600 font-bold bg-red-50 p-2 rounded-lg border border-red-200">⚠️ Duration cannot exceed 1 hour ({diff} mins selected).</div>;
                          } else {
                            return <div className="text-xs text-emerald-700 font-bold bg-emerald-50 p-2 rounded-lg border border-emerald-200 flex items-center justify-between"><span>Duration: {diff} Minutes (1 Hour)</span><CheckCircle2 className="w-4 h-4 text-emerald-600" /></div>;
                          }
                        })()}
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">From Date *</label>
                            <input 
                              type="date" 
                              data-testid="leave-from" 
                              value={leaveForm.from_date} 
                              onChange={(e) => setLeaveForm({ ...leaveForm, from_date: e.target.value })} 
                              className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm" 
                              required 
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">To Date *</label>
                            <input 
                              type="date" 
                              data-testid="leave-to" 
                              value={leaveForm.to_date} 
                              onChange={(e) => setLeaveForm({ ...leaveForm, to_date: e.target.value })} 
                              className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm" 
                              required 
                            />
                          </div>
                        </div>

                        <div className="space-y-2 py-1">
                          <label className="flex items-center gap-2.5 text-slate-700 cursor-pointer">
                            <input 
                              type="checkbox" 
                              checked={leaveForm.half_day} 
                              onChange={(e) => setLeaveForm({ 
                                ...leaveForm, 
                                half_day: e.target.checked,
                                half_day_period: e.target.checked ? (leaveForm.half_day_period || 'first_half') : 'first_half' 
                              })} 
                              className="w-4 h-4 text-[#0052FF] rounded border-slate-300 focus:ring-[#0052FF]/20"
                            />
                            <span className="text-sm font-medium">Apply as Half Day</span>
                          </label>

                          {leaveForm.half_day && (
                            <div className="bg-blue-50/60 border border-blue-100 rounded-lg p-3 space-y-2 animate-in fade-in duration-150">
                              <label className="block text-xs font-semibold text-blue-900 uppercase tracking-wider">
                                Select Half-Day Period *
                              </label>
                              <div className="grid grid-cols-2 gap-3">
                                <label className={`flex items-center space-x-2 p-2.5 rounded-lg border text-xs font-semibold cursor-pointer transition-all ${
                                  leaveForm.half_day_period === 'first_half' 
                                    ? 'bg-white border-[#0052FF] text-[#0052FF] shadow-sm' 
                                    : 'bg-white/60 border-slate-200 text-slate-700 hover:bg-white'
                                }`}>
                                  <input
                                    type="radio"
                                    name="half_day_period"
                                    value="first_half"
                                    checked={leaveForm.half_day_period === 'first_half'}
                                    onChange={(e) => setLeaveForm({ ...leaveForm, half_day_period: e.target.value })}
                                    className="w-4 h-4 text-[#0052FF]"
                                  />
                                  <span>First Half</span>
                                </label>
                                <label className={`flex items-center space-x-2 p-2.5 rounded-lg border text-xs font-semibold cursor-pointer transition-all ${
                                  leaveForm.half_day_period === 'second_half' 
                                    ? 'bg-white border-[#0052FF] text-[#0052FF] shadow-sm' 
                                    : 'bg-white/60 border-slate-200 text-slate-700 hover:bg-white'
                                }`}>
                                  <input
                                    type="radio"
                                    name="half_day_period"
                                    value="second_half"
                                    checked={leaveForm.half_day_period === 'second_half'}
                                    onChange={(e) => setLeaveForm({ ...leaveForm, half_day_period: e.target.value })}
                                    className="w-4 h-4 text-[#0052FF]"
                                  />
                                  <span>Second Half</span>
                                </label>
                              </div>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* STEP 2: Reason & Details */}
                {leaveWizardStep === 2 && (
                  <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Reason for Leave *</label>
                      <textarea 
                        data-testid="leave-reason" 
                        value={leaveForm.reason} 
                        onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })} 
                        rows={3} 
                        placeholder="Please elaborate your reason..."
                        className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0052FF]/20" 
                        required
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Emergency Contact</label>
                        <input 
                          type="tel" 
                          placeholder="Phone number"
                          value={leaveForm.emergency_contact} 
                          onChange={(e) => setLeaveForm({ ...leaveForm, emergency_contact: e.target.value })} 
                          className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0052FF]/20" 
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Delegate Tasks To</label>
                        <input 
                          type="text" 
                          placeholder="Employee Name"
                          value={leaveForm.delegate_employee} 
                          onChange={(e) => setLeaveForm({ ...leaveForm, delegate_employee: e.target.value })} 
                          className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0052FF]/20" 
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Supporting Document (optional)</label>
                      <input 
                        type="file" 
                        onChange={(e) => setLeaveForm({ ...leaveForm, attachment_name: e.target.files[0]?.name || '' })} 
                        className="w-full text-xs text-slate-500 border border-slate-300 rounded-lg px-3 py-2 cursor-pointer bg-slate-50"
                      />
                    </div>
                  </div>
                )}

                {/* STEP 3: Preview & Submit */}
                {leaveWizardStep === 3 && (
                  <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300 bg-slate-50 p-4 rounded-xl border border-slate-100 text-xs">
                    <h4 className="font-bold text-sm text-slate-900 border-b border-slate-200 pb-2">Leave Summary Details</h4>
                    <div className="grid grid-cols-2 gap-y-3 gap-x-2">
                      <p className="text-slate-500">Leave Type</p>
                      <p className="font-semibold text-slate-800 text-right">{leaveTypes.find(t => t.id === leaveForm.leave_type_id)?.name || 'N/A'}</p>
                      
                      <p className="text-slate-500">Duration</p>
                      <p className="font-semibold text-slate-800 text-right">{leaveForm.from_date} to {leaveForm.to_date}</p>
                      
                      <p className="text-slate-500">Half Day Option</p>
                      <p className="font-semibold text-slate-800 text-right">
                        {leaveForm.half_day 
                          ? `Yes (${leaveForm.half_day_period === 'first_half' ? 'First Half' : 'Second Half'})` 
                          : 'Full Day'}
                      </p>

                      <p className="text-slate-500">Reason</p>
                      <p className="font-semibold text-slate-800 text-right truncate max-w-[200px]">{leaveForm.reason || 'N/A'}</p>

                      <p className="text-slate-500">Delegate Task</p>
                      <p className="font-semibold text-slate-800 text-right">{leaveForm.delegate_employee || 'N/A'}</p>

                      <p className="text-slate-500">Emergency Contact</p>
                      <p className="font-semibold text-slate-800 text-right">{leaveForm.emergency_contact || 'N/A'}</p>
                    </div>

                    {/* Authoritative Balance Impact Preview */}
                    {(() => {
                      if (!leaveForm.leave_type_id || leaveForm.leave_type_id === 'ONE_HOUR_PERMISSION') return null;
                      const selectedLt = leaveTypes.find(t => t.id === leaveForm.leave_type_id || t.code === leaveForm.leave_type_id);
                      const selectedCode = selectedLt?.code || selectedLt?.leave_type || '';
                      const requiresBalance = selectedLt?.requires_balance !== false && selectedLt?.balance_required !== false && selectedCode !== 'LOP';
                      let reqDays = 1;
                      if (leaveForm.half_day) reqDays = 0.5;
                      else if (leaveForm.from_date && leaveForm.to_date) {
                        const d1 = new Date(leaveForm.from_date);
                        const d2 = new Date(leaveForm.to_date);
                        const diffTime = d2 - d1;
                        reqDays = diffTime >= 0 ? Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1 : 1;
                      }

                      if (!requiresBalance) {
                        return (
                          <div className="mt-3 bg-slate-100/80 rounded-lg border border-slate-200 p-3 space-y-1">
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600">Balance Policy</span>
                              <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded text-[10px] font-semibold border border-amber-200">
                                No Balance Required
                              </span>
                            </div>
                            <p className="text-[11px] text-slate-600">
                              Loss of Pay does not consume paid balance. Total unpaid duration: <strong className="font-mono text-slate-800">{reqDays} day{reqDays > 1 ? 's' : ''}</strong>.
                            </p>
                          </div>
                        );
                      }

                      const matchedBal = leaveBalances.find(b => 
                        (selectedCode && (b.leave_type_code === selectedCode || b.leave_type === selectedCode)) ||
                        b.leave_type_id === leaveForm.leave_type_id
                      );
                      const availBal = matchedBal ? parseFloat(matchedBal.available_balance ?? matchedBal.closing_balance ?? matchedBal.balance ?? 0) : null;
                      if (availBal === null) return null;
                      const afterBal = Math.round((availBal - reqDays) * 100) / 100;
                      return (
                        <div className="mt-3 bg-white rounded-lg border border-slate-200 p-3 space-y-2">
                          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Balance Impact Preview</p>
                          <div className="grid grid-cols-3 gap-2 text-center">
                            <div className="bg-slate-50 p-2 rounded border border-slate-100">
                              <p className="text-[10px] text-slate-500">Available Now</p>
                              <p className="text-sm font-bold text-slate-900 font-mono">{availBal}</p>
                            </div>
                            <div className="bg-amber-50 p-2 rounded border border-amber-100">
                              <p className="text-[10px] text-amber-700">Requested</p>
                              <p className="text-sm font-bold text-amber-700 font-mono">{reqDays}</p>
                            </div>
                            <div className={`p-2 rounded border ${afterBal < 0 ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-100'}`}>
                              <p className="text-[10px] text-slate-500">After Approval</p>
                              <p className={`text-sm font-bold font-mono ${afterBal < 0 ? 'text-red-700' : 'text-emerald-700'}`}>{afterBal}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 text-[10px]">
                      Your request will be routed to your Reporting Manager, then to HR. Click Submit to execute.
                    </div>
                  </div>
                )}

              </form>

              {/* Wizard Footer Actions */}
              <div className="p-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between gap-3">
                {leaveWizardStep > 1 ? (
                  <button 
                    type="button" 
                    onClick={() => setLeaveWizardStep(prev => prev - 1)}
                    className="px-4 py-2 border border-slate-300 rounded-lg text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50"
                  >
                    Back
                  </button>
                ) : (
                  <div />
                )}

                {leaveWizardStep < 3 ? (
                  <button 
                    type="button" 
                    disabled={leaveWizardStep === 1 && leaveTypes.length === 0 && leaveForm.leave_type_id !== 'ONE_HOUR_PERMISSION'}
                    onClick={() => {
                      if (leaveWizardStep === 1 && !leaveForm.leave_type_id) {
                        if (leaveTypes.length === 0) {
                          toast.error('No active leave types available. Please contact your HR administrator.');
                        } else {
                          toast.error('Please pick a leave type first');
                        }
                        return;
                      }
                      if (leaveWizardStep === 1 && leaveForm.half_day && !leaveForm.half_day_period) {
                        toast.error('Please select First Half or Second Half for your half-day leave');
                        return;
                      }
                      setLeaveWizardStep(prev => prev + 1);
                    }}
                    className="px-5 py-2.5 bg-[#0052FF] text-white text-xs font-semibold rounded-lg hover:bg-[#0040CC] disabled:opacity-50"
                  >
                    Next
                  </button>
                ) : (
                  <button 
                    form="apply-leave-form"
                    data-testid="submit-leave-btn"
                    type="submit" 
                    onClick={handleLeaveSubmit}
                    disabled={submitting}
                    className="px-6 py-2.5 bg-[#0052FF] text-white text-xs font-semibold rounded-lg hover:bg-[#0040CC] disabled:opacity-50"
                  >
                    {submitting 
                      ? 'Submitting...' 
                      : reworkTarget 
                        ? 'Resubmit Request' 
                        : reapplyTarget 
                          ? 'Submit Reapplication' 
                          : 'Submit Application'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 2. REGULARIZATION REQUEST MODAL */}
        {showRegModal && (
          <div className="fixed inset-0 bg-black/45 flex items-center justify-center z-50 p-4" onClick={() => {
            setShowRegModal(false);
            setReworkTarget(null);
            setReapplyTarget(null);
            setReworkNotes('');
          }}>
            <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-lg font-bold text-slate-900">
                  {reworkTarget 
                    ? 'Rework & Resubmit Attendance Regularization' 
                    : reapplyTarget 
                      ? 'Reapply Attendance Regularization' 
                      : 'Attendance Regularization'}
                </h3>
                <button onClick={() => {
                  setShowRegModal(false);
                  setReworkTarget(null);
                  setReapplyTarget(null);
                  setReworkNotes('');
                }}><X className="w-5 h-5 text-slate-400" /></button>
              </div>

              <form onSubmit={handleRegSubmit} className="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
                {reworkTarget && (
                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-3.5 text-xs text-orange-900 animate-in fade-in duration-150">
                    <div className="flex items-center gap-2 font-bold mb-1 text-orange-800">
                      <RotateCcw className="w-4 h-4 text-orange-600 shrink-0" />
                      <span>Rework Requested by Manager</span>
                    </div>
                    <p className="text-slate-700 mb-2">
                      <strong className="text-orange-950 font-semibold">Manager Remarks:</strong>{' '}
                      {reworkTarget.send_back_remarks || reworkTarget.rework_remarks || reworkTarget.rejection_reason || reworkTarget.approver_remarks || 'Please adjust the regularization details and resubmit.'}
                    </p>
                    <div>
                      <label className="block text-[11px] font-semibold text-orange-950 mb-1 uppercase tracking-wider">
                        Your Rework Notes / Clarification
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Corrected proposed punch times as requested..."
                        value={reworkNotes}
                        onChange={(e) => setReworkNotes(e.target.value)}
                        className="w-full bg-white border border-orange-200 rounded-lg px-3 py-2 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-400"
                      />
                    </div>
                  </div>
                )}
                {reapplyTarget && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 text-xs text-amber-900 animate-in fade-in duration-150">
                    <div className="flex items-center gap-2 font-bold mb-1 text-amber-800">
                      <RotateCcw className="w-4 h-4 text-amber-600 shrink-0" />
                      <span>Reapplying for Previously Rejected Regularization</span>
                    </div>
                    <p className="text-slate-700">
                      <strong className="text-amber-950 font-semibold">Previous Rejection Reason:</strong>{' '}
                      {reapplyTarget.rejection_reason || reapplyTarget.approver_remarks || reapplyTarget.remarks || 'Rejected by approver'}
                    </p>
                    <p className="text-[11px] text-amber-700 mt-1">
                      You are submitting a fresh regularization referencing the rejected request.
                    </p>
                  </div>
                )}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider">
                      Select Attendance Date *
                    </label>
                    <span className="text-[11px] font-medium text-slate-500">
                      {actionRequiredDates.length} eligible date{actionRequiredDates.length === 1 ? '' : 's'}
                    </span>
                  </div>

                  {/* Calendar Month Navigation & Grid */}
                  <div className="border border-slate-200 rounded-xl p-3 bg-slate-50/70 mb-3 space-y-2.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 font-bold text-sm text-slate-800">
                        <CalendarIcon className="w-4 h-4 text-[#0052FF]" />
                        <span>
                          {regCalendarMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}
                        </span>
                        {loadingEligibleDates && (
                          <RefreshCw className="w-3.5 h-3.5 text-blue-500 animate-spin ml-1" />
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          data-testid="reg-month-prev"
                          onClick={() => {
                            const prevM = new Date(regCalendarMonth.getFullYear(), regCalendarMonth.getMonth() - 1, 1);
                            setRegCalendarMonth(prevM);
                            fetchEligibleDatesForMonth(prevM);
                          }}
                          className="p-1 hover:bg-white rounded-lg border border-slate-200 text-slate-600 transition-colors shadow-sm"
                          title="Previous Month"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          data-testid="reg-month-next"
                          onClick={() => {
                            const nextM = new Date(regCalendarMonth.getFullYear(), regCalendarMonth.getMonth() + 1, 1);
                            setRegCalendarMonth(nextM);
                            fetchEligibleDatesForMonth(nextM);
                          }}
                          className="p-1 hover:bg-white rounded-lg border border-slate-200 text-slate-600 transition-colors shadow-sm"
                          title="Next Month"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Day-of-week headers */}
                    <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      <span>Mo</span><span>Tu</span><span>We</span><span>Th</span><span>Fr</span><span>Sa</span><span>Su</span>
                    </div>

                    {/* Calendar Days Grid */}
                    <div className="grid grid-cols-7 gap-1">
                      {getRegCalendarCells(regCalendarMonth).map((cell, idx) => {
                        if (!cell) {
                          return <div key={`pad-${idx}`} className="h-8" />;
                        }
                        const { dateStr, dayNum, isEligible, isSelected, eligItem } = cell;
                        if (isEligible) {
                          return (
                            <button
                              key={dateStr}
                              type="button"
                              data-testid={`reg-calendar-day-${dateStr}`}
                              onClick={() => handleSelectRegDate(dateStr, eligItem)}
                              className={`h-8 rounded-lg text-xs font-semibold flex flex-col items-center justify-center transition-all ${
                                isSelected
                                  ? 'bg-[#0052FF] text-white shadow-md ring-2 ring-[#0052FF]/30 font-bold scale-105'
                                  : 'bg-white hover:bg-blue-50 text-blue-700 border border-blue-200 hover:border-blue-400 shadow-sm'
                              }`}
                              title={`${dateStr} — ${eligItem?.current_status || 'Eligible for Regularization'}`}
                            >
                              <span>{dayNum}</span>
                              <span className={`w-1 h-1 rounded-full ${isSelected ? 'bg-white' : 'bg-[#0052FF]'}`} />
                            </button>
                          );
                        }
                        return (
                          <button
                            key={dateStr}
                            type="button"
                            disabled
                            data-testid={`reg-calendar-day-${dateStr}-disabled`}
                            className="h-8 rounded-lg text-xs text-slate-300 bg-slate-100/60 cursor-not-allowed flex items-center justify-center border border-transparent select-none"
                            title="No attendance record or exception requiring regularization"
                          >
                            {dayNum}
                          </button>
                        );
                      })}
                    </div>

                    {/* Helpful message when selected month has no eligible dates */}
                    {actionRequiredDates.length === 0 && !loadingEligibleDates && (
                      <div className="p-3 bg-emerald-50 border border-emerald-200/80 rounded-xl text-emerald-800 text-xs flex items-start gap-2 mt-2" data-testid="reg-no-eligible-notice">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-emerald-900">No attendance regularization is required at this time.</p>
                          <p className="text-[11px] text-emerald-700 mt-0.5">
                            Only absent and half-day dates that require correction are eligible for regularization. Present dates, holidays, and weekly offs are excluded.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Dropdown Quick Selector */}
                  <div className="space-y-1">
                    <label className="block text-[11px] font-medium text-slate-500">Or choose from list:</label>
                    <select
                      data-testid="reg-date-select"
                      value={regForm.attendance_date}
                      onChange={(e) => {
                        const val = e.target.value;
                        const item = actionRequiredDates.find(d => d.attendance_date === val);
                        handleSelectRegDate(val, item);
                      }}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white font-medium text-slate-800 focus:ring-2 focus:ring-[#0052FF]/20"
                      required
                    >
                      <option value="">
                        {actionRequiredDates.length > 0 ? '-- Select an eligible date --' : 'No attendance regularization is required at this time.'}
                      </option>
                      {actionRequiredDates.map((d) => (
                        <option key={d.attendance_date} value={d.attendance_date}>
                          {`${d.attendance_date} — ${d.current_status || 'Record'}${d.current_in_time ? ` (In: ${d.current_in_time})` : ''}${d.current_out_time ? ` (Out: ${d.current_out_time})` : ''}`}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Selected Attendance Details Card */}
                  {selectedAttDetail && (
                    <div className="mt-3 bg-blue-50/70 border border-blue-200 rounded-xl p-3 text-xs space-y-1.5" data-testid="reg-attendance-details">
                      <div className="font-bold text-blue-900 flex justify-between items-center border-b border-blue-200/60 pb-1.5">
                        <span className="flex items-center gap-1.5">
                          <Clock className="w-3.5 h-3.5 text-[#0052FF]" />
                          Attendance on {selectedAttDetail.attendance_date}
                        </span>
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded font-semibold uppercase text-[10px]">
                          {selectedAttDetail.current_status || 'Active Record'}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 pt-1 text-slate-600">
                        <div>
                          <span className="text-slate-400">Recorded IN:</span>{' '}
                          <strong className="text-slate-700">{selectedAttDetail.current_in_time || 'None (Missing)'}</strong>
                        </div>
                        <div>
                          <span className="text-slate-400">Recorded OUT:</span>{' '}
                          <strong className="text-slate-700">{selectedAttDetail.current_out_time || 'None (Missing)'}</strong>
                        </div>
                      </div>
                      {selectedAttDetail.has_exception && (
                        <div className="text-amber-800 bg-amber-100/60 px-2 py-1 rounded text-[11px] font-medium">
                          Exception: {selectedAttDetail.exception_type || 'Authoritative Attendance Exception'}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Reason / Request Type *</label>
                  <select
                    data-testid="reg-type-select"
                    value={regForm.request_type}
                    onChange={(e) => {
                      const val = e.target.value;
                      setRegForm({
                        ...regForm,
                        request_type: val,
                        alternate_status: val === 'compoff' ? 'leave' : regForm.alternate_status
                      });
                    }}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white font-medium"
                  >
                    <option value="missing_punch">Missing punch (forgot to scan)</option>
                    <option value="wrong_punch">Wrong punch (recorded incorrectly)</option>
                    <option value="on_duty">On duty (client visit / outside office)</option>
                    <option value="work_from_home">Work from home</option>
                    <option value="compoff">Comp-Off Adjustment (Use Approved Comp-Off)</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {regForm.request_type === 'compoff' && (
                  <div className="space-y-2 bg-blue-50/70 p-3.5 rounded-xl border border-blue-100">
                    <label className="block text-xs font-semibold text-blue-900 uppercase tracking-wider mb-1">
                      Select Approved Comp-Off Source *
                    </label>
                    {eligibleCompOffs.length > 0 ? (
                      <select
                        data-testid="reg-compoff-select"
                        value={regForm.comp_off_id || ''}
                        onChange={(e) => setRegForm({ ...regForm, comp_off_id: e.target.value })}
                        className="w-full border border-blue-200 rounded-lg px-3 py-2.5 text-sm bg-white font-medium text-slate-800"
                        required
                      >
                        <option value="">-- Choose an approved Comp-Off --</option>
                        {eligibleCompOffs.map((co) => (
                          <option key={co.id} value={co.id}>
                            {`${co.comp_off_code || 'CO'} — Worked ${co.worked_date} (${co.available_days || 1} Day Available)`}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div className="p-3 bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-lg">
                        No approved Comp-Off records available. You must first submit a Comp-Off request and receive approval.
                      </div>
                    )}

                    {regForm.comp_off_id && (() => {
                      const selectedCO = eligibleCompOffs.find(c => c.id === regForm.comp_off_id);
                      if (!selectedCO) return null;
                      return (
                        <div className="mt-2 p-3 bg-white border border-blue-200 rounded-lg text-xs space-y-1 text-slate-700 shadow-sm">
                          <div className="font-semibold text-blue-900 flex justify-between items-center">
                            <span>Comp-Off: {selectedCO.comp_off_code}</span>
                            <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded text-[10px] font-bold uppercase">Approved</span>
                          </div>
                          <div><span className="text-slate-500">Worked Date:</span> <strong className="text-slate-800">{selectedCO.worked_date}</strong></div>
                          <div><span className="text-slate-500">Hours Worked:</span> {selectedCO.hours_worked} hrs</div>
                          <div><span className="text-slate-500">Credit Available:</span> <strong className="text-emerald-700">{selectedCO.available_days} Day</strong></div>
                          {selectedCO.reason && <div><span className="text-slate-500">Reason:</span> {selectedCO.reason}</div>}
                        </div>
                      );
                    })()}
                  </div>
                )}

                {regForm.request_type !== 'compoff' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Proposed In Time</label>
                      <input
                        type="time"
                        data-testid="reg-in-time"
                        value={regForm.proposed_in_time}
                        onChange={(e) => setRegForm({ ...regForm, proposed_in_time: e.target.value })}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Proposed Out Time</label>
                      <input
                        type="time"
                        data-testid="reg-out-time"
                        value={regForm.proposed_out_time}
                        onChange={(e) => setRegForm({ ...regForm, proposed_out_time: e.target.value })}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Mark Attendance As</label>
                  {regForm.request_type === 'compoff' ? (
                    <input
                      type="text"
                      value="Comp-Off Leave (CompOff)"
                      disabled
                      className="w-full border border-slate-200 bg-slate-100 rounded-lg px-3 py-2.5 text-sm text-slate-700 font-semibold"
                    />
                  ) : (
                    <select
                      data-testid="reg-status-select"
                      value={regForm.alternate_status}
                      onChange={(e) => setRegForm({ ...regForm, alternate_status: e.target.value })}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white"
                    >
                      <option value="">Keep current status</option>
                      <option value="present">Present</option>
                      <option value="half_day">Half Day</option>
                      <option value="on_duty">On Duty</option>
                      <option value="wfh">Work From Home</option>
                    </select>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Detailed Explanation *</label>
                  <textarea
                    data-testid="reg-reason"
                    value={regForm.reason}
                    onChange={(e) => setRegForm({ ...regForm, reason: e.target.value })}
                    rows={3}
                    placeholder="Provide details for punch correction..."
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0052FF]/20"
                    required
                  />
                </div>

                <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 text-blue-800 text-[10px] leading-relaxed">
                  Regularization requests are processed by your Reporting Manager and HR. Alternate status is updated on approval.
                </div>

                <button
                  data-testid="submit-reg-btn"
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-[#0052FF] text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-[#0040CC] disabled:opacity-50 transition-colors"
                >
                  {submitting 
                    ? 'Submitting...' 
                    : reworkTarget 
                      ? 'Resubmit Regularization' 
                      : reapplyTarget 
                        ? 'Submit Reapplication' 
                        : 'Submit Request'}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* 3. SHIFT REQUEST MODAL */}
        {showShiftModal && (
          <div className="fixed inset-0 bg-black/45 flex items-center justify-center z-50 p-4" onClick={() => {
            setShowShiftModal(false);
            setReworkTarget(null);
            setReapplyTarget(null);
            setReworkNotes('');
          }}>
            <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-lg font-bold text-slate-900">
                  {reworkTarget 
                    ? 'Rework & Resubmit Shift Request' 
                    : reapplyTarget 
                      ? 'Reapply Shift Request' 
                      : 'Shift Swap & Change'}
                </h3>
                <button onClick={() => {
                  setShowShiftModal(false);
                  setReworkTarget(null);
                  setReapplyTarget(null);
                  setReworkNotes('');
                }}><X className="w-5 h-5 text-slate-400" /></button>
              </div>

              <form onSubmit={handleShiftSubmit} className="p-6 space-y-4">
                {reworkTarget && (
                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-3.5 text-xs text-orange-900 animate-in fade-in duration-150">
                    <div className="flex items-center gap-2 font-bold mb-1 text-orange-800">
                      <RotateCcw className="w-4 h-4 text-orange-600 shrink-0" />
                      <span>Rework Requested by Manager</span>
                    </div>
                    <p className="text-slate-700 mb-2">
                      <strong className="text-orange-950 font-semibold">Manager Remarks:</strong>{' '}
                      {reworkTarget.send_back_remarks || reworkTarget.rework_remarks || reworkTarget.rejection_reason || reworkTarget.approver_remarks || 'Please adjust shift details and resubmit.'}
                    </p>
                    <div>
                      <label className="block text-[11px] font-semibold text-orange-950 mb-1 uppercase tracking-wider">
                        Your Rework Notes / Clarification
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Adjusted requested shift timing..."
                        value={reworkNotes}
                        onChange={(e) => setReworkNotes(e.target.value)}
                        className="w-full bg-white border border-orange-200 rounded-lg px-3 py-2 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-400"
                      />
                    </div>
                  </div>
                )}
                {reapplyTarget && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 text-xs text-amber-900 animate-in fade-in duration-150">
                    <div className="flex items-center gap-2 font-bold mb-1 text-amber-800">
                      <RotateCcw className="w-4 h-4 text-amber-600 shrink-0" />
                      <span>Reapplying for Previously Rejected Shift Request</span>
                    </div>
                    <p className="text-slate-700">
                      <strong className="text-amber-950 font-semibold">Previous Rejection Reason:</strong>{' '}
                      {reapplyTarget.rejection_reason || reapplyTarget.approver_remarks || reapplyTarget.remarks || 'Rejected by approver'}
                    </p>
                  </div>
                )}
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Exchange Type</label>
                  <select 
                    value={shiftForm.request_type} 
                    onChange={(e) => setShiftForm({ ...shiftForm, request_type: e.target.value })} 
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white"
                  >
                    <option value="change">Shift Change</option>
                    <option value="swap">Shift Swap (with peer)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Effective Date *</label>
                  <input 
                    type="date" 
                    value={shiftForm.requested_date} 
                    onChange={(e) => setShiftForm({ ...shiftForm, requested_date: e.target.value })} 
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm" 
                    required 
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Current Shift</label>
                    <input 
                      type="text" 
                      value={shiftForm.current_shift_id} 
                      disabled 
                      className="w-full border border-slate-200 bg-slate-50 text-slate-400 rounded-lg px-3 py-2.5 text-sm" 
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Requested Shift *</label>
                    <select 
                      value={shiftForm.requested_shift_id} 
                      onChange={(e) => setShiftForm({ ...shiftForm, requested_shift_id: e.target.value })} 
                      className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm bg-white"
                    >
                      <option value="MOR_6_2">Morning Shift (6 AM - 2 PM)</option>
                      <option value="GEN_9_6">General Shift (9 AM - 6 PM)</option>
                      <option value="AFT_2_10">Afternoon Shift (2 PM - 10 PM)</option>
                      <option value="NIGHT_10_6">Night Shift (10 PM - 6 AM)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Comments / Reason *</label>
                  <textarea 
                    value={shiftForm.reason} 
                    onChange={(e) => setShiftForm({ ...shiftForm, reason: e.target.value })} 
                    rows={2} 
                    placeholder="Enter reason for shift exchange..."
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0052FF]/20" 
                    required 
                  />
                </div>

                <button 
                  data-testid="submit-shift-btn" 
                  type="submit" 
                  disabled={submitting}
                  className="w-full bg-[#0052FF] text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-[#0040CC] disabled:opacity-50 transition-all"
                >
                  {submitting 
                    ? 'Submitting...' 
                    : reworkTarget 
                      ? 'Resubmit Shift Request' 
                      : reapplyTarget 
                        ? 'Submit Reapplication' 
                        : 'Submit Request'}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* 4. COMP-OFF REQUEST MODAL */}
        {showCompoffModal && (
          <div className="fixed inset-0 bg-black/45 flex items-center justify-center z-50 p-4" onClick={() => {
            setShowCompoffModal(false);
            setReworkTarget(null);
            setReapplyTarget(null);
            setReworkNotes('');
            setCompoffAttachment(null);
            if (compoffFileInputRef.current) compoffFileInputRef.current.value = '';
          }}>
            <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
              <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                <h3 className="text-lg font-bold text-slate-900">
                  {reworkTarget 
                    ? 'Rework & Resubmit Comp-Off Request' 
                    : reapplyTarget 
                      ? 'Reapply Comp-Off Request' 
                      : 'Request Comp-Off Day'}
                </h3>
                <button onClick={() => {
                  setShowCompoffModal(false);
                  setReworkTarget(null);
                  setReapplyTarget(null);
                  setReworkNotes('');
                  setCompoffAttachment(null);
                  if (compoffFileInputRef.current) compoffFileInputRef.current.value = '';
                }}><X className="w-5 h-5 text-slate-400" /></button>
              </div>

              <form onSubmit={handleCompoffSubmit} className="p-6 space-y-4">
                {reworkTarget && (
                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-3.5 text-xs text-orange-900 animate-in fade-in duration-150">
                    <div className="flex items-center gap-2 font-bold mb-1 text-orange-800">
                      <RotateCcw className="w-4 h-4 text-orange-600 shrink-0" />
                      <span>Rework Requested by Manager</span>
                    </div>
                    <p className="text-slate-700 mb-2">
                      <strong className="text-orange-950 font-semibold">Manager Remarks:</strong>{' '}
                      {reworkTarget.send_back_remarks || reworkTarget.rework_remarks || reworkTarget.rejection_reason || reworkTarget.approver_remarks || 'Please adjust the comp-off details and resubmit.'}
                    </p>
                    <div>
                      <label className="block text-[11px] font-semibold text-orange-950 mb-1 uppercase tracking-wider">
                        Your Rework Notes / Clarification
                      </label>
                      <input
                        type="text"
                        placeholder="e.g. Corrected worked date and hours..."
                        value={reworkNotes}
                        onChange={(e) => setReworkNotes(e.target.value)}
                        className="w-full bg-white border border-orange-200 rounded-lg px-3 py-2 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-orange-400"
                      />
                    </div>
                  </div>
                )}
                {reapplyTarget && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 text-xs text-amber-900 animate-in fade-in duration-150">
                    <div className="flex items-center gap-2 font-bold mb-1 text-amber-800">
                      <RotateCcw className="w-4 h-4 text-amber-600 shrink-0" />
                      <span>Reapplying for Previously Rejected Comp-Off Request</span>
                    </div>
                    <p className="text-slate-700">
                      <strong className="text-amber-950 font-semibold">Previous Rejection Reason:</strong>{' '}
                      {reapplyTarget.rejection_reason || reapplyTarget.approver_remarks || reapplyTarget.remarks || 'Rejected by approver'}
                    </p>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Worked Date *</label>
                    <input 
                      type="date" 
                      max={(() => {
                        const d = new Date();
                        d.setDate(d.getDate() - 1);
                        return d.toISOString().split('T')[0];
                      })()}
                      value={compoffForm.worked_date} 
                      onChange={(e) => setCompoffForm({ ...compoffForm, worked_date: e.target.value })} 
                      className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0052FF]/20" 
                      required 
                      data-testid="input-compoff-worked-date"
                    />
                    <p className="text-[10px] text-amber-600 font-medium mt-1">Past dates only (today &amp; future dates disabled)</p>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Hours Worked *</label>
                    <input 
                      type="number" 
                      step="0.5"
                      min="1"
                      value={compoffForm.hours_worked} 
                      onChange={(e) => setCompoffForm({ ...compoffForm, hours_worked: parseFloat(e.target.value) || 0 })} 
                      className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0052FF]/20" 
                      required
                    />
                    <p className="text-[10px] text-slate-400 mt-1">Actual hours worked</p>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Target Leave Date (Optional)</label>
                  <input 
                    type="date" 
                    value={compoffForm.comp_off_date} 
                    onChange={(e) => setCompoffForm({ ...compoffForm, comp_off_date: e.target.value })} 
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0052FF]/20" 
                  />
                  <p className="text-[10px] text-slate-400 mt-1">Leave date can also be picked later when applying leave</p>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Reason *</label>
                  <textarea 
                    value={compoffForm.reason} 
                    onChange={(e) => setCompoffForm({ ...compoffForm, reason: e.target.value })} 
                    rows={2} 
                    placeholder="Mention project name or work detail..."
                    className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0052FF]/20" 
                    required 
                  />
                </div>

                {/* ATTACHMENT SECTION */}
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">
                    Attachments (Optional)
                  </label>
                  <input
                    ref={compoffFileInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png,image/jpeg,image/png,application/pdf"
                    onChange={handleCompoffFileChange}
                    className="hidden"
                    data-testid="compoff-file-input"
                  />

                  {!compoffAttachment ? (
                    <div
                      onClick={() => compoffFileInputRef.current?.click()}
                      className="border-2 border-dashed border-slate-200 hover:border-[#0052FF]/60 rounded-xl p-4 flex flex-col items-center justify-center cursor-pointer bg-slate-50/50 hover:bg-blue-50/20 transition-all text-center group"
                      data-testid="compoff-dropzone"
                    >
                      <Paperclip className="w-5 h-5 text-slate-400 group-hover:text-[#0052FF] mb-1.5 transition-colors" />
                      <p className="text-xs font-semibold text-slate-700 group-hover:text-[#0052FF]">
                        Click to upload document or proof
                      </p>
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        Supported: PDF, JPG, JPEG, PNG (Max 10 MB)
                      </p>
                    </div>
                  ) : (
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-center justify-between gap-3 animate-in fade-in" data-testid="compoff-selected-file">
                      <div className="flex items-center gap-2.5 min-w-0">
                        <span className="p-2 rounded-lg bg-blue-100 text-[#0052FF] shrink-0">
                          <FileText className="w-4 h-4" />
                        </span>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-slate-800 truncate" title={compoffAttachment.name || compoffAttachment.filename} data-testid="compoff-file-name">
                            {compoffAttachment.name || compoffAttachment.filename}
                          </p>
                          <div className="flex items-center gap-2 text-[10px] text-slate-400">
                            <span className="font-mono uppercase bg-slate-200 px-1 py-0.2 rounded text-[9px] text-slate-700">
                              {(compoffAttachment.type?.split('/')[1] || compoffAttachment.name?.split('.').pop() || 'FILE').toUpperCase()}
                            </span>
                            <span data-testid="compoff-file-size">{compoffAttachment.size_formatted || (compoffAttachment.size ? `${Math.round(compoffAttachment.size / 1024)} KB` : '')}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5 shrink-0">
                        <button
                          type="button"
                          onClick={() => compoffFileInputRef.current?.click()}
                          className="px-2.5 py-1 text-xs font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-100 rounded-lg transition"
                          data-testid="compoff-replace-btn"
                          title="Choose a different document"
                        >
                          Replace
                        </button>
                        <button
                          type="button"
                          onClick={removeCompoffAttachment}
                          className="p-1 text-rose-600 hover:bg-rose-50 rounded-lg transition"
                          data-testid="compoff-remove-btn"
                          title="Remove attachment"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <button 
                  data-testid="submit-compoff-btn" 
                  type="submit" 
                  disabled={submitting || compoffUploading}
                  className="w-full bg-[#0052FF] text-white py-2.5 rounded-lg text-sm font-semibold hover:bg-[#0040CC] disabled:opacity-50 transition-all"
                >
                  {compoffUploading
                    ? 'Uploading Attachment...'
                    : submitting 
                      ? 'Submitting...' 
                      : reworkTarget 
                        ? 'Resubmit Comp-Off' 
                        : reapplyTarget 
                          ? 'Submit Reapplication' 
                          : 'Submit Request'}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* 5. PREVIEW WORKFLOW DRAWER / MODAL */}
        {previewItem && (
          <div className="fixed inset-0 bg-black/55 z-50 flex items-center justify-end" onClick={() => setPreviewItem(null)}>
            <div className="bg-white h-full w-full max-w-md shadow-2xl p-6 overflow-y-auto flex flex-col justify-between" onClick={e => e.stopPropagation()}>
              
              <div className="space-y-6">
                <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">Application Preview</h3>
                    <p className="text-xs text-slate-500 font-mono mt-0.5">Type: {previewType?.toUpperCase()}</p>
                  </div>
                  <button onClick={() => setPreviewItem(null)} className="p-1 hover:bg-slate-100 rounded">
                    <X className="w-5 h-5 text-slate-400" />
                  </button>
                </div>

                {/* Rework and Reapply Callout Cards */}
                {['rework', 'sent_back'].includes(previewItem.status) && (
                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-3.5 text-xs text-orange-950 space-y-2 animate-in fade-in">
                    <div className="flex items-center gap-1.5 font-bold text-orange-800">
                      <RotateCcw className="w-4 h-4 text-orange-600 shrink-0" />
                      <span>Action Required: Sent Back for Rework</span>
                    </div>
                    <p className="text-slate-700">
                      <strong className="text-orange-950 font-semibold">Approver Remarks:</strong>{' '}
                      {previewItem.send_back_remarks || previewItem.rework_remarks || previewItem.approver_remarks || 'Please edit and resubmit this request.'}
                    </p>
                    <button
                      onClick={() => {
                        const itemToRework = previewItem;
                        const typeToRework = previewType;
                        setPreviewItem(null);
                        handleOpenRework(itemToRework, typeToRework);
                      }}
                      className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-semibold text-xs transition-colors shadow-sm"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      <span>Rework & Resubmit This Request</span>
                    </button>
                  </div>
                )}
                {previewItem.status === 'rejected' && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-3.5 text-xs text-amber-950 space-y-2 animate-in fade-in">
                    <div className="flex items-center gap-1.5 font-bold text-amber-800">
                      <RotateCcw className="w-4 h-4 text-amber-600 shrink-0" />
                      <span>Request Rejected</span>
                    </div>
                    <p className="text-slate-700">
                      <strong className="text-amber-950 font-semibold">Rejection Reason:</strong>{' '}
                      {previewItem.rejection_reason || previewItem.approver_remarks || previewItem.remarks || 'Rejected by approver'}
                    </p>
                    <button
                      onClick={() => {
                        const itemToReapply = previewItem;
                        const typeToReapply = previewType;
                        setPreviewItem(null);
                        handleOpenReapply(itemToReapply, typeToReapply);
                      }}
                      className="w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold text-xs transition-colors shadow-sm"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      <span>Reapply for this Request</span>
                    </button>
                  </div>
                )}

                {/* Workflow Timeline */}
                <div className="space-y-4">
                  <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Approval Workflow Timeline</h4>
                  <div className="relative border-l-2 border-slate-200 pl-6 ml-3 space-y-6 py-2">
                    
                    {/* Step 1: Submission */}
                    <div className="relative">
                      <div className="absolute -left-[31px] top-0.5 bg-emerald-500 text-white rounded-full p-1">
                        <Check className="w-3 h-3" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-800">Submitted by Employee</p>
                        <p className="text-[10px] text-slate-400 mt-0.5 font-mono">
                          {previewItem.created_at ? previewItem.created_at.slice(0, 16).replace('T', ' ') : 'Just now'}
                        </p>
                      </div>
                    </div>

                    {/* Step 2: Reporting Manager */}
                    <div className="relative">
                      <div className={`absolute -left-[31px] top-0.5 rounded-full p-1 ${
                        ['approved', 'rejected', 'cancelled'].includes(previewItem.status)
                          ? 'bg-emerald-500 text-white'
                          : ['rework', 'sent_back'].includes(previewItem.status)
                            ? 'bg-orange-500 text-white'
                            : 'bg-amber-500 text-white animate-pulse'
                      }`}>
                        {['approved', 'rejected', 'cancelled'].includes(previewItem.status) ? (
                          <Check className="w-3 h-3" />
                        ) : ['rework', 'sent_back'].includes(previewItem.status) ? (
                          <RotateCcw className="w-3 h-3" />
                        ) : (
                          <Clock className="w-3 h-3" />
                        )}
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-800">Reporting Manager Review</p>
                        <p className="text-[10px] text-slate-500 font-medium">Stage: Manager Verification</p>
                        {['rework', 'sent_back'].includes(previewItem.status) && (
                          <p className="text-[10px] text-orange-700 font-medium mt-1">
                            Sent back: "{previewItem.send_back_remarks || previewItem.rework_remarks || previewItem.approver_remarks || 'Rework requested'}"
                          </p>
                        )}
                        {previewItem.status === 'rejected' && (
                          <p className="text-[10px] text-red-600 font-medium mt-1">
                            Rejected remarks: "{previewItem.rejection_reason || previewItem.approver_remarks || 'Not approved'}"
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Step 3: HR stage */}
                    <div className="relative">
                      <div className={`absolute -left-[31px] top-0.5 rounded-full p-1 ${
                        previewItem.status === 'approved'
                          ? 'bg-emerald-500 text-white'
                          : 'bg-slate-200 text-slate-400'
                      }`}>
                        {previewItem.status === 'approved' ? <Check className="w-3 h-3" /> : <UserCheck className="w-3 h-3" />}
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-800">HR final approval</p>
                        <p className="text-[10px] text-slate-500 font-medium">Stage: Final Release</p>
                      </div>
                    </div>

                    {/* Step 4: Approved/Final status */}
                    <div className="relative">
                      <div className={`absolute -left-[31px] top-0.5 rounded-full p-1 ${
                        previewItem.status === 'approved'
                          ? 'bg-emerald-500 text-white'
                          : 'bg-slate-200 text-slate-400'
                      }`}>
                        {previewItem.status === 'approved' ? <Check className="w-3 h-3" /> : <Building2 className="w-3 h-3" />}
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-slate-800">Status Resolved</p>
                        <p className="text-[10px] text-slate-500">Current state: <strong className="uppercase">{previewItem.status}</strong></p>
                      </div>
                    </div>

                  </div>
                </div>

                {/* Details list */}
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 text-xs space-y-2.5">
                  <h4 className="font-bold text-slate-800">Application Details</h4>
                  
                  {previewType === 'leave' && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Leave Type</span>
                        <span className="font-semibold text-slate-800">{previewItem.leave_type_name || previewItem.leave_type}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Duration</span>
                        <span className="font-semibold text-slate-800">{previewItem.from_date} to {previewItem.to_date}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">{previewItem.is_permission || previewItem.leave_type_code === 'ONE_HOUR_PERMISSION' ? 'Duration' : 'Total Days'}</span>
                        <span className="font-semibold text-slate-800">
                          {previewItem.is_permission || previewItem.leave_type_code === 'ONE_HOUR_PERMISSION'
                            ? (previewItem.duration_display || formatPermissionDuration(previewItem.duration_minutes || (previewItem.duration_hours ? previewItem.duration_hours * 60 : 60)))
                            : (previewItem.is_partial_day || previewItem.half_day
                                ? `0.5 Day (${(previewItem.half_day_period || previewItem.half_day_session) === 'first_half' ? 'First Half' : 'Second Half'})`
                                : `${previewItem.total_days !== undefined && previewItem.total_days !== null ? previewItem.total_days : (previewItem.days || 1)} day(s)`
                              )
                          }
                        </span>
                      </div>
                    </>
                  )}

                  {previewType === 'reg' && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Date</span>
                        <span className="font-semibold text-slate-800 font-mono">{previewItem.attendance_date}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Proposed In/Out</span>
                        <span className="font-semibold text-slate-800 font-mono">{previewItem.proposed_in_time} / {previewItem.proposed_out_time}</span>
                      </div>
                    </>
                  )}

                  {previewType === 'shift' && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Exchange Type</span>
                        <span className="font-semibold text-slate-800 capitalize">{previewItem.request_type || 'Change'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Effective Date</span>
                        <span className="font-semibold text-slate-800 font-mono">{previewItem.requested_date}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Current Shift</span>
                        <span className="font-semibold text-slate-800 font-mono">{previewItem.current_shift_id || 'GEN_9_6'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Requested Shift</span>
                        <span className="font-semibold text-slate-800 font-mono">{previewItem.requested_shift_id}</span>
                      </div>
                    </>
                  )}

                  {previewType === 'compoff' && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Worked Date</span>
                        <span className="font-semibold text-slate-800 font-mono">{previewItem.worked_date || previewItem.work_date}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Hours Worked</span>
                        <span className="font-semibold text-slate-800">{previewItem.hours_worked || 8} hrs</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Earned Credit</span>
                        <span className="font-semibold text-indigo-700">{previewItem.earned_days || previewItem.days || 1} Day(s)</span>
                      </div>
                      {previewItem.comp_off_date && (
                        <div className="flex justify-between">
                          <span className="text-slate-500">Target Leave Date</span>
                          <span className="font-semibold text-slate-800 font-mono">{previewItem.comp_off_date}</span>
                        </div>
                      )}
                      <div className="flex justify-between items-center pt-1 border-t border-slate-100">
                        <span className="text-slate-500">Attachment</span>
                        {(() => {
                          const att = (Array.isArray(previewItem.attachments) && previewItem.attachments[0])
                            || (previewItem.file_id || previewItem.attachment_id || previewItem.attachment_name ? {
                                name: previewItem.attachment_name || 'Document',
                                file_id: previewItem.file_id || previewItem.attachment_id,
                                url: previewItem.attachment_url,
                              } : null);
                          if (!att) {
                            return <span className="text-slate-400 italic" data-testid="preview-no-attachment">No attachment available</span>;
                          }
                          const fileUrl = att.url || (att.file_id ? `${API_URL}/ess/compoff-requests/attachments/${att.file_id}` : '');
                          return (
                            <a
                              href={fileUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1 font-semibold text-blue-600 hover:underline"
                              data-testid="preview-attachment-link"
                            >
                              <Paperclip className="w-3 h-3" />
                              <span>{att.name || att.filename || 'View Attachment'}</span>
                            </a>
                          );
                        })()}
                      </div>
                    </>
                  )}

                  <div className="flex justify-between">
                    <span className="text-slate-500">Reason</span>
                    <span className="font-semibold text-slate-800 truncate max-w-[200px]" title={previewItem.reason}>{previewItem.reason || 'N/A'}</span>
                  </div>
                </div>
              </div>

              <button 
                onClick={() => setPreviewItem(null)} 
                className="w-full py-2.5 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-semibold mt-6 transition-all"
              >
                Close Preview
              </button>

            </div>
          </div>
        )}

        {/* 5. LEAVE CALENDAR DATE DETAILS MODAL */}
        {selectedDateDetailModal && (
          <div 
            className="fixed inset-0 bg-black/45 flex items-center justify-center z-50 p-4" 
            onClick={() => setSelectedDateDetailModal(null)}
          >
            <div 
              className="bg-white rounded-2xl w-full max-w-xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]" 
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/60">
                <div>
                  <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                    <CalendarIcon className="w-5 h-5 text-[#0052FF]" />
                    Leave Calendar Details
                  </h3>
                  <p className="text-xs text-slate-500 font-mono mt-0.5">
                    {selectedDateDetailModal.dateObj?.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    {selectedDateDetailModal.isWeekend && <span className="ml-2 text-slate-400 font-sans font-medium">(Weekend)</span>}
                  </p>
                </div>
                <button onClick={() => setSelectedDateDetailModal(null)} className="p-1 hover:bg-slate-200 rounded-full">
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto space-y-6">
                
                {/* 1. Attendance Record Section */}
                {selectedDateDetailModal.attendanceRecord && (() => {
                  const r = selectedDateDetailModal.attendanceRecord;
                  const isSplit = selectedDateDetailModal.hasSplit;
                  const isSplitCo = r.status === 'half_day_present_compoff' || selectedDateDetailModal.isSplitCompoff;
                  const inPunch = r.in_time || r.actual_in_time || r.check_in || '--:--';
                  const outPunch = r.out_time || r.actual_out_time || r.check_out || '--:--';
                  const statusStr = (r.status || 'present').replace(/_/g, ' ');

                  const badgeClass = 
                    r.status === 'half_day_present_leave' ? 'bg-gradient-to-r from-emerald-50 to-blue-50 text-blue-900 border-blue-200' :
                    r.status === 'half_day_present_compoff' || isSplitCo ? 'bg-gradient-to-r from-emerald-50 to-teal-50 text-teal-900 border-teal-200' :
                    r.status === 'compoff' ? 'bg-teal-50 text-teal-800 border-teal-200' :
                    r.status === 'leave' ? 'bg-blue-50 text-blue-800 border-blue-200' :
                    r.status === 'present' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' :
                    r.status === 'absent' ? 'bg-red-50 text-red-800 border-red-200' :
                    r.status === 'half_day' ? 'bg-amber-50 text-amber-800 border-amber-200' :
                    'bg-slate-100 text-slate-700 border-slate-200';

                  const badgeLabel = 
                    r.status === 'half_day_present_leave' ? 'Half-Day Present + Half-Day Leave' :
                    r.status === 'half_day_present_compoff' || isSplitCo ? 'Half-Day Present + Half-Day Comp-Off' :
                    r.status === 'compoff' ? 'Approved Comp-Off' :
                    r.status === 'leave' ? 'Approved Leave' :
                    r.status === 'present' ? 'Present' :
                    r.status === 'absent' ? 'Absent' :
                    statusStr;

                  return (
                    <div className="space-y-3" data-testid="date-modal-attendance-section">
                      <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                        <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                          <Clock className="w-4 h-4 text-emerald-600" />
                          Attendance Record
                        </h4>
                        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase border ${badgeClass}`}>
                          {badgeLabel}
                        </span>
                      </div>

                      {isSplit && (
                        <div className={`p-3.5 border rounded-xl text-xs space-y-2 ${
                          isSplitCo
                            ? 'bg-gradient-to-r from-emerald-50 to-teal-50 border-teal-200'
                            : 'bg-gradient-to-r from-emerald-50 to-blue-50 border-blue-200'
                        }`}>
                          <div className="flex flex-wrap items-center justify-between gap-2 font-semibold text-slate-800">
                            <span>First Half: <strong className={
                              selectedDateDetailModal.firstHalfStatus === 'leave' ? 'text-blue-700' :
                              selectedDateDetailModal.firstHalfStatus === 'compoff' ? 'text-teal-700' :
                              selectedDateDetailModal.firstHalfStatus === 'absent' ? 'text-red-700' : 'text-emerald-700'
                            }>
                              {selectedDateDetailModal.firstHalfStatus === 'leave' ? 'Approved Leave (0.5 Day)' :
                               selectedDateDetailModal.firstHalfStatus === 'compoff' ? 'Approved Comp-Off (0.5 Day)' :
                               selectedDateDetailModal.firstHalfStatus === 'absent' ? 'Absent (0.5 Day)' : 'Present (0.5 Day)'}
                            </strong></span>
                            <span>Second Half: <strong className={
                              selectedDateDetailModal.secondHalfStatus === 'leave' ? 'text-blue-700' :
                              selectedDateDetailModal.secondHalfStatus === 'compoff' ? 'text-teal-700' :
                              selectedDateDetailModal.secondHalfStatus === 'absent' ? 'text-red-700' : 'text-emerald-700'
                            }>
                              {selectedDateDetailModal.secondHalfStatus === 'leave' ? 'Approved Leave (0.5 Day)' :
                               selectedDateDetailModal.secondHalfStatus === 'compoff' ? 'Approved Comp-Off (0.5 Day)' :
                               selectedDateDetailModal.secondHalfStatus === 'absent' ? 'Absent (0.5 Day)' : 'Present (0.5 Day)'}
                            </strong></span>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 border-t border-slate-200/60 text-[11px] text-slate-600">
                            <div>
                              <span className="text-slate-400 block text-[9px] uppercase font-semibold">Effective Status</span>
                              <strong className="text-slate-900 font-semibold">{isSplitCo ? 'Half-Day Present + Comp-Off' : 'Half-Day Present + Leave'}</strong>
                            </div>
                            <div>
                              <span className="text-slate-400 block text-[9px] uppercase font-semibold">Leave Type & Status</span>
                              <strong className="text-slate-900 font-semibold">
                                {r.leave_type || (isSplitCo ? 'Comp-Off' : (selectedDateDetailModal.approvedSplitLeave?.leave_type || 'Leave'))} (Approved)
                              </strong>
                            </div>
                            <div>
                              <span className="text-slate-400 block text-[9px] uppercase font-semibold">Attendance Credit</span>
                              <strong className="text-emerald-700 font-semibold">{r.attendance_credit ?? r.present_duration_days ?? 0.5} day</strong>
                            </div>
                            <div>
                              <span className="text-slate-400 block text-[9px] uppercase font-semibold">Leave Consumed</span>
                              <strong className="text-blue-700 font-semibold">{r.leave_consumed ?? r.leave_duration_days ?? 0.5} day</strong>
                            </div>
                          </div>
                        </div>
                      )}

                      {!isSplit && (r.status === 'leave' || r.status === 'compoff' || r.is_leave || r.is_compoff) && (
                        <div className={`p-3.5 border rounded-xl text-xs space-y-2 ${
                          r.status === 'compoff' || r.is_compoff ? 'bg-teal-50/70 border-teal-200' : 'bg-blue-50/70 border-blue-200'
                        }`}>
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div>
                              <span className="text-slate-500 text-[11px]">Leave / Adjustment: </span>
                              <strong className={r.status === 'compoff' ? 'text-teal-800' : 'text-blue-800'}>
                                {r.leave_type || (r.status === 'compoff' ? 'Comp-Off' : 'Approved Leave')} (1.0 Day)
                              </strong>
                            </div>
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-emerald-100 text-emerald-800 border border-emerald-200">
                              Approved
                            </span>
                          </div>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 border-t border-slate-200/60 text-[11px] text-slate-600">
                            <div>
                              <span className="text-slate-400 block text-[9px] uppercase font-semibold">Effective Status</span>
                              <strong className="text-slate-900 font-semibold">{r.status === 'compoff' ? 'Comp-Off' : 'Leave'}</strong>
                            </div>
                            <div>
                              <span className="text-slate-400 block text-[9px] uppercase font-semibold">Leave Consumed</span>
                              <strong className="text-blue-700 font-semibold">{r.leave_consumed ?? r.leave_duration_days ?? 1.0} day</strong>
                            </div>
                            <div>
                              <span className="text-slate-400 block text-[9px] uppercase font-semibold">Attendance Credit</span>
                              <strong className="text-slate-700 font-semibold">{r.attendance_credit ?? r.present_duration_days ?? 0.0} day</strong>
                            </div>
                            <div>
                              <span className="text-slate-400 block text-[9px] uppercase font-semibold">Payable Days</span>
                              <strong className="text-emerald-700 font-semibold">{r.payable_days ?? 1.0} day</strong>
                            </div>
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs bg-slate-50 p-4 rounded-xl border border-slate-100">
                        <div>
                          <span className="text-slate-400 block text-[10px] uppercase font-semibold">Actual IN Punch</span>
                          <strong className="text-slate-800 font-mono text-sm">{inPunch}</strong>
                        </div>
                        <div>
                          <span className="text-slate-400 block text-[10px] uppercase font-semibold">Actual OUT Punch</span>
                          <strong className="text-slate-800 font-mono text-sm">{outPunch}</strong>
                        </div>
                        <div>
                          <span className="text-slate-400 block text-[10px] uppercase font-semibold">Worked Duration</span>
                          <strong className="text-slate-800 font-mono text-sm">{r.total_hours ?? r.worked_duration ?? 0} hrs</strong>
                        </div>
                        <div>
                          <span className="text-slate-400 block text-[10px] uppercase font-semibold">Attendance Credit</span>
                          <strong className="text-emerald-700 font-mono text-sm">
                            {r.attendance_credit ?? r.present_duration_days ?? (r.status === 'present' ? 1.0 : (isSplit ? 0.5 : (r.status === 'half_day' ? 0.5 : 0.0)))} day
                          </strong>
                        </div>
                        <div>
                          <span className="text-slate-400 block text-[10px] uppercase font-semibold">Leave Consumed</span>
                          <strong className="text-blue-700 font-mono text-sm">
                            {r.leave_consumed ?? r.leave_duration_days ?? (r.status === 'leave' || r.status === 'compoff' ? 1.0 : (isSplit ? 0.5 : 0.0))} day
                          </strong>
                        </div>
                        <div>
                          <span className="text-slate-400 block text-[10px] uppercase font-semibold">Payable Days</span>
                          <strong className="text-slate-900 font-mono text-sm">
                            {r.payable_days ?? (r.status === 'absent' ? 0.0 : (r.status === 'half_day' ? 0.5 : 1.0))} day
                          </strong>
                        </div>
                        <div>
                          <span className="text-slate-400 block text-[10px] uppercase font-semibold">Permission Credit</span>
                          <strong className="text-teal-700 font-mono text-sm">
                            {r.permission_hours_credited > 0 ? `+${r.permission_hours_credited} hrs` : '0 hrs'}
                          </strong>
                          {r.permission_minutes_credited > 0 && (
                            <span className="text-[10px] text-teal-600 block">({formatPermissionDuration(r.permission_minutes_credited)})</span>
                          )}
                        </div>
                        <div>
                          <span className="text-slate-400 block text-[10px] uppercase font-semibold">Effective Working Hours</span>
                          <strong className="text-slate-900 font-mono text-sm">{r.effective_working_hours ?? r.total_hours ?? 0} hrs</strong>
                        </div>
                        <div>
                          <span className="text-slate-400 block text-[10px] uppercase font-semibold">Attendance Deficit</span>
                          <strong className={`font-mono text-sm ${(r.attendance_deficit_hours || 0) > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                            {r.attendance_deficit_hours > 0 ? `${r.attendance_deficit_hours} hrs` : '0 hrs (No deficit)'}
                          </strong>
                        </div>
                      </div>

                      {(r.late_excused || r.early_exit_excused) && (
                        <div className="p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-800 flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                          <span>
                            {r.late_excused && r.early_exit_excused
                              ? 'Late arrival & early departure excused via approved permission.'
                              : (r.late_excused ? 'Late arrival excused via approved permission window.' : 'Early departure excused via approved permission window.')}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* 2. Company Holidays Section */}
                {selectedDateDetailModal.matchingHolidays.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-xs font-bold text-indigo-900 uppercase tracking-wider flex items-center gap-1.5">
                      <Gift className="w-4 h-4 text-indigo-600" />
                      Company Holiday ({selectedDateDetailModal.matchingHolidays.length})
                    </h4>
                    {selectedDateDetailModal.matchingHolidays.map((h, i) => (
                      <div key={h.id || i} className="p-4 bg-indigo-50/70 border border-indigo-200 rounded-xl space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="font-bold text-sm text-indigo-950">{h.name}</span>
                          <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase bg-indigo-200 text-indigo-800">
                            {h.type || 'Company Holiday'}
                          </span>
                        </div>
                        {h.code && <p className="text-xs font-mono text-indigo-700">Code: {h.code}</p>}
                        {h.description && <p className="text-xs text-indigo-900 bg-white/70 p-2.5 rounded-lg border border-indigo-100">{h.description}</p>}
                      </div>
                    ))}
                  </div>
                )}

                {/* 3. Leave Applications Section */}
                {selectedDateDetailModal.matchingLeaves.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-1.5">
                      <FileText className="w-4 h-4 text-[#0052FF]" />
                      Leave Application(s) ({selectedDateDetailModal.matchingLeaves.length})
                    </h4>
                    {selectedDateDetailModal.matchingLeaves.map((l, i) => {
                      const appNo = l.application_number || l.appNo || l.id || `LA-${String(i+1).padStart(4, '0')}`;
                      const empName = l.employee_name || l.employee?.name || 'Employee';
                      const empCode = l.employee_code || l.employee?.code || '';
                      const leaveTypeName = l.leave_type_name || l.leave_type || 'Leave';
                      
                      const totalDays = l.total_days !== undefined && l.total_days !== null ? l.total_days : (l.days || 1);
                      let dayPos = 'Single Day Leave';
                      if (l.from_date !== l.to_date) {
                        if (selectedDateDetailModal.iso === l.from_date) {
                          dayPos = `Day 1 of ${totalDays} (First Day of multi-day leave)`;
                        } else if (selectedDateDetailModal.iso === l.to_date) {
                          dayPos = `Day ${totalDays} of ${totalDays} (Last Day of multi-day leave)`;
                        } else {
                          dayPos = `Intermediate Day (Multi-day leave: ${l.from_date} to ${l.to_date})`;
                        }
                      }

                      const appliedOnStr = formatDateTimeStr(l.created_at || l.applied_at || l.submitted_at);
                      const approvedOnStr = formatDateTimeStr(l.approved_at || l.decided_at);
                      const rejectedOnStr = formatDateTimeStr(l.rejected_at);

                      let badgeColor = 'bg-amber-100 text-amber-800 border-amber-300';
                      if (l.status === 'approved') badgeColor = 'bg-emerald-100 text-emerald-800 border-emerald-300';
                      if (l.status === 'rejected') badgeColor = 'bg-red-100 text-red-800 border-red-300';
                      if (l.status === 'cancelled') badgeColor = 'bg-slate-100 text-slate-700 border-slate-300';

                      return (
                        <div key={l.id || i} className="p-4 bg-white border border-slate-200 rounded-xl shadow-sm space-y-3">
                          <div className="flex items-center justify-between border-b border-slate-100 pb-2.5">
                            <div>
                              <span className="font-mono text-xs font-bold text-slate-700">{appNo}</span>
                              <h5 className="font-bold text-sm text-slate-900 mt-0.5">{leaveTypeName}</h5>
                            </div>
                            <span className={`px-2.5 py-0.5 rounded-full border text-[10px] font-bold uppercase ${badgeColor}`}>
                              {l.status}
                            </span>
                          </div>

                          <div className="grid grid-cols-2 gap-2.5 text-xs text-slate-600">
                            <div>
                              <span className="text-slate-400 block text-[10px] uppercase font-semibold">Applicant</span>
                              <strong className="text-slate-800">{empName} {empCode ? `(${empCode})` : ''}</strong>
                            </div>
                            <div>
                              <span className="text-slate-400 block text-[10px] uppercase font-semibold">Leave Period</span>
                              <span className="font-mono font-medium">{l.from_date} → {l.to_date}</span>
                            </div>
                            <div>
                              <span className="text-slate-400 block text-[10px] uppercase font-semibold">Selected Date Position</span>
                              <strong className="text-blue-700">{dayPos}</strong>
                            </div>
                            <div>
                              <span className="text-slate-400 block text-[10px] uppercase font-semibold">Duration & Deduction</span>
                              <span>
                                {l.is_permission || l.leave_type_code === 'ONE_HOUR_PERMISSION' ? (
                                  <strong className="text-indigo-700">{l.duration_display || formatPermissionDuration(l.duration_minutes || 60)} (0.0 leave days)</strong>
                                ) : l.is_partial_day || l.half_day ? (
                                  <strong className="text-amber-700">0.5 Day ({(l.half_day_period || l.half_day_session) === 'first_half' ? 'First Half' : 'Second Half'}) — Exactly 0.5 Day deducted</strong>
                                ) : (
                                  `${totalDays} Full Day(s)`
                                )}
                              </span>
                            </div>
                            <div className="col-span-2 bg-slate-50 p-2.5 rounded-lg border border-slate-100">
                              <span className="text-slate-400 block text-[10px] uppercase font-semibold">Applied On (Submission Timestamp)</span>
                              <strong className="text-slate-800">{appliedOnStr || 'Not Recorded (Legacy Entry)'}</strong>
                            </div>
                          </div>

                          {/* Reason */}
                          <div className="text-xs">
                            <span className="text-slate-400 block text-[10px] uppercase font-semibold mb-0.5">Reason / Remarks</span>
                            <p className="p-2.5 bg-slate-50 rounded-lg text-slate-700 italic border border-slate-100">
                              {l.reason || l.remarks || 'No remarks provided.'}
                            </p>
                          </div>

                          {/* Approver Details */}
                          {(l.approved_by_name || l.approver_name || l.manager_name || l.approver_remarks || l.rejection_reason) && (
                            <div className="text-xs p-3 bg-blue-50/60 border border-blue-100 rounded-lg space-y-1 text-slate-700">
                              <div className="font-semibold text-blue-900 flex justify-between">
                                <span>Approver: {l.approved_by_name || l.approver_name || l.manager_name || 'Reporting Manager'}</span>
                                <span>{approvedOnStr || rejectedOnStr || ''}</span>
                              </div>
                              {(l.approver_remarks || l.rejection_reason) && (
                                <p className="text-[11px] text-slate-600 mt-1">
                                  <strong>Remarks:</strong> {l.approver_remarks || l.rejection_reason}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* 4. Permission Requests Section */}
                {(selectedDateDetailModal.matchingPermissions || []).length > 0 && (
                  <div className="space-y-3" data-testid="date-modal-permission-section">
                    <h4 className="text-xs font-bold text-teal-900 uppercase tracking-wider flex items-center gap-1.5">
                      <Clock className="w-4 h-4 text-teal-600" />
                      Permission Request(s) ({selectedDateDetailModal.matchingPermissions.length})
                    </h4>
                    {selectedDateDetailModal.matchingPermissions.map((p, i) => (
                      <div key={p.id || i} className="p-4 bg-teal-50/40 border border-teal-200 rounded-xl space-y-3">
                        <div className="flex items-center justify-between border-b border-teal-100 pb-2">
                          <div>
                            <span className="font-mono text-xs font-bold text-teal-800">{p.request_number || `PERM-${String(i+1).padStart(4, '0')}`}</span>
                            <p className="text-xs font-semibold text-slate-800 mt-0.5">Short Duration Permission</p>
                          </div>
                          <span className={`px-2.5 py-0.5 rounded-full border text-[10px] font-bold uppercase ${
                            p.status === 'approved' ? 'bg-emerald-100 text-emerald-800 border-emerald-300' :
                            p.status === 'rejected' ? 'bg-red-100 text-red-800 border-red-300' : 'bg-amber-100 text-amber-800 border-amber-300'
                          }`}>
                            {p.status}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                          <div>
                            <span className="text-slate-400 block text-[10px] uppercase font-semibold">Time Window</span>
                            <strong className="text-slate-800 font-mono">{p.start_time} → {p.end_time}</strong>
                          </div>
                          <div>
                            <span className="text-slate-400 block text-[10px] uppercase font-semibold">Requested Duration</span>
                            <strong className="text-teal-800 font-mono">{p.duration_display || formatPermissionDuration(p.duration_minutes || (p.duration_hours ? p.duration_hours * 60 : 60))}</strong>
                          </div>
                        </div>
                        <div className="text-xs">
                          <span className="text-slate-400 block text-[10px] uppercase font-semibold mb-0.5">Reason</span>
                          <p className="p-2.5 bg-white/70 rounded-lg text-slate-700 italic border border-teal-100">{p.reason || 'No reason provided.'}</p>
                        </div>
                        {(p.approver_remarks || p.rejection_reason) && (
                          <div className="text-xs p-2.5 bg-white/80 border border-teal-100 rounded-lg text-slate-600">
                            <strong>Approver Remarks:</strong> {p.approver_remarks || p.rejection_reason}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* 5. Regularization Requests Section */}
                {(selectedDateDetailModal.matchingRegularizations || []).length > 0 && (
                  <div className="space-y-3" data-testid="date-modal-regularization-section">
                    <h4 className="text-xs font-bold text-violet-900 uppercase tracking-wider flex items-center gap-1.5">
                      <FileEdit className="w-4 h-4 text-violet-600" />
                      Attendance Regularization(s) ({selectedDateDetailModal.matchingRegularizations.length})
                    </h4>
                    {selectedDateDetailModal.matchingRegularizations.map((reg, i) => (
                      <div key={reg.id || i} className="p-4 bg-violet-50/40 border border-violet-200 rounded-xl space-y-3">
                        <div className="flex items-center justify-between border-b border-violet-100 pb-2">
                          <div>
                            <span className="font-mono text-xs font-bold text-violet-800">{reg.request_number || `REG-${String(i+1).padStart(4, '0')}`}</span>
                            <p className="text-xs font-semibold text-slate-800 mt-0.5">Punch Correction</p>
                          </div>
                          <span className={`px-2.5 py-0.5 rounded-full border text-[10px] font-bold uppercase ${
                            reg.status === 'approved' ? 'bg-emerald-100 text-emerald-800 border-emerald-300' :
                            reg.status === 'rejected' ? 'bg-red-100 text-red-800 border-red-300' : 'bg-amber-100 text-amber-800 border-amber-300'
                          }`}>
                            {reg.status}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                          <div>
                            <span className="text-slate-400 block text-[10px] uppercase font-semibold">Original Punches (Audit)</span>
                            <span className="font-mono">{reg.original_in_time || '--:--'} → {reg.original_out_time || '--:--'}</span>
                            {reg.original_status && <span className="text-[10px] text-slate-400 block font-sans">Status: {reg.original_status}</span>}
                          </div>
                          <div>
                            <span className="text-slate-400 block text-[10px] uppercase font-semibold">Proposed Punches</span>
                            <strong className="text-violet-800 font-mono">{reg.proposed_in_time || '--:--'} → {reg.proposed_out_time || '--:--'}</strong>
                          </div>
                        </div>
                        <div className="text-xs">
                          <span className="text-slate-400 block text-[10px] uppercase font-semibold mb-0.5">Reason</span>
                          <p className="p-2.5 bg-white/70 rounded-lg text-slate-700 italic border border-violet-100">{reg.reason || 'No reason provided.'}</p>
                        </div>
                        {(reg.approver_remarks || reg.rejection_reason) && (
                          <div className="text-xs p-2.5 bg-white/80 border border-violet-100 rounded-lg text-slate-600">
                            <strong>Approver Remarks:</strong> {reg.approver_remarks || reg.rejection_reason}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* 6. Comp-Off Requests Section */}
                {(selectedDateDetailModal.matchingCompOffs || []).length > 0 && (
                  <div className="space-y-3" data-testid="date-modal-compoff-section">
                    <h4 className="text-xs font-bold text-teal-900 uppercase tracking-wider flex items-center gap-1.5">
                      <Clock className="w-4 h-4 text-teal-600" />
                      Comp-Off Request(s) ({selectedDateDetailModal.matchingCompOffs.length})
                    </h4>
                    {selectedDateDetailModal.matchingCompOffs.map((co, i) => (
                      <div key={co.id || co._id || i} className="p-4 bg-teal-50/50 border border-teal-200 rounded-xl space-y-2">
                        <div className="flex items-center justify-between border-b border-teal-100 pb-2">
                          <span className="font-mono text-xs font-bold text-teal-800">{co.request_number || `CO-${String(i+1).padStart(4, '0')}`}</span>
                          <span className={`px-2.5 py-0.5 rounded-full border text-[10px] font-bold uppercase ${
                            co.status === 'approved' || co.status === 'availed' ? 'bg-emerald-100 text-emerald-800 border-emerald-300' :
                            co.status === 'rejected' ? 'bg-red-100 text-red-800 border-red-300' : 'bg-amber-100 text-amber-800 border-amber-300'
                          }`}>
                            {co.status}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                          <div>
                            <span className="text-slate-400 block text-[10px] uppercase font-semibold">Worked Date</span>
                            <span className="font-mono">{co.worked_date || co.work_date || '—'}</span>
                          </div>
                          <div>
                            <span className="text-slate-400 block text-[10px] uppercase font-semibold">Comp-Off Avail Date</span>
                            <span className="font-mono text-teal-800 font-semibold">{co.comp_off_date || co.availed_date || '—'}</span>
                          </div>
                        </div>
                        {co.reason && (
                          <div className="text-xs">
                            <span className="text-slate-400 block text-[10px] uppercase font-semibold mb-0.5">Reason</span>
                            <p className="p-2.5 bg-white/70 rounded-lg text-slate-700 italic border border-teal-100">{co.reason}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* No Records Found */}
                {!selectedDateDetailModal.attendanceRecord && 
                 selectedDateDetailModal.matchingHolidays.length === 0 && 
                 selectedDateDetailModal.matchingLeaves.length === 0 &&
                 (selectedDateDetailModal.matchingPermissions || []).length === 0 &&
                 (selectedDateDetailModal.matchingRegularizations || []).length === 0 &&
                 (selectedDateDetailModal.matchingCompOffs || []).length === 0 && (
                  <div className="py-8 text-center space-y-3">
                    <div className="w-12 h-12 bg-slate-100 text-slate-400 rounded-full flex items-center justify-center mx-auto">
                      <CalendarIcon className="w-6 h-6" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-slate-800">No Records Recorded</p>
                      <p className="text-xs text-slate-500 mt-0.5">There are no attendance punches, leave applications, permissions, or holidays on this date.</p>
                    </div>
                    <button
                      onClick={() => {
                        const targetIso = selectedDateDetailModal.iso;
                        setSelectedDateDetailModal(null);
                        setLeaveForm(prev => ({ ...prev, from_date: targetIso, to_date: targetIso }));
                        setLeaveWizardStep(1);
                        setShowLeaveModal(true);
                      }}
                      className="px-4 py-2 bg-[#0052FF] text-white text-xs font-semibold rounded-lg hover:bg-[#0040CC] transition-colors" >
                      Apply Leave for {selectedDateDetailModal.iso}
                    </button>
                  </div>
                )}

              </div>

              <div className="p-4 border-t border-slate-100 bg-slate-50 flex justify-end">
                <button 
                  onClick={() => setSelectedDateDetailModal(null)} 
                  className="px-5 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-semibold transition-all" >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </MainLayout>
  );
};

export default ESSLeaveAttendance;
